import React, { useState, useMemo, useRef } from 'react';
import { Student, StudentAcademicProgress, SemesterProgress, SubjectGrade, Teacher, SchoolSettings } from '../../types.ts';
import * as XLSX from 'xlsx';
import { 
  Search, 
  Plus, 
  Trash2, 
  Edit3, 
  Check, 
  X, 
  Award, 
  BookOpen, 
  TrendingUp, 
  TrendingDown,
  Wrench,
  Save, 
  ChevronRight,
  ChevronLeft,
  Printer,
  Sparkles,
  Info,
  Upload,
  Download,
  FileSpreadsheet,
  FileUp,
  AlertCircle,
  CheckCircle2,
  HelpCircle,
  RefreshCw,
  BarChart,
  Copy,
  Trophy,
  AlertTriangle,
  MessageSquareQuote,
  Target,
  ShieldAlert,
  Users,
  UserCheck,
  Medal,
  ArrowUpDown,
  SlidersHorizontal,
  GraduationCap,
  Compass,
  Briefcase,
  Brain,
  Lightbulb,
  LayoutGrid,
  List,
  Table
} from 'lucide-react';

export interface StudentCareerProfile {
  specializationTrack: string;
  majorBadgeColor: string;
  topDominantSubjects: string[];
  careerPaths: string[];
  cognitiveStrength: string;
  recommendedSteps: string[];
  summarySentence: string;
}

export const getCustomizedClosingStory = (studentId: string, studentName: string): string => {
  const motivations = [
    `Mari kita tetapkan target dan komitmen baru untuk semester depan. Bapak dan orang tua senantiasa siap mendampingi agar prestasimu semakin gemilang! Tetap optimis dan semangat belajar!`,
    `Tantangan di depan adalah kesempatan emas untuk tumbuh. Bapak bersama orang tua akan selalu mendampingi setiap langkah belajarmu demi meraih prestasi yang lebih tinggi. Tetap semangat dan pantang menyerah!`,
    `Setiap usaha keras yang kamu lakukan hari ini adalah investasi masa depanmu. Ingatlah bahwa Bapak dan orang tua selalu mendukung penuh perjuanganmu untuk terus bersinar di semester baru nanti!`,
    `Mari sambut semester baru dengan optimisme baru dan tekad yang lebih kuat. Bapak dan orang tua selalu mendoakan dan siap membimbingmu agar potensi terbaikmu dapat terwujud seutuhnya!`,
    `Langkah kecil yang konsisten akan membawamu ke puncak kesuksesan yang luar biasa. Bapak dan orang tua sangat bangga padamu dan siap mengawal perjalanan belajarmu agar terus berkembang gemilang!`,
    `Jadikan setiap pencapaian sebagai batu lompatan, dan setiap kekurangan sebagai pelajaran berharga. Bapak dan orang tua akan selalu mendampingimu dengan kasih sayang untuk mewujudkan cita-citamu!`,
    `Keberhasilan sejati lahir dari ketekunan dan kerja keras yang tiada henti. Percayalah, Bapak dan orang tua selalu berdiri di belakangmu, siap membantumu mengukir prestasi-prestasi baru yang membanggakan!`,
    `Pertahankan semangat juangmu dan mari kita rancang langkah-langkah hebat untuk semester depan. Bapak dan orang tua senantiasa siap membimbing dan mendoakan kesuksesanmu di setiap langkah!`,
    `Kamu memiliki potensi luar biasa yang masih bisa digali lebih dalam lagi. Bersama Bapak dan dukungan hangat dari orang tua, mari kita jadikan semester berikutnya sebagai panggung pembuktian prestasimu!`,
    `Semangat belajarmu adalah inspirasi yang berharga bagi kita semua. Bapak dan orang tua berkomitmen penuh untuk terus mendampingi serta memfasilitasi jalanmu menuju masa depan yang cerah dan sukses!`,
    `Setiap hari adalah lembaran baru untuk mengukir cerita sukses yang baru. Mari kita songsong semester depan dengan komitmen penuh, didampingi doa dan bimbingan tiada henti dari Bapak serta orang tua!`,
    `Fokus pada proses dan teruslah berusaha memberikan yang terbaik di setiap kesempatan. Bapak dan orang tua akan selalu menjadi pendukung setiamu, membantumu melewati rintangan untuk mencapai bintang!`,
    `Jangan pernah ragu akan kemampuan hebat yang ada di dalam dirimu sendiri. Bapak bersama orang tua akan terus mendampingi perjalanan belajarmu agar cita-citamu dapat tercapai satu demi satu!`,
    `Prestasi gemilang adalah buah dari kesabaran, disiplin, dan doa yang tulus. Bapak dan orang tua selalu siap sedia menuntun langkahmu menuju tangga kesuksesan di masa depan!`,
    `Mari kita perkuat komitmen belajar kita demi menghadapi tantangan semester depan dengan kepala tegak. Bapak dan orang tua selalu di sini, siap memberikan dukungan terbaik agar kamu terus tumbuh berprestasi!`,
    `Keberanianmu untuk terus mencoba dan memperbaiki diri adalah kunci kesuksesan sejati. Bersama Bapak dan orang tua, mari kita wujudkan impian dan prestasi belajarmu yang luar biasa!`
  ];
  
  // Simple deterministic hash based on studentId and studentName
  const combined = studentId + studentName;
  let hash = 0;
  for (let i = 0; i < combined.length; i++) {
    hash = combined.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % motivations.length;
  return motivations[index] + "”";
};

export function getDetailedStudentCareerProfile(
  subjects: string[],
  topGrades: { semesterName?: string; subjectName: string; grade: number }[] = [],
  studentName: string = 'Siswa',
  studentGender?: string
): StudentCareerProfile {
  const firstName = studentName ? studentName.split(' ')[0] : 'Ananda';

  // Helper matching function
  function matchSubjectCategory(subName: string): string | null {
    const s = subName.toLowerCase();
    if (s.includes('agama') || s.includes('budi pekerti') || s.includes('pai') || s.includes('pak') || s.includes('keagamaan')) {
      return 'Pendidikan Agama dan Budi Pekerti';
    }
    if (s.includes('pancasila') || s.includes('pkn') || s.includes('ppkn') || s.includes('kewarganegaraan')) {
      return 'Pancasila';
    }
    if (s.includes('inggris') || s.includes('english') || s.includes('b. ing') || s.includes('inggr')) {
      return 'Bahasa Inggris';
    }
    if (s.includes('indonesia') || s.includes('indo') || s.includes('b.indo') || s.includes('b. indo')) {
      return 'Bahasa Indonesia';
    }
    if (s.includes('matematika') || s.includes('mtk') || s.includes('math') || s.includes('kalkulus') || s.includes('aljabar') || s.includes('hitung') || s.includes('numerasi')) {
      return 'Matematika';
    }
    if (s.includes('ipa') || s.includes('biologi') || s.includes('kimia') || s.includes('fisika') || s.includes('sains') || s.includes('science') || s.includes('ilmu pengetahuan alam') || s.includes('alam')) {
      return 'Ilmu Pengetahuan Alam (IPA)';
    }
    if (s.includes('ips') || s.includes('sejarah') || s.includes('ekonomi') || s.includes('geografi') || s.includes('sosiologi') || s.includes('sosial') || s.includes('ilmu pengetahuan sosial')) {
      return 'Ilmu Pengetahuan Sosial (IPS)';
    }
    if (s.includes('pjok') || s.includes('olahraga') || s.includes('jasmani') || s.includes('penjas') || s.includes('penjaskes') || s.includes('kebugaran')) {
      return 'Pendidikan Jasmani Olahraga dan Kesehatan';
    }
    if (s.includes('informatika') || s.includes('komputer') || s.includes('tik') || s.includes('coding') || s.includes('teknologi') || s.includes('software') || s.includes('programming') || s.includes('rpl')) {
      return 'Informatika';
    }
    if (s.includes('seni') || s.includes('prakarya') || s.includes('budaya') || s.includes('musik') || s.includes('rupa') || s.includes('tari') || s.includes('sbk') || s.includes('desain') || s.includes('keterampilan')) {
      return 'Seni dan Prakarya';
    }
    return null;
  }

  const SUBJECT_PROFILES: Record<string, {
    careers: string[];
    specialization: string;
    cognitiveStrength: string;
    recommendedSteps: string[];
    color: string;
  }> = {
    'Pendidikan Agama dan Budi Pekerti': {
      careers: ['Guru/dosen Pendidikan Agama', 'Pengelola Lembaga Keagamaan'],
      specialization: 'Pendidikan Agama & Keagamaan',
      cognitiveStrength: 'keteladanan moral, budi pekerti yang luhur, kematangan spiritual, serta kepedulian yang tinggi terhadap nilai-nilai keagamaan',
      recommendedSteps: [
        'Ikuti kegiatan keagamaan, kajian remaja, atau kepanitiaan hari besar keagamaan di sekolah.',
        'Kembangkan kapasitas kepemimpinan etis dan keterampilan mengajar/presentasi.',
        'Arahkan ke sekolah lanjutan umum/agama dengan program studi Pendidikan Agama atau Manajemen Keagamaan.'
      ],
      color: 'emerald'
    },
    'Pancasila': {
      careers: ['Diplomat', 'Hakim', 'Staf Pemerintahan atau Administrasi Negara'],
      specialization: 'Tata Kelola Publik & Hukum',
      cognitiveStrength: 'kesadaran konstitusi yang tinggi, jiwa kepemimpinan etis, integritas moral kuat, serta pemahaman mendalam tentang tata negara',
      recommendedSteps: [
        'Aktif dalam organisasi OSIS/MPK, debat konstitusi, atau simulasi diplomasi.',
        'Perdalam literasi mengenai sejarah politik, hukum pemerintahan, dan hubungan internasional.',
        'Arahkan ke sekolah lanjutan dengan target studi Ilmu Hukum, Ilmu Politik, atau Administrasi Negara.'
      ],
      color: 'blue'
    },
    'Bahasa Indonesia': {
      careers: ['Guru Bahasa Indonesia', 'Penulis', 'Editor', 'Public Relations (Humas)'],
      specialization: 'Sastra, Penulisan & Komunikasi',
      cognitiveStrength: 'kepekaan linguistik yang baik, daya literasi yang kaya, serta kecakapan menyusun gagasan ke dalam tulisan dan komunikasi publik',
      recommendedSteps: [
        'Aktif menulis artikel, esai, karya sastra (puisi/cerpen), atau mengelola majalah dinding sekolah.',
        'Ikuti pelatihan jurnalistik, public speaking, atau lomba debat bahasa.',
        'Persiapkan kelanjutan studi ke rumpun ilmu Sastra Indonesia, Ilmu Komunikasi, atau Keguruan Bahasa.'
      ],
      color: 'pink'
    },
    'Matematika': {
      careers: ['Data Analyst', 'Programmer', 'Insinyur', 'Analis Keuangan', 'Ilmuwan Data (Data Scientist)'],
      specialization: 'Sains Kuantitatif & Rekayasa',
      cognitiveStrength: 'kecerdasan logis-matematis yang sangat tajam, ketelitian kalkulasi kuantitatif, dan ketahanan mental tinggi dalam memecahkan masalah analitis',
      recommendedSteps: [
        'Ikuti klub matematika sekolah, latihan soal HOTS, atau kompetisi olimpiade matematika.',
        'Kenalkan dasar pemrograman komputer, pemodelan data, atau literasi keuangan dasar.',
        'Arahkan peminatan sekolah lanjutan ke rumpun MIPA, Teknik, atau Sains Data.'
      ],
      color: 'indigo'
    },
    'Ilmu Pengetahuan Alam (IPA)': {
      careers: ['Dokter', 'Apoteker', 'Peneliti Sains', 'Teknolog Laboratorium'],
      specialization: 'Kedokteran & Sains Hayati',
      cognitiveStrength: 'daya observasi fenomena alamiah yang tajam, ketelitian penyelidikan eksperimental, serta dedikasi tinggi dalam mengeksplorasi ilmu sains',
      recommendedSteps: [
        'Libatkan dalam eksperimen praktikum mandiri, Karya Ilmiah Remaja (KIR), atau PMR.',
        'Eksplorasi literatur dasar seputar ilmu biologi manusia, anatomi dasar, atau farmasi.',
        'Arahkan ke sekolah lanjutan jurusan MIPA dengan persiapan masuk rumpun Kesehatan/Kedokteran/Farmasi.'
      ],
      color: 'teal'
    },
    'Ilmu Pengetahuan Sosial (IPS)': {
      careers: ['Analis Ekonomi', 'Sosiolog', 'Geografer', 'Peneliti Kebumian'],
      specialization: 'Sosial, Humaniora & Ekonomi',
      cognitiveStrength: 'kepekaan sosial yang matang, pemahaman kritis terhadap fenomena dinamika kemasyarakatan, serta kemampuan menganalisis sistem ekonomi dan geografis',
      recommendedSteps: [
        'Lakukan proyek mini pengamatan sosiologis masyarakat sekitar atau analisis berita ekonomi.',
        'Perkaya bacaan mengenai geografi kebumian, sejarah peradaban, dan ilmu sosiologi-ekonomi.',
        'Persiapkan studi lanjutan di bidang Ilmu Ekonomi, Geografi/Geologi, Sosiologi, atau IPS Terpadu.'
      ],
      color: 'amber'
    },
    'Bahasa Inggris': {
      careers: ['Dosen/ Guru Bahasa Inggris', 'Penerjemah', 'Diplomat', 'Public Relations'],
      specialization: 'Bahasa Asing & Komunikasi Internasional',
      cognitiveStrength: 'kecakapan komunikasi bahasa asing yang fasih secara lisan maupun tulisan, serta kepekaan lintas budaya untuk berinteraksi global',
      recommendedSteps: [
        'Ikuti klub debat Bahasa Inggris, latihan menulis opini berbahasa asing, atau berdiskusi aktif.',
        'Perbanyak menonton materi edukasi, presentasi, atau membaca buku sastra asing.',
        'Arahkan ke sekolah lanjutan dengan target sastra/pendidikan Bahasa Inggris atau Hubungan Internasional.'
      ],
      color: 'cyan'
    },
    'Pendidikan Jasmani Olahraga dan Kesehatan': {
      careers: ['Dosen/Guru PJOK', 'Pelatih Olahraga', 'Atlet', 'Instruktur Olahraga', 'Fisioterapis'],
      specialization: 'Keolahragaan & Kesehatan Kebugaran',
      cognitiveStrength: 'kecerdasan kinestetik-motorik yang prima, kedisiplinan dan stamina fisik yang tangguh, serta sportivitas tinggi',
      recommendedSteps: [
        'Ikuti klub/ekstrakurikuler olahraga prestasi dan ikuti kompetisi tingkat sekolah hingga daerah.',
        'Pelajari dasar-dasar nutrisi atletik, fisiologi gerak, dan manajemen pemulihan stamina.',
        'Persiapkan fisik dan akademis menuju seleksi sekolah kedinasan atau program studi Ilmu Keolahragaan/Fisioterapi.'
      ],
      color: 'orange'
    },
    'Informatika': {
      careers: ['Programmer', 'Web Developer', 'Game Developer', 'Data Analyst', 'Cybersecurity Specialist', 'UI/UX Designer'],
      specialization: 'Teknologi Informasi & Komputasi',
      cognitiveStrength: 'kemampuan berpikir komputasional terstruktur, kecepatan pemahaman logika algoritma digital, serta minat tinggi dalam merancang rekayasa teknologi',
      recommendedSteps: [
        'Fasilitasi bimbingan pemrograman dasar (Python, JavaScript) dan logika basis data.',
        'Dorong membuat proyek pembuatan aplikasi web/game sederhana atau ikuti hackathon pelajar.',
        'Arahkan sekolah lanjutan ke jurusan Teknik Komputer, RPL, atau Rekayasa Informatika.'
      ],
      color: 'blue'
    },
    'Seni dan Prakarya': {
      careers: ['Seniman', 'Pelukis', 'Ilustrator', 'Desainer Grafis', 'Sutradara', 'Penulis Naskah'],
      specialization: 'Industri Kreatif & Seni Budaya',
      cognitiveStrength: 'orisinalitas ide kreatif yang tinggi, kepekaan rasa estetika yang mendalam, serta keterampilan mengekspresikan gagasan lewat karya seni',
      recommendedSteps: [
        'Buat portofolio kumpulan karya seni visual, desain grafis, naskah drama, atau aransemen musik.',
        'Ikuti pameran seni sekolah, kontes kreasi visual digital, atau festival pertunjukan drama.',
        'Persiapkan kelanjutan pendidikan ke rumpun Desain Komunikasi Visual (DKV), Seni Rupa, atau Perfilman.'
      ],
      color: 'rose'
    }
  };

  const matchedCategories: string[] = [];
  const allSubUnique = Array.from(new Set([
    ...subjects,
    ...topGrades.map(g => g.subjectName)
  ]));

  for (const sub of allSubUnique) {
    const cat = matchSubjectCategory(sub);
    if (cat && !matchedCategories.includes(cat)) {
      matchedCategories.push(cat);
    }
  }

  // Fallback if none matched
  if (matchedCategories.length === 0) {
    matchedCategories.push('Matematika');
  }

  const primaryCat = matchedCategories[0];
  const secondaryCat = matchedCategories[1];
  const tertiaryCat = matchedCategories[2];

  let specializationTrack = '';
  if (matchedCategories.length >= 3) {
    specializationTrack = `${SUBJECT_PROFILES[primaryCat].specialization}, ${SUBJECT_PROFILES[secondaryCat].specialization}, & ${SUBJECT_PROFILES[tertiaryCat].specialization}`;
  } else if (matchedCategories.length === 2) {
    specializationTrack = `${SUBJECT_PROFILES[primaryCat].specialization} & ${SUBJECT_PROFILES[secondaryCat].specialization}`;
  } else {
    specializationTrack = SUBJECT_PROFILES[primaryCat].specialization;
  }

  let careerPaths: string[] = matchedCategories.map((cat, idx) => {
    return `Opsi ${idx + 1} = ${SUBJECT_PROFILES[cat].careers.join(', ')}`;
  });

  let cognitiveStrength = '';
  if (matchedCategories.length >= 2) {
    cognitiveStrength = `Kombinasi luar biasa dari potensi unggul ananda pada beberapa bidang akademik utama. ${firstName} menunjukkan ${SUBJECT_PROFILES[primaryCat].cognitiveStrength}. Hal ini berpadu serasi dengan ${SUBJECT_PROFILES[secondaryCat].cognitiveStrength}, memberikan bekal istimewa untuk bersaing di tingkat lanjutan.`;
  } else {
    cognitiveStrength = `Ananda ${firstName} menunjukkan kekuatan belajar yang luar biasa dalam ${SUBJECT_PROFILES[primaryCat].cognitiveStrength}. Karakteristik ini menjadi modal emas utama untuk terus dikembangkan di jenjang pendidikan berikutnya.`;
  }

  let recommendedSteps: string[] = [];
  if (matchedCategories.length >= 3) {
    recommendedSteps = [
      SUBJECT_PROFILES[primaryCat].recommendedSteps[0],
      SUBJECT_PROFILES[secondaryCat].recommendedSteps[1] || SUBJECT_PROFILES[primaryCat].recommendedSteps[1],
      SUBJECT_PROFILES[tertiaryCat].recommendedSteps[2] || SUBJECT_PROFILES[primaryCat].recommendedSteps[2],
    ];
  } else if (matchedCategories.length === 2) {
    recommendedSteps = [
      SUBJECT_PROFILES[primaryCat].recommendedSteps[0],
      SUBJECT_PROFILES[secondaryCat].recommendedSteps[0],
      SUBJECT_PROFILES[primaryCat].recommendedSteps[2]
    ];
  } else {
    recommendedSteps = SUBJECT_PROFILES[primaryCat].recommendedSteps;
  }

  const majorBadgeColor = SUBJECT_PROFILES[primaryCat].color;

  const dominantList = topGrades.length > 0 
    ? Array.from(new Set(topGrades.map(g => g.subjectName))).slice(0, 4)
    : subjects.slice(0, 3);

  const cleanPaths = careerPaths.map(p => p.replace(/^Opsi \d+\s*=\s*/, ''));
  const summarySentence = `Pencapaian mapel unggulan ${dominantList.join(', ')} menunjukkan potensi istimewa ${firstName} untuk berkembang optimal pada ${specializationTrack}, dengan prospek karier menjanjikan sebagai ${cleanPaths.slice(0, 3).join(', ')}.`;

  return {
    specializationTrack,
    majorBadgeColor,
    topDominantSubjects: dominantList,
    careerPaths,
    cognitiveStrength,
    recommendedSteps,
    summarySentence
  };
}

function getFuturePotential(subjects: string[]): string {
  const profile = getDetailedStudentCareerProfile(subjects);
  return `${profile.careerPaths.slice(0, 4).join(', ')} (Jalur Peminatan: ${profile.specializationTrack})`;
}

export interface KKTPSubjectEvaluation {
  subjectName: string;
  grades: { semesterName: string; grade: number }[];
  average: number;
  trendType: 'improving' | 'declining' | 'fluctuating' | 'stable' | 'recovered' | 'single';
  trendLabel: string;
  trendBadgeColor: string;
  motivation: string;
  strategy: string;
  solutions: string[];
}

export function generateKKTPSubjectEvaluation(
  subjectName: string,
  grades: { semesterName: string; grade: number }[],
  studentName: string = 'Siswa'
): KKTPSubjectEvaluation {
  const firstName = studentName ? studentName.split(' ')[0] : 'Ananda';
  const validGrades = grades.filter(g => typeof g.grade === 'number' && !isNaN(g.grade));
  const avg = validGrades.length > 0
    ? parseFloat((validGrades.reduce((sum, g) => sum + g.grade, 0) / validGrades.length).toFixed(1))
    : 0;

  const sLower = subjectName.toLowerCase();
  
  // Categorize subject
  let category: 'math_science' | 'languages' | 'social_humanities' | 'religion' | 'arts_physical_skills' | 'general' = 'general';
  if (sLower.includes('matematika') || sLower.includes('ipa') || sLower.includes('fisika') || sLower.includes('biologi') || sLower.includes('kimia') || sLower.includes('informatika') || sLower.includes('tik') || sLower.includes('komputer')) {
    category = 'math_science';
  } else if (sLower.includes('indonesia') || sLower.includes('inggris') || sLower.includes('sunda') || sLower.includes('jawa') || sLower.includes('bahasa') || sLower.includes('arab') || sLower.includes('literasi')) {
    category = 'languages';
  } else if (sLower.includes('ips') || sLower.includes('pancasila') || sLower.includes('pkn') || sLower.includes('ppkn') || sLower.includes('kewarganegaraan') || sLower.includes('sejarah') || sLower.includes('geografi') || sLower.includes('ekonomi') || sLower.includes('sosiologi')) {
    category = 'social_humanities';
  } else if (sLower.includes('agama') || sLower.includes('budi pekerti') || sLower.includes('pai') || sLower.includes('pak') || sLower.includes('keagamaan')) {
    category = 'religion';
  } else if (sLower.includes('pjok') || sLower.includes('olahraga') || sLower.includes('penjas') || sLower.includes('seni') || sLower.includes('rupa') || sLower.includes('musik') || sLower.includes('tari') || sLower.includes('prakarya') || sLower.includes('keterampilan')) {
    category = 'arts_physical_skills';
  }

  // Trend analysis based on semester progression
  let trendType: KKTPSubjectEvaluation['trendType'] = 'single';
  let trendLabel = 'Data Awal';
  let trendBadgeColor = 'bg-slate-100 text-slate-700 border-slate-300';

  if (validGrades.length >= 2) {
    const firstGrade = validGrades[0].grade;
    const lastGrade = validGrades[validGrades.length - 1].grade;
    const prevGrade = validGrades[validGrades.length - 2].grade;
    const diffOverall = lastGrade - firstGrade;
    const diffLatest = lastGrade - prevGrade;

    if (lastGrade >= 75 && validGrades.some(g => g.grade < 75)) {
      trendType = 'recovered';
      trendLabel = `🎉 Pulih & Tuntas di Semester Akhir (${lastGrade})`;
      trendBadgeColor = 'bg-emerald-100 text-emerald-800 border-emerald-300';
    } else if (diffOverall >= 3 || diffLatest >= 3) {
      const diffVal = diffOverall > 0 ? diffOverall : diffLatest;
      trendType = 'improving';
      trendLabel = `📈 Tren Meningkat (+${diffVal} Poin)`;
      trendBadgeColor = 'bg-emerald-50 text-emerald-700 border-emerald-200';
    } else if (diffOverall <= -3 || diffLatest <= -3) {
      const diffVal = diffOverall < 0 ? diffOverall : diffLatest;
      trendType = 'declining';
      trendLabel = `📉 Mengalami Penurunan (${diffVal} Poin)`;
      trendBadgeColor = 'bg-rose-100 text-rose-800 border-rose-300';
    } else {
      let isUpAndDown = false;
      for (let i = 1; i < validGrades.length - 1; i++) {
        if ((validGrades[i].grade > validGrades[i - 1].grade && validGrades[i].grade > validGrades[i + 1].grade) ||
            (validGrades[i].grade < validGrades[i - 1].grade && validGrades[i].grade < validGrades[i + 1].grade)) {
          isUpAndDown = true;
          break;
        }
      }
      if (isUpAndDown) {
        trendType = 'fluctuating';
        trendLabel = '〰 Tren Fluktuatif (Perlu Konsistensi)';
        trendBadgeColor = 'bg-amber-100 text-amber-800 border-amber-300';
      } else {
        trendType = 'stable';
        trendLabel = '📌 Nilai Relatif Stabil di Bawah KKTP';
        trendBadgeColor = 'bg-amber-50 text-amber-700 border-amber-200';
      }
    }
  } else if (validGrades.length === 1) {
    trendLabel = `Skor Semester: ${validGrades[0].grade} (Perlu Penguatan)`;
    trendBadgeColor = 'bg-amber-100 text-amber-800 border-amber-300';
  }

  // Dynamic Motivation
  let motivation = '';
  if (trendType === 'recovered') {
    motivation = `Hebat sekali ${firstName}! Terlihat jelas perjuangan ananda untuk bangkit hingga berhasil menembus standar KKTP (${validGrades[validGrades.length - 1].grade}) di semester terakhir. Pertahankan ritme belajar positif ini agar terus konsisten!`;
  } else if (trendType === 'improving') {
    motivation = `Usaha dan kerja keras ${firstName} pada mata pelajaran ${subjectName} mulai membuahkan hasil nyata dengan adanya tren kenaikan nilai yang positif. Selangkah lagi ananda pasti mampu melampaui batas standar KKTP 75 dengan gemilang!`;
  } else if (trendType === 'declining') {
    motivation = `Jangan berkecil hati ya ${firstName}, penurunan nilai di semester terakhir adalah proses evaluasi bahwa materi semakin mendalam. Dengan strategi belajar yang tepat dan bimbingan rutin, ananda pasti bisa kembali ke performa terbaik.`;
  } else if (trendType === 'fluctuating') {
    motivation = `Potensi ${firstName} di mata pelajaran ${subjectName} sebenarnya sangat baik saat fokus penuh. Kunci utama untuk ananda saat ini adalah menjaga konsistensi belajar harian dan tidak menunda pengerjaan tugas.`;
  } else {
    motivation = `Setiap proses belajar memiliki ritmenya masing-masing. ${firstName} memiliki kemampuan untuk menguasai ${subjectName} dengan baik asalkan tidak ragu bertanya dan mau mencoba cara belajar yang baru.`;
  }

  // Subject Strategy
  let strategy = '';
  if (category === 'math_science') {
    strategy = `Fokus pada penguasaan konsep dasar dan pemahaman logika rumus, bukan sekadar menghafal. Buat rangkuman rumus visual (mind map/cheat sheet) dan biasakan mengerjakan minimal 3-5 soal latihan mandiri secara bertahap setiap selesai satu bab.`;
  } else if (category === 'languages') {
    strategy = `Tingkatkan pembiasaan literasi dengan membaca teks/bacaan terarah selama 15 menit per hari, mencatat kosakata sulit (vocabulary bank), serta berlatih menulis ringkasan dan menjawab soal pemahaman bacaan secara runtut.`;
  } else if (category === 'social_humanities') {
    strategy = `Gunakan metode bagan alur konsep / mind mapping untuk menghubungkan fakta sejarah, kondisi geografis, atau nilai kewarganegaraan dengan fenomena nyata di sekitar. Aktif dalam diskusi dan pembuatan rangkuman berbasis poin kunci.`;
  } else if (category === 'religion') {
    strategy = `Perkuat literasi baca-tulis ayat/dalil dan pendalaman hikmah ajaran moral dalam kehidupan sehari-hari. Lakukan konsultasi untuk materi hukum bacaan atau konsep pemahaman yang dirasa belum tuntas.`;
  } else if (category === 'arts_physical_skills') {
    strategy = `Tingkatkan frekuensi latihan teknik dasar secara konsisten dan terukur. Fokus pada penyelesaian tugas karya/praktik tepat waktu dengan memperhatikan rubrik penilaian yang diberikan guru.`;
  } else {
    strategy = `Identifikasi sub-bab atau topik yang paling sulit dipahami, lalu buat catatan intisari materi tersendiri serta minta umpan balik berkala dari guru mata pelajaran.`;
  }

  // Actionable Solutions
  let solutions: string[] = [];
  if (category === 'math_science') {
    solutions = [
      `Mengikuti program klinik remedial / bimbingan kelompok kecil bersama guru mata pelajaran ${subjectName} untuk membedah materi yang belum tuntas.`,
      `Membentuk kelompok belajar tutor sebaya bersama teman sekelas yang memiliki nilai unggul di mata pelajaran ini.`,
      `Menyisihkan jadwal belajar mandiri khusus 25-30 menit per hari untuk latihan soal bertingkat (dari tingkat dasar ke variasi soal HOTS).`,
      `Konsultasi langsung ke guru pengampu ketika menemukan kendala konsep sebelum pelaksanaan asesmen/ujian berikutnya.`
    ];
  } else if (category === 'languages') {
    solutions = [
      `Mengikuti sesi perbaikan tugas literasi dan remedial penilaian harian yang diselenggarakan guru pengampu.`,
      `Membuat buku saku kosakata/tata bahasa baru dan melatihnya dalam penyusunan kalimat terstruktur setiap minggu.`,
      `Melatih pengerjaan latihan pemahaman bacaan dan meminta bimbingan koreksi kepada guru atau teman kelompok belajar.`,
      `Membangun komunikasi aktif dengan wali kelas untuk memantau kemajuan pengerjaan tugas bahasa secara berkala.`
    ];
  } else if (category === 'social_humanities') {
    solutions = [
      `Menyusun rangkuman visual / infografis ringkas untuk setiap Kompetensi Dasar yang nilainya masih di bawah KKTP.`,
      `Mengikuti program remedial terstruktur dan pengayaan materi bersama guru mapel.`,
      `Belajar kelompok dengan metode saling menjelaskan konsep (peer teaching) untuk memperkuat daya ingat dan analisis.`,
      `Menjadwalkan waktu review materi 2 kali seminggu menjelang asesmen sumatif.`
    ];
  } else {
    solutions = [
      `Mendaftar pada sesi bimbingan remedial khusus untuk menuntaskan capaian tujuan pembelajaran yang masih kurang.`,
      `Membuat jadwal belajar rutin mandiri di rumah dengan pendampingan orang tua dan pantauan wali kelas.`,
      `Memanfaatkan bahan ajar digital atau video penjelasan materi untuk mempermudah pemahaman konsep yang abstrak.`,
      `Melakukan refleksi berkala bersama wali kelas untuk memastikan progres belajar terus menunjukkan tren positif.`
    ];
  }

  return {
    subjectName,
    grades: validGrades,
    average: avg,
    trendType,
    trendLabel,
    trendBadgeColor,
    motivation,
    strategy,
    solutions
  };
}

interface StudentAcademicProgressManagerProps {
  homeroomClass: string;
  students: Student[];
  academicProgress: StudentAcademicProgress[];
  onSaveAcademicProgress?: (progress: StudentAcademicProgress, method: 'POST' | 'PUT' | 'DELETE') => Promise<any>;
  activeUser?: Teacher;
  schoolSettings?: SchoolSettings;
  appTheme?: string;
}

const StudentAcademicProgressManager: React.FC<StudentAcademicProgressManagerProps> = ({
  homeroomClass,
  students,
  academicProgress,
  onSaveAcademicProgress,
  activeUser,
  schoolSettings,
}) => {
  // Filter students belonging to this homeroom class
  const classStudents = useMemo(() => {
    return students.filter(s => s.class === homeroomClass && s.status === 'Aktif');
  }, [students, homeroomClass]);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState<string>('');

  // Filter student list by search query
  const filteredStudents = useMemo(() => {
    return classStudents.filter(s => 
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
      s.nis.includes(searchQuery)
    );
  }, [classStudents, searchQuery]);

  // Selected student's progress data
  const currentProgress = useMemo(() => {
    return academicProgress.find(p => p.studentId === selectedStudentId);
  }, [academicProgress, selectedStudentId]);

  const selectedStudent = useMemo(() => {
    return classStudents.find(s => s.id === selectedStudentId);
  }, [classStudents, selectedStudentId]);

  const currentStudentIndex = useMemo(() => {
    return classStudents.findIndex(s => s.id === selectedStudentId);
  }, [classStudents, selectedStudentId]);

  const handlePrevStudent = () => {
    if (classStudents.length === 0) return;
    if (currentStudentIndex > 0) {
      setSelectedStudentId(classStudents[currentStudentIndex - 1].id);
    } else {
      setSelectedStudentId(classStudents[classStudents.length - 1].id);
    }
    setInlineEditCell(null);
  };

  const handleNextStudent = () => {
    if (classStudents.length === 0) return;
    if (currentStudentIndex >= 0 && currentStudentIndex < classStudents.length - 1) {
      setSelectedStudentId(classStudents[currentStudentIndex + 1].id);
    } else {
      setSelectedStudentId(classStudents[0].id);
    }
    setInlineEditCell(null);
  };

  // Local Form / Interactive States
  const [newSemesterName, setNewSemesterName] = useState('');
  const [showAddSemester, setShowAddSemester] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [copiedNarrative, setCopiedNarrative] = useState(false);

  // Overview Table States (Default View)
  const [overviewSearchQuery, setOverviewSearchQuery] = useState('');
  const [overviewGenderFilter, setOverviewGenderFilter] = useState<'all' | 'Laki-laki' | 'Perempuan'>('all');
  const [overviewSort, setOverviewSort] = useState<'rank' | 'name' | 'nis' | 'totalSum' | 'avg'>('rank');
  const [overviewViewMode, setOverviewViewMode] = useState<'table' | 'cards'>('cards');

  // Student Detail View States
  const [studentScoreViewMode, setStudentScoreViewMode] = useState<'matrix' | 'cards'>('matrix');
  const [selectedSemesterTab, setSelectedSemesterTab] = useState<string>('');

  // Bulk Upload States & Ref
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState<'merge' | 'replace'>('merge');
  const [parsedBulkData, setParsedBulkData] = useState<{
    matchedStudent: Student;
    semesters: { semesterName: string; subjects: SubjectGrade[] }[];
  }[]>([]);
  const [unmatchedRows, setUnmatchedRows] = useState<any[]>([]);
  const [uploadFileName, setUploadFileName] = useState('');
  const [isProcessingBulk, setIsProcessingBulk] = useState(false);
  const bulkFileInputRef = useRef<HTMLInputElement>(null);

  const [templateSubjectsInput, setTemplateSubjectsInput] = useState(
    'Pendidikan Agama dan Budi Pekerti, Pancasila, Bahasa Indonesia, Matematika, Ilmu Pengetahuan Alam (IPA), Ilmu Pengetahuan Sosial (IPS), Bahasa Inggris, Pendidikan Jasmani Olahraga dan Kesehatan, Informatika, Seni dan Prakarya'
  );
  const [showTemplateSettings, setShowTemplateSettings] = useState(false);

  // Template Download Handler
  const handleDownloadTemplate = () => {
    const customSubjects = templateSubjectsInput
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0);

    const rows: any[] = [];

    if (classStudents.length === 0) {
      const sampleRow: any = {
        'NIS': '202401',
        'Nama Siswa': 'Siswa Contoh',
        'Semester': 'Semester 1'
      };
      customSubjects.forEach(sub => {
        sampleRow[sub] = 85;
      });
      rows.push(sampleRow);
    } else {
      classStudents.forEach(st => {
        const row: any = {
          'NIS': st.nis,
          'Nama Siswa': st.name,
          'Semester': 'Semester 1'
        };
        customSubjects.forEach(sub => {
          row[sub] = ''; // Blank for teacher to fill
        });
        rows.push(row);
      });
    }

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Format Upload Nilai');

    const cols = [
      { wch: 15 }, // NIS
      { wch: 30 }, // Nama
      { wch: 15 }, // Semester
      ...customSubjects.map(() => ({ wch: 15 })) // Subjects
    ];
    ws['!cols'] = cols;

    XLSX.writeFile(wb, `Template_Nilai_Akademik_Kelas_${homeroomClass}.xlsx`);
  };

  // Bulk File Selection Handler
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadFileName(file.name);
    const reader = new FileReader();

    reader.onload = (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const jsonData = XLSX.utils.sheet_to_json<any>(ws);

        if (!jsonData || jsonData.length === 0) {
          alert('File Excel/CSV kosong atau format tidak sesuai.');
          return;
        }

        const studentMap = new Map<string, {
          matchedStudent: Student;
          semesterMap: Map<string, Map<string, number>>;
        }>();

        const unmatchedList: any[] = [];

        jsonData.forEach((row: any) => {
          const nisKey = Object.keys(row).find(k => k.trim().toLowerCase() === 'nis');
          const nameKey = Object.keys(row).find(k => k.trim().toLowerCase().includes('nama'));
          const semKey = Object.keys(row).find(k => k.trim().toLowerCase().includes('semester') || k.trim().toLowerCase().includes('sms'));

          const nisVal = nisKey && row[nisKey] ? String(row[nisKey]).trim() : '';
          const nameVal = nameKey && row[nameKey] ? String(row[nameKey]).trim() : '';
          const semVal = semKey && row[semKey] ? String(row[semKey]).trim() : 'Semester 1';

          const matched = classStudents.find(s => 
            (nisVal && s.nis.trim() === nisVal) || 
            (nameVal && s.name.trim().toLowerCase() === nameVal.toLowerCase())
          );

          if (!matched) {
            unmatchedList.push({ nis: nisVal, name: nameVal, semester: semVal, error: 'Siswa tidak ditemukan' });
            return;
          }

          if (!studentMap.has(matched.id)) {
            studentMap.set(matched.id, {
              matchedStudent: matched,
              semesterMap: new Map()
            });
          }

          const stEntry = studentMap.get(matched.id)!;
          if (!stEntry.semesterMap.has(semVal)) {
            stEntry.semesterMap.set(semVal, new Map());
          }

          const semSubjectsMap = stEntry.semesterMap.get(semVal)!;

          // Process all other keys as subjects
          Object.keys(row).forEach(key => {
            if (key !== nisKey && key !== nameKey && key !== semKey) {
              const rawGrade = row[key];
              if (rawGrade !== undefined && rawGrade !== null && rawGrade !== '') {
                const numGrade = Number(rawGrade);
                if (!isNaN(numGrade) && numGrade >= 0 && numGrade <= 100) {
                  semSubjectsMap.set(key.trim(), numGrade);
                }
              }
            }
          });
        });

        const parsedArray = Array.from(studentMap.values()).map(entry => {
          const semesters: { semesterName: string; subjects: SubjectGrade[] }[] = [];
          
          entry.semesterMap.forEach((subMap, semName) => {
            const subjects: SubjectGrade[] = [];
            subMap.forEach((grade, subjectName) => {
              subjects.push({ subjectName, grade });
            });
            semesters.push({ semesterName: semName, subjects });
          });

          return {
            matchedStudent: entry.matchedStudent,
            semesters
          };
        });

        setParsedBulkData(parsedArray);
        setUnmatchedRows(unmatchedList);
      } catch (err) {
        console.error('Failed to parse Excel file:', err);
        alert('Gagal membaca file Excel. Pastikan format file sesuai (.xlsx / .xls / .csv).');
      }
    };

    reader.readAsBinaryString(file);
  };

  // Submit Bulk Data
  const handleConfirmBulkSave = async () => {
    if (parsedBulkData.length === 0 || !onSaveAcademicProgress) return;
    setIsProcessingBulk(true);

    try {
      for (const item of parsedBulkData) {
        const studentId = item.matchedStudent.id;
        const existingProgress = academicProgress.find(p => p.studentId === studentId);

        let finalSemesters: SemesterProgress[] = [];

        if (bulkMode === 'replace' || !existingProgress) {
          finalSemesters = item.semesters;
        } else {
          const existingMap = new Map<string, Map<string, number>>();

          existingProgress.semesters.forEach(s => {
            const map = new Map<string, number>();
            s.subjects.forEach(sub => map.set(sub.subjectName.toLowerCase(), sub.grade));
            existingMap.set(s.semesterName.toLowerCase(), map);
          });

          item.semesters.forEach(s => {
            const semKey = s.semesterName.toLowerCase();
            if (!existingMap.has(semKey)) {
              existingMap.set(semKey, new Map());
            }
            const map = existingMap.get(semKey)!;
            s.subjects.forEach(sub => map.set(sub.subjectName.toLowerCase(), sub.grade));
          });

          const allSemesterNames = Array.from(
            new Set([
              ...existingProgress.semesters.map(s => s.semesterName),
              ...item.semesters.map(s => s.semesterName)
            ])
          );

          finalSemesters = allSemesterNames.map(semName => {
            const map = existingMap.get(semName.toLowerCase());
            const subjects: SubjectGrade[] = [];
            if (map) {
              map.forEach((grade, subNameKey) => {
                const originalSub = item.semesters
                  .flatMap(s => s.subjects)
                  .concat(existingProgress.semesters.flatMap(s => s.subjects))
                  .find(sub => sub.subjectName.toLowerCase() === subNameKey);

                subjects.push({
                  subjectName: originalSub ? originalSub.subjectName : subNameKey,
                  grade
                });
              });
            }
            return { semesterName: semName, subjects };
          });
        }

        const progressObj: StudentAcademicProgress = {
          id: studentId,
          studentId: studentId,
          class: homeroomClass,
          semesters: finalSemesters,
          createdAt: existingProgress?.createdAt || Date.now(),
          updatedAt: Date.now(),
        };

        await onSaveAcademicProgress(progressObj, existingProgress ? 'PUT' : 'POST');
      }

      alert(`Berhasil menyimpan data nilai masal untuk ${parsedBulkData.length} siswa!`);
      setIsBulkUploadModalOpen(false);
      setParsedBulkData([]);
      setUnmatchedRows([]);
      setUploadFileName('');
      if (bulkFileInputRef.current) bulkFileInputRef.current.value = '';
    } catch (error) {
      console.error('Error saving bulk academic progress:', error);
      alert('Terjadi kesalahan saat menyimpan data masal.');
    } finally {
      setIsProcessingBulk(false);
    }
  };

  // For adding a subject to a specific semester
  const [activeAddingSemester, setActiveAddingSemester] = useState<string | null>(null);
  const [newSubjectName, setNewSubjectName] = useState('');
  const [newSubjectGrade, setNewSubjectGrade] = useState<number | ''>('');

  // For editing subject inline inside the grid
  const [inlineEditCell, setInlineEditCell] = useState<{ semesterName: string; subjectName: string } | null>(null);

  // For adding a completely new subject row across the board
  const [isAddingNewSubject, setIsAddingNewSubject] = useState(false);
  const [globalNewSubjectName, setGlobalNewSubjectName] = useState('');

  const handleUpdateGrade = async (semesterName: string, subjectName: string, gradeStr: string) => {
    if (!currentProgress || !onSaveAcademicProgress) return;
    
    if (gradeStr.trim() === '') {
      // If empty, delete the subject from this semester entirely
      const updatedSemesters = currentProgress.semesters.map(sem => {
        if (sem.semesterName === semesterName) {
          return {
            ...sem,
            subjects: sem.subjects.filter(s => s.subjectName !== subjectName)
          };
        }
        return sem;
      });
      const progressObj = { ...currentProgress, semesters: updatedSemesters, updatedAt: Date.now() };
      await onSaveAcademicProgress(progressObj, 'PUT');
      return;
    }

    const grade = Number(gradeStr);
    if (isNaN(grade) || grade < 0 || grade > 100) {
      alert('Nilai harus berupa angka 0 - 100.');
      return;
    }

    const updatedSemesters = currentProgress.semesters.map(sem => {
      if (sem.semesterName === semesterName) {
        const existingSubIndex = sem.subjects.findIndex(s => s.subjectName === subjectName);
        const newSubjects = [...sem.subjects];
        if (existingSubIndex >= 0) {
          newSubjects[existingSubIndex].grade = grade;
        } else {
          newSubjects.push({ subjectName, grade });
        }
        return { ...sem, subjects: newSubjects };
      }
      return sem;
    });

    const progressObj = { ...currentProgress, semesters: updatedSemesters, updatedAt: Date.now() };
    await onSaveAcademicProgress(progressObj, 'PUT');
  };

  const handleAddGlobalSubject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!globalNewSubjectName.trim() || !currentProgress || !onSaveAcademicProgress) return;
    
    const subjectName = globalNewSubjectName.trim();
    const updatedSemesters = [...currentProgress.semesters];
    
    if (updatedSemesters.length > 0) {
      // Check if it already exists anywhere
      let exists = false;
      for (const sem of updatedSemesters) {
        if (sem.subjects.some(s => s.subjectName.toLowerCase() === subjectName.toLowerCase())) {
          exists = true;
          break;
        }
      }
      
      if (!exists) {
        updatedSemesters[0] = {
          ...updatedSemesters[0],
          subjects: [...updatedSemesters[0].subjects, { subjectName, grade: 0 }]
        };
      }
    }

    const progressObj = { ...currentProgress, semesters: updatedSemesters, updatedAt: Date.now() };
    await onSaveAcademicProgress(progressObj, 'PUT');
    setIsAddingNewSubject(false);
    setGlobalNewSubjectName('');
  };

  // Initialize Standard 1-6 Semesters
  const handleInitializeDefaultSemesters = async () => {
    if (!selectedStudentId || !onSaveAcademicProgress) return;

    const defaultSemesters: SemesterProgress[] = [
      { semesterName: 'Semester 1', subjects: [] },
      { semesterName: 'Semester 2', subjects: [] },
      { semesterName: 'Semester 3', subjects: [] },
      { semesterName: 'Semester 4', subjects: [] },
      { semesterName: 'Semester 5', subjects: [] },
      { semesterName: 'Semester 6', subjects: [] },
    ];

    const newProgress: StudentAcademicProgress = {
      id: selectedStudentId, // document id can match studentId
      studentId: selectedStudentId,
      class: homeroomClass,
      semesters: defaultSemesters,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };

    await onSaveAcademicProgress(newProgress, 'POST');
  };

  // Add Semester
  const handleAddSemester = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudentId || !newSemesterName.trim() || !onSaveAcademicProgress) return;

    const semName = newSemesterName.trim();
    const existingSemesters = currentProgress?.semesters || [];

    if (existingSemesters.some(s => s.semesterName.toLowerCase() === semName.toLowerCase())) {
      alert('Semester ini sudah ada.');
      return;
    }

    const updatedSemesters = [...existingSemesters, { semesterName: semName, subjects: [] }];
    const progressObj: StudentAcademicProgress = {
      id: selectedStudentId,
      studentId: selectedStudentId,
      class: homeroomClass,
      semesters: updatedSemesters,
      createdAt: currentProgress?.createdAt || Date.now(),
      updatedAt: Date.now(),
    };

    await onSaveAcademicProgress(progressObj, currentProgress ? 'PUT' : 'POST');
    setNewSemesterName('');
    setShowAddSemester(false);
  };

  // Delete Semester
  const handleDeleteSemester = async (semesterName: string) => {
    if (!selectedStudentId || !currentProgress || !onSaveAcademicProgress) return;
    if (!confirm(`Apakah Anda yakin ingin menghapus ${semesterName} beserta semua nilainya?`)) return;

    const updatedSemesters = currentProgress.semesters.filter(s => s.semesterName !== semesterName);
    const progressObj: StudentAcademicProgress = {
      ...currentProgress,
      semesters: updatedSemesters,
      updatedAt: Date.now(),
    };

    await onSaveAcademicProgress(progressObj, 'PUT');
  };

  // Calculate Average and Sum score per semester
  const semesterAverages = useMemo(() => {
    if (!currentProgress) return [];
    return currentProgress.semesters.map(sem => {
      if (sem.subjects.length === 0) return { name: sem.semesterName, avg: 0, sum: 0, count: 0 };
      const sum = sem.subjects.reduce((acc, curr) => acc + curr.grade, 0);
      return {
        name: sem.semesterName,
        avg: parseFloat((sum / sem.subjects.length).toFixed(1)),
        sum,
        count: sem.subjects.length
      };
    });
  }, [currentProgress]);

  // Overall total sum across all filled semesters
  const overallTotalSum = useMemo(() => {
    return semesterAverages.reduce((acc, curr) => acc + curr.sum, 0);
  }, [semesterAverages]);

  // Overall average across all filled semesters
  const overallAverage = useMemo(() => {
    const validAverages = semesterAverages.filter(s => s.count > 0);
    if (validAverages.length === 0) return 0;
    const totalSubjectCount = validAverages.reduce((acc, curr) => acc + curr.count, 0);
    if (totalSubjectCount === 0) return 0;
    return parseFloat((overallTotalSum / totalSubjectCount).toFixed(1));
  }, [semesterAverages, overallTotalSum]);

  // Ranking calculation across all students in homeroom class: BERDASARKAN JUMLAH SELURUH NILAI MAPEL
  const classRankings = useMemo(() => {
    if (!classStudents || classStudents.length === 0) {
      return { overallRankings: {}, semesterRankings: {} };
    }

    // 1. Calculate each student's semester sums, averages & overall total sum
    const studentDataList = classStudents.map(st => {
      const stProg = academicProgress.find(p => p.studentId === st.id);
      const semData: Record<string, { sum: number; avg: number; count: number }> = {};
      let totalSumAllSemesters = 0;
      let totalSubjectCountAllSemesters = 0;
      let validSemCount = 0;

      if (stProg && stProg.semesters) {
        stProg.semesters.forEach(sem => {
          if (sem.subjects && sem.subjects.length > 0) {
            const sum = sem.subjects.reduce((a, b) => a + b.grade, 0);
            const avg = parseFloat((sum / sem.subjects.length).toFixed(1));
            semData[sem.semesterName] = { sum, avg, count: sem.subjects.length };
            totalSumAllSemesters += sum;
            totalSubjectCountAllSemesters += sem.subjects.length;
            validSemCount++;
          }
        });
      }

      const overallAvg = totalSubjectCountAllSemesters > 0 
        ? parseFloat((totalSumAllSemesters / totalSubjectCountAllSemesters).toFixed(1)) 
        : 0;

      return { 
        studentId: st.id, 
        name: st.name, 
        totalSum: totalSumAllSemesters, 
        overallAvg, 
        semData, 
        validSemCount,
        subjectCount: totalSubjectCountAllSemesters
      };
    });

    // 2. Overall Rankings: DIHITUNG DARI JUMLAH SELURUH NILAI MAPEL (totalSum)
    const overallRankings: Record<string, { rank: number; total: number; score: number; totalSum: number; avg: number }> = {};
    const studentsWithOverallScore = studentDataList
      .filter(s => s.totalSum > 0)
      .sort((a, b) => {
        if (b.totalSum !== a.totalSum) {
          return b.totalSum - a.totalSum; // Peringkat dari jumlah seluruh nilai mapel
        }
        return b.overallAvg - a.overallAvg;
      });

    studentsWithOverallScore.forEach((item, index) => {
      overallRankings[item.studentId] = {
        rank: index + 1,
        total: studentsWithOverallScore.length,
        score: item.totalSum,
        totalSum: item.totalSum,
        avg: item.overallAvg
      };
    });

    // 3. Per Semester Rankings: DIHITUNG DARI JUMLAH NILAI MAPEL SEMESTER (sem.sum)
    const semesterRankings: Record<string, Record<string, { rank: number; total: number; score: number; totalSum: number; avg: number }>> = {};
    
    // Collect all semester names
    const allSemesterNames = new Set<string>();
    studentDataList.forEach(s => Object.keys(s.semData).forEach(semName => allSemesterNames.add(semName)));

    allSemesterNames.forEach(semName => {
      const semStudents = studentDataList
        .filter(s => s.semData[semName] && s.semData[semName].sum > 0)
        .sort((a, b) => {
          if (b.semData[semName].sum !== a.semData[semName].sum) {
            return b.semData[semName].sum - a.semData[semName].sum; // Peringkat dari jumlah nilai mapel
          }
          return b.semData[semName].avg - a.semData[semName].avg;
        });

      semStudents.forEach((item, index) => {
        if (!semesterRankings[item.studentId]) {
          semesterRankings[item.studentId] = {};
        }
        semesterRankings[item.studentId][semName] = {
          rank: index + 1,
          total: semStudents.length,
          score: item.semData[semName].sum,
          totalSum: item.semData[semName].sum,
          avg: item.semData[semName].avg
        };
      });
    });

    return { overallRankings, semesterRankings };
  }, [classStudents, academicProgress]);

  // Overview data with calculated ranks, total sums, and gender styling
  const overviewStudentList = useMemo(() => {
    return classStudents.map(student => {
      const studentRank = classRankings.overallRankings[student.id];
      const prog = academicProgress.find(p => p.studentId === student.id);
      
      let totalSum = 0;
      let totalSubjects = 0;
      let semCount = 0;
      
      if (prog && prog.semesters) {
        prog.semesters.forEach(sem => {
          if (sem.subjects && sem.subjects.length > 0) {
            const semSum = sem.subjects.reduce((a, b) => a + b.grade, 0);
            totalSum += semSum;
            totalSubjects += sem.subjects.length;
            semCount++;
          }
        });
      }

      const overallAvg = totalSubjects > 0 ? parseFloat((totalSum / totalSubjects).toFixed(1)) : 0;
      const rank = studentRank ? studentRank.rank : null;

      return {
        student,
        totalSum,
        totalSubjects,
        overallAvg,
        rank,
        semCount,
        hasData: totalSum > 0
      };
    });
  }, [classStudents, classRankings, academicProgress]);

  const filteredOverviewStudents = useMemo(() => {
    return overviewStudentList.filter(item => {
      const q = overviewSearchQuery.toLowerCase().trim();
      const matchesSearch = !q ||
        item.student.name.toLowerCase().includes(q) ||
        item.student.nis.includes(q) ||
        (item.student.nisn && item.student.nisn.includes(q));
      
      const matchesGender = 
        overviewGenderFilter === 'all' || item.student.gender === overviewGenderFilter;

      return matchesSearch && matchesGender;
    }).sort((a, b) => {
      if (overviewSort === 'rank') {
        if (a.rank !== null && b.rank !== null) return a.rank - b.rank;
        if (a.rank !== null) return -1;
        if (b.rank !== null) return 1;
        return a.student.name.localeCompare(b.student.name);
      }
      if (overviewSort === 'totalSum') {
        if (b.totalSum !== a.totalSum) return b.totalSum - a.totalSum;
        return b.overallAvg - a.overallAvg;
      }
      if (overviewSort === 'avg') {
        return b.overallAvg - a.overallAvg;
      }
      if (overviewSort === 'nis') {
        return a.student.nis.localeCompare(b.student.nis);
      }
      return a.student.name.localeCompare(b.student.name);
    });
  }, [overviewStudentList, overviewSearchQuery, overviewGenderFilter, overviewSort]);

  // Overview quick stats
  const overviewStats = useMemo(() => {
    const total = classStudents.length;
    const maleCount = classStudents.filter(s => s.gender === 'Laki-laki').length;
    const femaleCount = classStudents.filter(s => s.gender === 'Perempuan').length;
    const withDataCount = overviewStudentList.filter(s => s.hasData).length;
    
    const rank1Student = overviewStudentList.find(s => s.rank === 1 && s.totalSum > 0);
    const sortedByAvg = [...overviewStudentList].filter(s => s.overallAvg > 0).sort((a, b) => b.overallAvg - a.overallAvg);
    const topAvgStudent = sortedByAvg.length > 0 ? sortedByAvg[0] : null;

    return {
      total,
      maleCount,
      femaleCount,
      withDataCount,
      rank1Student,
      topAvgStudent
    };
  }, [classStudents, overviewStudentList]);

  // Generate Modern & Stylish Print Report (Rekap Perkembangan Siswa)
  // Generate Modern & Stylish Print Report (Rekap Perkembangan Siswa - 1 Page Per Student)
  const handlePrintReport = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow || !selectedStudent || !currentProgress) return;

    const schoolTitle = schoolSettings?.schoolName || 'SMP NEGERI 1 KAWALI';
    const govTitle = schoolSettings?.governmentName || 'PEMERINTAH DAERAH PROVINSI JAWA BARAT';
    const deptTitle = schoolSettings?.departmentName || 'DINAS PENDIDIKAN';
    const schoolAddress = schoolSettings?.address || 'Jl. Raya Kawali No. 1, Ciamis - Jawa Barat';
    const additionalKop = schoolSettings?.additionalKopLine || '';
    const academicYear = schoolSettings?.academicYear || '2025/2026';
    const homeroomTeacherName = activeUser?.name || 'Wali Kelas';
    const homeroomTeacherNip = activeUser?.nip ? `NIP. ${activeUser.nip}` : '-';
    const city = schoolSettings?.kabupaten || schoolSettings?.kecamatan || 'Kawali';

    const currentDate = new Date().toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    const studentOverallRank = classRankings.overallRankings[selectedStudent.id];
    const studentSemesterRanks = classRankings.semesterRankings[selectedStudent.id] || {};
    const hasEverEnteredTop10 = (studentOverallRank && studentOverallRank.rank <= 10) || currentProgress.semesters.some(sem => studentSemesterRanks[sem.semesterName] && studentSemesterRanks[sem.semesterName].rank <= 10);
    const leftLogoHtml = schoolSettings?.departmentLogoUrl
      ? `<img src="${schoolSettings.departmentLogoUrl}" style="height: 48px; max-width: 55px; object-fit: contain;" alt="Logo Dinas" />`
      : (schoolSettings?.logoUrl 
          ? `<img src="${schoolSettings.logoUrl}" style="height: 48px; max-width: 55px; object-fit: contain;" alt="Logo Sekolah" />` 
          : `<div style="width: 48px; height: 48px; background: linear-gradient(135deg, #059669 0%, #047857 100%); border-radius: 8px; display: flex; align-items: center; justify-content: center; color: white; font-weight: 900; font-size: 20px;">${schoolTitle.charAt(0)}</div>`);

    const rightLogoHtml = schoolSettings?.logoUrl
      ? `<img src="${schoolSettings.logoUrl}" style="height: 48px; max-width: 55px; object-fit: contain;" alt="Logo Sekolah" />`
      : `<div style="width: 48px; height: 48px;"></div>`;

    const semestersHtml = currentProgress.semesters.map((sem, sIdx) => {
      const semRank = studentSemesterRanks[sem.semesterName];
      const semSum = sem.subjects.reduce((a, b) => a + b.grade, 0);
      const semAvg = sem.subjects.length > 0
        ? (semSum / sem.subjects.length).toFixed(1)
        : '-';

      const subjectsRows = sem.subjects.map((sub, idx) => `
        <tr style="border-bottom: 1px solid #f1f5f9; ${idx % 2 === 1 ? 'background-color: #fafbfc;' : ''}">
          <td style="padding: 4.5px 6px; text-align: center; color: #64748b; font-size: 10px; width: 24px;">${idx + 1}</td>
          <td style="padding: 4.5px 6px; font-weight: 600; color: #1e293b; font-size: 10px;">${sub.subjectName}</td>
          <td style="padding: 4.5px 6px; text-align: center; font-weight: 800; color: ${sub.grade >= 75 ? '#059669' : '#dc2626'}; font-size: 10.5px; width: 48px;">
            ${sub.grade}
          </td>
          <td style="padding: 4.5px 6px; text-align: center; width: 70px;">
            ${sub.grade >= 75 
              ? '<span style="display: inline-block; padding: 1.5px 6px; font-size: 9px; font-weight: 700; background: #ecfdf5; color: #065f46; border: 1px solid #a7f3d0; border-radius: 9999px;">✓ Tuntas</span>' 
              : '<span style="display: inline-block; padding: 1.5px 6px; font-size: 9px; font-weight: 700; background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; border-radius: 9999px;">⚠ Remedial</span>'}
          </td>
        </tr>
      `).join('');

      return `
        <div style="background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; page-break-inside: avoid;">
          <div style="background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); padding: 6px 9px; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #e2e8f0;">
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; background: #059669; color: #ffffff; font-weight: 800; font-size: 10px; border-radius: 4px;">
                ${sIdx + 1}
              </span>
              <span style="font-weight: 800; font-size: 10.5px; color: #0f172a; text-transform: uppercase;">
                ${sem.semesterName}
              </span>
            </div>
            <div style="display: flex; gap: 4px; align-items: center;">
              ${semRank && semRank.rank <= 10 ? `
                <span style="background: #eef2ff; color: #4338ca; border: 1px solid #c7d2fe; padding: 1.5px 6px; border-radius: 9999px; font-size: 9px; font-weight: 800;">
                  #${semRank.rank}
                </span>
              ` : ''}
              <span style="background: #f1f5f9; color: #334155; border: 1px solid #cbd5e1; padding: 1.5px 6px; border-radius: 9999px; font-size: 9px; font-weight: 800;">
                Jml: ${semSum}
              </span>
              <span style="background: #ecfdf5; color: #065f46; border: 1px solid #a7f3d0; padding: 1.5px 7px; border-radius: 9999px; font-size: 9.5px; font-weight: 800;">
                Rata: ${semAvg}
              </span>
            </div>
          </div>
          <table style="width: 100%; border-collapse: collapse; text-align: left;">
            <thead>
              <tr style="background-color: #f8fafc; border-bottom: 1px solid #e2e8f0;">
                <th style="padding: 4px 6px; font-size: 9px; font-weight: 800; color: #64748b; text-transform: uppercase; width: 24px; text-align: center;">No</th>
                <th style="padding: 4px 6px; font-size: 9px; font-weight: 800; color: #64748b; text-transform: uppercase;">Mata Pelajaran</th>
                <th style="padding: 4px 6px; font-size: 9px; font-weight: 800; color: #64748b; text-transform: uppercase; text-align: center; width: 48px;">Nilai</th>
                <th style="padding: 4px 6px; font-size: 9px; font-weight: 800; color: #64748b; text-transform: uppercase; text-align: center; width: 70px;">Status</th>
              </tr>
            </thead>
            <tbody>
              ${subjectsRows || '<tr><td colspan="4" style="padding: 8px; text-align: center; color: #94a3b8; font-style: italic; font-size: 9.5px;">Belum ada catatan nilai.</td></tr>'}
            </tbody>
          </table>
        </div>
      `;
    }).join('') || '<p style="text-align: center; color: #64748b; padding: 20px;">Belum ada data perkembangan akademik.</p>';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="id">
        <head>
          <meta charset="utf-8">
          <title>Rekap Perkembangan Akademik - ${selectedStudent.name}</title>
          <style>
            @page { size: A4 portrait; margin: 8mm 11mm 6mm 11mm; }
            * { box-sizing: border-box; }
            html, body {
              margin: 0;
              padding: 0;
              background: #ffffff;
              color: #0f172a; 
              font-family: 'Plus Jakarta Sans', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              font-size: 10px;
              line-height: 1.35;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .kop-container {
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding-bottom: 6px;
              margin-bottom: 6px;
              border-bottom: 2.5px double #0f172a;
            }
            .kop-logo {
              width: 52px;
              height: 48px;
              display: flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
            }
            .kop-text {
              flex: 1;
              text-align: center;
              padding: 0 10px;
            }
            .kop-gov { font-size: 10px; font-weight: 700; color: #475569; text-transform: uppercase; margin: 0; line-height: 1.2; }
            .kop-dept { font-size: 11px; font-weight: 800; color: #1e293b; text-transform: uppercase; margin: 1px 0; line-height: 1.2; }
            .kop-school { font-size: 15.5px; font-weight: 900; color: #047857; text-transform: uppercase; letter-spacing: 0.8px; margin: 1px 0; line-height: 1.2; }
            .kop-address { font-size: 8.5px; color: #64748b; margin: 1px 0 0 0; line-height: 1.2; }
            
            .doc-banner {
              text-align: center;
              margin-bottom: 8px;
            }
            .doc-pill {
              display: inline-block;
              background: linear-gradient(135deg, #059669 0%, #0d9488 100%);
              color: #ffffff;
              font-size: 10.5px;
              font-weight: 800;
              text-transform: uppercase;
              letter-spacing: 0.8px;
              padding: 3px 14px;
              border-radius: 9999px;
            }
            .doc-subtitle {
              font-size: 8.5px;
              color: #64748b;
              font-weight: 600;
              margin-top: 2px;
            }

            .student-card {
              display: flex;
              justify-content: space-between;
              align-items: center;
              background: #f8fafc;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              padding: 6px 12px;
              margin-bottom: 8px;
            }
            .student-meta {
              display: flex;
              flex-direction: column;
              gap: 2px;
            }
            .student-name {
              font-size: 13px;
              font-weight: 900;
              color: #0f172a;
            }
            .badges-container {
              display: flex;
              align-items: center;
              gap: 6px;
            }
            .stat-badge {
              background: #ffffff;
              border: 1px solid #cbd5e1;
              border-radius: 6px;
              padding: 3px 8px;
              text-align: center;
              min-width: 80px;
            }
            .stat-badge.emerald { background: #ecfdf5; border-color: #a7f3d0; }
            .stat-badge.amber { background: #fffbeb; border-color: #fde68a; }
            .stat-badge-title { font-size: 8px; font-weight: 800; text-transform: uppercase; color: #64748b; }
            .stat-badge.emerald .stat-badge-title { color: #047857; }
            .stat-badge.amber .stat-badge-title { color: #b45309; }
            .stat-badge-val { font-size: 12px; font-weight: 900; color: #0f172a; margin-top: 1px; }
            .stat-badge.emerald .stat-badge-val { color: #065f46; }
            .stat-badge.amber .stat-badge-val { color: #92400e; }

            .semesters-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 8px;
              margin-bottom: 10px;
            }

            .sig-section {
              margin-top: 10px;
              display: flex;
              justify-content: flex-end;
              page-break-inside: avoid;
            }
            .sig-box {
              text-align: center;
              width: 200px;
              font-size: 9.5px;
            }
            .sig-space {
              height: 40px;
            }
            .sig-name {
              font-weight: 800;
              color: #0f172a;
              text-decoration: underline;
            }
            .sig-nip {
              font-size: 8.5px;
              color: #64748b;
            }

            .footer-meta {
              margin-top: 6px;
              padding-top: 4px;
              border-top: 1px dashed #cbd5e1;
              display: flex;
              justify-content: space-between;
              font-size: 8px;
              color: #94a3b8;
            }
          </style>
        </head>
        <body onload="window.print()">
          <!-- KOP SURAT RESMI & AKURAT -->
          <div class="kop-container">
            <div class="kop-logo">
              ${leftLogoHtml}
            </div>
            <div class="kop-text">
              <div class="kop-gov">${govTitle}</div>
              <div class="kop-dept">${deptTitle}</div>
              <div class="kop-school">${schoolTitle}</div>
              <div class="kop-address">${schoolAddress}${additionalKop ? ` • ${additionalKop}` : ''}</div>
            </div>
            <div class="kop-logo">
              ${rightLogoHtml}
            </div>
          </div>

          <!-- DOCUMENT BANNER -->
          <div class="doc-banner">
            <div class="doc-pill">Daftar Nilai & Perkembangan Akademik Siswa</div>
            <div class="doc-subtitle">Daftar Nilai Akademik Semua Mata Pelajaran & Rekam Jejak Capaian Nilai Per Semester • Tahun Ajaran ${academicYear}</div>
          </div>

          <!-- PROFIL SISWA -->
          <div class="student-card">
            <div class="student-meta">
              <div class="student-name">${selectedStudent.name}</div>
              <div style="color: #475569; font-size: 9px;">
                NIS: <strong>${selectedStudent.nis}</strong> &nbsp;|&nbsp; 
                NISN: <strong>${selectedStudent.nisn || '-'}</strong> &nbsp;|&nbsp; 
                Kelas: <strong>Kelas ${homeroomClass}</strong>
              </div>
              <div style="color: #64748b; font-size: 8px;">
                Status: <strong style="color: #059669;">${selectedStudent.status}</strong> &nbsp;•&nbsp; 
                Total Semester: <strong>${currentProgress.semesters.length} Semester Terdata</strong>
              </div>
            </div>
            <div class="badges-container">
              ${(studentOverallRank && studentOverallRank.rank <= 10) ? `
                <div class="stat-badge amber">
                  <div class="stat-badge-title">Peringkat Kelas</div>
                  <div class="stat-badge-val">#${studentOverallRank.rank}</div>
                </div>
              ` : ''}
              <div class="stat-badge indigo">
                <div class="stat-badge-title">Jumlah Nilai</div>
                <div class="stat-badge-val">${studentOverallRank ? studentOverallRank.totalSum : overallTotalSum}</div>
              </div>
              <div class="stat-badge emerald">
                <div class="stat-badge-title">Rata Kumulatif</div>
                <div class="stat-badge-val">${overallAverage > 0 ? overallAverage : '-'}</div>
              </div>
            </div>
          </div>

          <!-- TABEL SEMESTER (2-KOLOM COMPACT FIT 1 HALAMAN) -->
          <div class="semesters-grid">
            ${semestersHtml}
          </div>

          <!-- TANDA TANGAN HANYA WALI KELAS -->
          <div class="sig-section">
            <div class="sig-box">
              <p>${city}, ${currentDate}<br/><strong>Wali Kelas ${homeroomClass}</strong></p>
              <div class="sig-space"></div>
              <div class="sig-name">${homeroomTeacherName}</div>
              <div class="sig-nip">${homeroomTeacherNip}</div>
            </div>
          </div>

          <!-- FOOTER META -->
          <div class="footer-meta">
            <span>Dokumen Resmi Administrasi Perkembangan Belajar Siswa • Kelas ${homeroomClass}</span>
            <span>Dicetak Otomatis pada ${currentDate} • ${schoolTitle}</span>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Generate Modern & Stylish Print Analysis & Narrative Report (1 Page Per Student)
  const handlePrintAnalysisReport = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow || !selectedStudent || !currentProgress) return;

    const schoolTitle = schoolSettings?.schoolName || 'SMP NEGERI 1 KAWALI';
    const govTitle = schoolSettings?.governmentName || 'PEMERINTAH DAERAH PROVINSI JAWA BARAT';
    const deptTitle = schoolSettings?.departmentName || 'DINAS PENDIDIKAN';
    const schoolAddress = schoolSettings?.address || 'Jl. Raya Kawali No. 1, Ciamis - Jawa Barat';
    const additionalKop = schoolSettings?.additionalKopLine || '';
    const academicYear = schoolSettings?.academicYear || '2025/2026';
    const homeroomTeacherName = activeUser?.name || 'Wali Kelas';
    const homeroomTeacherNip = activeUser?.nip ? `NIP. ${activeUser.nip}` : '-';
    const city = schoolSettings?.kabupaten || schoolSettings?.kecamatan || 'Kawali';

    const currentDate = new Date().toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    // Recompute analysis data
    const subjectMap: Record<string, { sem: string, grade: number }[]> = {};
    const allGradesList: { semesterName: string; subjectName: string; grade: number }[] = [];

    currentProgress.semesters.forEach(sem => {
      sem.subjects.forEach(sub => {
        allGradesList.push({ semesterName: sem.semesterName, subjectName: sub.subjectName, grade: sub.grade });
        if (!subjectMap[sub.subjectName]) subjectMap[sub.subjectName] = [];
        subjectMap[sub.subjectName].push({ sem: sem.semesterName, grade: sub.grade });
      });
    });

    let maxIncrease = { subject: '-', val: -999 };
    let maxDecrease = { subject: '-', val: 999 };
    let highestAvg = { subject: '-', val: -1 };
    let lowestAvg = { subject: '-', val: 101 };

    Object.entries(subjectMap).forEach(([sub, grades]) => {
      if (grades.length >= 2) {
        const first = grades[0].grade;
        const last = grades[grades.length - 1].grade;
        const diff = last - first;
        if (diff > maxIncrease.val) maxIncrease = { subject: sub, val: diff };
        if (diff < maxDecrease.val) maxDecrease = { subject: sub, val: diff };
      }
      const avg = grades.reduce((sum, g) => sum + g.grade, 0) / grades.length;
      if (avg > highestAvg.val) highestAvg = { subject: sub, val: avg };
      if (avg < lowestAvg.val) lowestAvg = { subject: sub, val: avg };
    });

    const KKTP_STANDARD = 75;
    const belowStandardGrades = allGradesList.filter(g => g.grade < KKTP_STANDARD);
    const topIndividualGrades = [...allGradesList].sort((a, b) => b.grade - a.grade).slice(0, 6);
    const subjectAverages = Object.entries(subjectMap).map(([sub, grades]) => ({
      subject: sub,
      avg: parseFloat((grades.reduce((sum, g) => sum + g.grade, 0) / grades.length).toFixed(1))
    })).sort((a, b) => b.avg - a.avg);

    const topSubjectAverages = subjectAverages.slice(0, 3);
    const studentOverallRank = classRankings.overallRankings[selectedStudent.id];
    const studentSemesterRanks = classRankings.semesterRankings[selectedStudent.id] || {};
    const hasEverEnteredTop10 = (studentOverallRank && studentOverallRank.rank <= 10) || currentProgress.semesters.some(sem => studentSemesterRanks[sem.semesterName] && studentSemesterRanks[sem.semesterName].rank <= 10);
    const studentFirstName = selectedStudent.name.split(' ')[0];

    const studentCareerProfile = getDetailedStudentCareerProfile(
      topSubjectAverages.map(s => s.subject),
      topIndividualGrades,
      selectedStudent.name,
      selectedStudent.gender
    );

    let rankStory = '';
    if (studentOverallRank) {
      if (studentOverallRank.rank <= 10) {
        rankStory = `Saat ini, peringkat ananda di kelas ${homeroomClass} berada di urutan ke-${studentOverallRank.rank} dari ${studentOverallRank.total} siswa dengan jumlah nilai seluruh mapel ${studentOverallRank.totalSum} (rata-rata gabungan ${overallAverage}).`;
      } else {
        rankStory = `Saat ini, ananda terus menunjukkan konsistensi dan semangat belajar yang sangat baik di kelas ${homeroomClass} dengan jumlah nilai seluruh mapel ${studentOverallRank.totalSum} (rata-rata gabungan ${overallAverage}).`;
      }
    }

    const introStory = `“Halo ${studentFirstName}, mari kita cermati rekam jejak perjalanan belajar ananda dari semester ke semester. ${rankStory ? rankStory : `Secara keseluruhan, capaian rata-rata gabungan ananda mencapai ${overallAverage > 0 ? overallAverage : '-'}.`}`;
    
    let topStory = '';
    let futurePotentialDesc = studentCareerProfile.summarySentence;
    if (topSubjectAverages.length > 0) {
      const topStr = topSubjectAverages.map(s => `${s.subject} (${s.avg})`).join(', ');
      const cleanNarrativeCareers = studentCareerProfile.careerPaths.map(p => p.replace(/^Opsi \d+\s*=\s*/, '')).slice(0, 3).join(', ');
      topStory = `Bapak sangat bangga dan mengapresiasi pencapaian ananda pada mata pelajaran ${topStr}, yang menjadi kekuatan utama prestasi belajarmu. Analisis prestasi mapel unggulan tersebut menunjukkan potensi dan bakat istimewa ananda pada bidang ${studentCareerProfile.specializationTrack}, dengan prospek karier menjanjikan di masa depan sebagai ${cleanNarrativeCareers}. Teruslah asah dan kembangkan minat belajar ini karena menjadi modal penting menggapai cita-citamu!`;
    }

    let progressStory = '';
    if (maxIncrease.subject !== '-' && maxIncrease.val > 0) {
      progressStory = `Selain itu, kerja keras ananda terlihat nyata di mata pelajaran ${maxIncrease.subject} dengan peningkatan signifikan sebesar +${maxIncrease.val} poin sejak awal semester.`;
    }

    let evaluationStory = '';
    if (belowStandardGrades.length > 0) {
      const belowSubs = Array.from(new Set(belowStandardGrades.map(g => g.subjectName))).join(', ');
      evaluationStory = `Namun, ada beberapa catatan evaluasi belajar bersama: terdapat ${belowStandardGrades.length} nilai yang masih di bawah standar KKTP 75, khususnya pada mata pelajaran ${belowSubs}. Jangan berkecil hati ya, evaluasi ini menjadi peta navigasi bagi kita untuk mulai menyusun strategi belajar mandiri dan bimbingan terarah.`;
    } else {
      evaluationStory = `Istimewanya lagi, seluruh nilai mata pelajaran ananda telah berhasil memenuhi dan melampaui standar kriteria ketercapaian KKTP (≥ 75) di semua semester tanpa kendala!`;
    }
    
    const closingStory = getCustomizedClosingStory(selectedStudent.id, selectedStudent.name);

    // Build Complete Matrix of Academic Grades (Daftar Nilai Akademik Seluruh Semester)
    const uniqueSubjectList: string[] = [];
    currentProgress.semesters.forEach(sem => {
      sem.subjects.forEach(sub => {
        if (!uniqueSubjectList.includes(sub.subjectName)) {
          uniqueSubjectList.push(sub.subjectName);
        }
      });
    });

    const daftarNilaiMatrixRows = uniqueSubjectList.map((subjectName, idx) => {
      const semGrades = currentProgress.semesters.map(sem => {
        const sub = sem.subjects.find(s => s.subjectName.toLowerCase() === subjectName.toLowerCase());
        return sub ? sub.grade : null;
      });

      const validGrades = semGrades.filter((g): g is number => g !== null);
      const subjectTotal = validGrades.reduce((a, b) => a + b, 0);
      const subjectAvg = validGrades.length > 0 ? (subjectTotal / validGrades.length).toFixed(1) : '-';
      const isTuntas = validGrades.length > 0 && validGrades.every(g => g >= KKTP_STANDARD);

      const gradeTds = semGrades.map(g => {
        if (g === null) {
          return `<td style="padding: 4.5px 6px; text-align: center; color: #cbd5e1; font-size: 10.5px;">-</td>`;
        }
        const color = g >= KKTP_STANDARD ? '#059669' : '#dc2626';
        const bg = g < KKTP_STANDARD ? '#fef2f2' : 'transparent';
        return `<td style="padding: 4.5px 6px; text-align: center; font-weight: 800; color: ${color}; background-color: ${bg}; font-size: 10.5px;">${g}</td>`;
      }).join('');

      return `
        <tr style="border-bottom: 1px solid #f1f5f9; ${idx % 2 === 1 ? 'background-color: #fafbfc;' : ''}">
          <td style="padding: 4.5px 6px; text-align: center; color: #64748b; font-size: 9.5px; width: 22px;">${idx + 1}</td>
          <td style="padding: 4.5px 6px; font-weight: 700; color: #1e293b; font-size: 10.5px;">${subjectName}</td>
          ${gradeTds}
          <td style="padding: 4.5px 6px; text-align: center; font-weight: 800; color: #047857; font-size: 10.5px; background-color: #f0fdf4;">${subjectAvg}</td>
          <td style="padding: 4.5px 6px; text-align: center; font-size: 9.5px;">
            ${validGrades.length === 0 
              ? '-' 
              : (isTuntas 
                  ? '<span style="color: #059669; font-weight: 700;">✓ Tuntas</span>' 
                  : '<span style="color: #dc2626; font-weight: 700;">⚠ Remedial</span>')}
          </td>
        </tr>
      `;
    }).join('');

    const matrixSemTotals = currentProgress.semesters.map(sem => sem.subjects.reduce((a, b) => a + b.grade, 0));
    const matrixSemAvgs = currentProgress.semesters.map(sem => sem.subjects.length > 0 ? (sem.subjects.reduce((a, b) => a + b.grade, 0) / sem.subjects.length).toFixed(1) : '-');
    const matrixSemRanks = currentProgress.semesters.map(sem => {
      const sRank = studentSemesterRanks[sem.semesterName];
      return (sRank && sRank.rank <= 10) ? `#${sRank.rank}` : '-';
    });

    // Top grades html cards (compact)
    const topGradesHtml = topIndividualGrades.map((g) => `
      <div style="display: flex; justify-content: space-between; align-items: center; padding: 4px 8px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 6px;">
        <div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 140px;">
          <span style="font-weight: 700; color: #1e293b; font-size: 10px;">${g.subjectName}</span>
          <span style="font-size: 8.5px; color: #64748b; margin-left: 3px;">(${g.semesterName})</span>
        </div>
        <div style="background: #ecfdf5; color: #065f46; border: 1px solid #a7f3d0; font-weight: 900; font-size: 10.5px; padding: 2px 6px; border-radius: 4px;">
          ${g.grade}
        </div>
      </div>
    `).join('');

    // Accurate Logos for kop
    const leftLogoHtml = schoolSettings?.departmentLogoUrl
      ? `<img src="${schoolSettings.departmentLogoUrl}" style="height: 50px; max-width: 58px; object-fit: contain;" alt="Logo Dinas" />`
      : (schoolSettings?.logoUrl 
          ? `<img src="${schoolSettings.logoUrl}" style="height: 50px; max-width: 58px; object-fit: contain;" alt="Logo Sekolah" />` 
          : `<div style="width: 50px; height: 50px; background: linear-gradient(135deg, #4f46e5 0%, #3730a3 100%); border-radius: 8px; display: flex; align-items: center; justify-content: center; color: white; font-weight: 900; font-size: 22px;">${schoolTitle.charAt(0)}</div>`);

    const rightLogoHtml = schoolSettings?.logoUrl
      ? `<img src="${schoolSettings.logoUrl}" style="height: 50px; max-width: 58px; object-fit: contain;" alt="Logo Sekolah" />`
      : `<div style="width: 50px; height: 50px;"></div>`;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="id">
        <head>
          <meta charset="utf-8">
          <title>Laporan Analisis & Narasi Bimbingan - ${selectedStudent.name}</title>
          <style>
            @page { size: A4 portrait; margin: 8mm 11mm 6mm 11mm; }
            * { box-sizing: border-box; }
            html, body {
              margin: 0;
              padding: 0;
              background: #ffffff;
              color: #0f172a; 
              font-family: 'Plus Jakarta Sans', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              font-size: 10px;
              line-height: 1.35;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            .kop-container {
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding-bottom: 6px;
              margin-bottom: 6px;
              border-bottom: 2.5px double #0f172a;
            }
            .kop-logo {
              width: 52px;
              height: 48px;
              display: flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
            }
            .kop-text {
              flex: 1;
              text-align: center;
              padding: 0 10px;
            }
            .kop-gov { font-size: 10px; font-weight: 700; color: #475569; text-transform: uppercase; margin: 0; line-height: 1.2; }
            .kop-dept { font-size: 11px; font-weight: 800; color: #1e293b; text-transform: uppercase; margin: 1px 0; line-height: 1.2; }
            .kop-school { font-size: 15.5px; font-weight: 900; color: #4338ca; text-transform: uppercase; letter-spacing: 0.8px; margin: 1px 0; line-height: 1.2; }
            .kop-address { font-size: 8.5px; color: #64748b; margin: 1px 0 0 0; line-height: 1.2; }

            .doc-banner {
              text-align: center;
              margin-bottom: 6px;
            }
            .doc-pill {
              display: inline-block;
              background: linear-gradient(135deg, #4f46e5 0%, #06b6d4 100%);
              color: #ffffff;
              font-size: 10.5px;
              font-weight: 800;
              text-transform: uppercase;
              letter-spacing: 0.8px;
              padding: 3px 14px;
              border-radius: 9999px;
            }
            .doc-subtitle {
              font-size: 8.5px;
              color: #64748b;
              font-weight: 600;
              margin-top: 2px;
            }

            .student-card {
              display: flex;
              justify-content: space-between;
              align-items: center;
              background: #f8fafc;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              padding: 6px 12px;
              margin-bottom: 6px;
            }
            .student-meta {
              display: flex;
              flex-direction: column;
              gap: 1.5px;
            }
            .student-name {
              font-size: 13px;
              font-weight: 900;
              color: #0f172a;
            }
            .badges-container {
              display: flex;
              align-items: center;
              gap: 6px;
            }
            .stat-badge {
              background: #ffffff;
              border: 1px solid #cbd5e1;
              border-radius: 6px;
              padding: 3px 8px;
              text-align: center;
              min-width: 70px;
            }
            .stat-badge.amber { background: #fffbeb; border-color: #fde68a; }
            .stat-badge.indigo { background: #eef2ff; border-color: #c7d2fe; }
            .stat-badge.emerald { background: #ecfdf5; border-color: #a7f3d0; }
            .stat-badge-title { font-size: 7.5px; font-weight: 800; text-transform: uppercase; color: #64748b; }
            .stat-badge.amber .stat-badge-title { color: #b45309; }
            .stat-badge.indigo .stat-badge-title { color: #4338ca; }
            .stat-badge.emerald .stat-badge-title { color: #047857; }
            .stat-badge-val { font-size: 12px; font-weight: 900; color: #0f172a; margin-top: 0.5px; }
            .stat-badge.amber .stat-badge-val { color: #92400e; }
            .stat-badge.indigo .stat-badge-val { color: #3730a3; }
            .stat-badge.emerald .stat-badge-val { color: #065f46; }

            .kpi-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 6px;
              margin-bottom: 6px;
            }
            .kpi-card {
              border-radius: 6px;
              padding: 4px 7px;
              border: 1px solid transparent;
            }
            .kpi-card.emerald { background: #f0fdf4; border-color: #bbf7d0; }
            .kpi-card.rose { background: #fff1f2; border-color: #fecdd3; }
            .kpi-card.indigo { background: #eef2ff; border-color: #c7d2fe; }
            .kpi-card.amber { background: #fffbeb; border-color: #fde68a; }
            .kpi-label { font-size: 7.5px; font-weight: 800; text-transform: uppercase; margin-bottom: 1px; }
            .kpi-card.emerald .kpi-label { color: #166534; }
            .kpi-card.rose .kpi-label { color: #9f1239; }
            .kpi-card.indigo .kpi-label { color: #3730a3; }
            .kpi-card.amber .kpi-label { color: #92400e; }
            .kpi-val { font-size: 10px; font-weight: 800; color: #0f172a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            .kpi-sub { font-size: 8.5px; font-weight: 700; margin-top: 1px; }
            .kpi-card.emerald .kpi-sub { color: #15803d; }
            .kpi-card.rose .kpi-sub { color: #be123c; }
            .kpi-card.indigo .kpi-sub { color: #4338ca; }
            .kpi-card.amber .kpi-sub { color: #b45309; }

            .section-box {
              background: #ffffff;
              border: 1px solid #e2e8f0;
              border-radius: 6px;
              overflow: hidden;
              page-break-inside: avoid;
            }
            .section-header {
              background: #f8fafc;
              padding: 4px 7px;
              border-bottom: 1px solid #e2e8f0;
              font-size: 8.5px;
              font-weight: 800;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              color: #334155;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }

            .narrative-box {
              background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
              border-left: 3.5px solid #4f46e5;
              border-top: 1px solid #e2e8f0;
              border-right: 1px solid #e2e8f0;
              border-bottom: 1px solid #e2e8f0;
              border-radius: 0 6px 6px 0;
              padding: 6px 10px;
              margin-bottom: 6px;
              page-break-inside: avoid;
            }
            .narrative-title {
              font-size: 9.5px;
              font-weight: 800;
              color: #4338ca;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin-bottom: 3px;
            }
            .narrative-text {
              font-size: 9px;
              color: #334155;
              line-height: 1.38;
              text-align: justify;
            }
            .narrative-text p {
              margin: 0 0 2.5px 0;
            }
            .narrative-text p:last-child {
              margin-bottom: 0;
              font-weight: 700;
              color: #312e81;
            }

            .potential-box {
              background: #f0fdfa;
              border: 1px solid #99f6e4;
              border-radius: 5px;
              padding: 4px 6px;
              margin-top: 3px;
              font-size: 8px;
              color: #115e59;
              line-height: 1.3;
            }

            .sig-section {
              margin-top: 8px;
              display: flex;
              justify-content: flex-end;
              page-break-inside: avoid;
            }
            .sig-box {
              text-align: center;
              width: 190px;
              font-size: 9.5px;
            }
            .sig-space {
              height: 38px;
            }
            .sig-name {
              font-weight: 800;
              color: #0f172a;
              text-decoration: underline;
            }
            .sig-nip {
              font-size: 8.5px;
              color: #64748b;
            }

            .footer-meta {
              margin-top: 6px;
              padding-top: 3px;
              border-top: 1px dashed #cbd5e1;
              display: flex;
              justify-content: space-between;
              font-size: 8px;
              color: #94a3b8;
            }
          </style>
        </head>
        <body onload="window.print()">
          <!-- KOP SURAT RESMI & AKURAT -->
          <div class="kop-container">
            <div class="kop-logo">
              ${leftLogoHtml}
            </div>
            <div class="kop-text">
              <div class="kop-gov">${govTitle}</div>
              <div class="kop-dept">${deptTitle}</div>
              <div class="kop-school">${schoolTitle}</div>
              <div class="kop-address">${schoolAddress}${additionalKop ? ` • ${additionalKop}` : ''}</div>
            </div>
            <div class="kop-logo">
              ${rightLogoHtml}
            </div>
          </div>

          <!-- DOCUMENT BANNER -->
          <div class="doc-banner">
            <div class="doc-pill">Laporan Analisis Akademik & Narasi Bimbingan</div>
            <div class="doc-subtitle">Analisis Komprehensif Potensi Minat & Perkembangan Prestasi Belajar Siswa</div>
          </div>

          <!-- PROFIL SISWA -->
          <div class="student-card">
            <div class="student-meta">
              <div class="student-name">${selectedStudent.name}</div>
              <div style="color: #475569; font-size: 9px;">
                NIS: <strong>${selectedStudent.nis}</strong> &nbsp;|&nbsp; 
                NISN: <strong>${selectedStudent.nisn || '-'}</strong> &nbsp;|&nbsp; 
                Kelas: <strong>Kelas ${homeroomClass}</strong>
              </div>
              <div style="color: #64748b; font-size: 8px;">
                Tahun Ajaran: <strong>${academicYear}</strong> &nbsp;•&nbsp; 
                Wali Kelas: <strong>${homeroomTeacherName}</strong>
              </div>
            </div>
            <div class="badges-container">
              ${(studentOverallRank && studentOverallRank.rank <= 10) ? `
                <div class="stat-badge amber">
                  <div class="stat-badge-title">Peringkat Kelas</div>
                  <div class="stat-badge-val">#${studentOverallRank.rank}</div>
                </div>
              ` : ''}
              <div class="stat-badge indigo">
                <div class="stat-badge-title">Jumlah Nilai</div>
                <div class="stat-badge-val">${studentOverallRank ? studentOverallRank.totalSum : overallTotalSum}</div>
              </div>
              <div class="stat-badge emerald">
                <div class="stat-badge-title">Rata Gabungan</div>
                <div class="stat-badge-val">${overallAverage > 0 ? overallAverage : '-'}</div>
              </div>
            </div>
          </div>

          <!-- 4 KPI HIGHLIGHT CARDS -->
          <div class="kpi-grid">
            <div class="kpi-card emerald">
              <div class="kpi-label">🚀 Peningkatan Tertinggi</div>
              <div class="kpi-val">${maxIncrease.subject}</div>
              <div class="kpi-sub">+${maxIncrease.val === -999 ? 0 : maxIncrease.val} Poin</div>
            </div>
            <div class="kpi-card rose">
              <div class="kpi-label">📉 Evaluasi Penurunan</div>
              <div class="kpi-val">${maxDecrease.subject}</div>
              <div class="kpi-sub">${maxDecrease.val === 999 ? 'Stabil' : `${maxDecrease.val} Poin`}</div>
            </div>
            <div class="kpi-card indigo">
              <div class="kpi-label">🌟 Rata Tertinggi</div>
              <div class="kpi-val">${highestAvg.subject}</div>
              <div class="kpi-sub">Skor: ${highestAvg.val === -1 ? '-' : highestAvg.val.toFixed(1)}</div>
            </div>
            <div class="kpi-card amber">
              <div class="kpi-label">🎯 Perlu Perhatian</div>
              <div class="kpi-val">${lowestAvg.subject}</div>
              <div class="kpi-sub">Skor: ${lowestAvg.val === 101 ? '-' : lowestAvg.val.toFixed(1)}</div>
            </div>
          </div>

          <!-- 1. DAFTAR NILAI AKADEMIK SISWA (SEMUA NILAI SISWA PER SEMESTER) -->
          <div class="section-box" style="margin-bottom: 6px;">
            <div class="section-header">
              <span>1. Daftar Nilai Akademik Siswa (Seluruh Semester)</span>
              <span style="font-size: 8px; color: #64748b; font-weight: 700;">Standar KKTP: 75</span>
            </div>
            <table style="width: 100%; border-collapse: collapse; text-align: left;">
              <thead>
                <tr style="background: #f8fafc; border-bottom: 1.5px solid #cbd5e1;">
                  <th style="padding: 3px 4px; font-size: 8px; font-weight: 800; color: #475569; width: 18px; text-align: center;">No</th>
                  <th style="padding: 3px 5px; font-size: 8px; font-weight: 800; color: #475569;">Mata Pelajaran</th>
                  ${currentProgress.semesters.map(sem => `
                    <th style="padding: 3px 4px; font-size: 8px; font-weight: 800; color: #475569; text-align: center;">${sem.semesterName}</th>
                  `).join('')}
                  <th style="padding: 3px 4px; font-size: 8px; font-weight: 800; color: #047857; text-align: center; width: 50px;">Rata-Rata</th>
                  <th style="padding: 3px 4px; font-size: 8px; font-weight: 800; color: #475569; text-align: center; width: 55px;">KKTP</th>
                </tr>
              </thead>
              <tbody>
                ${daftarNilaiMatrixRows || '<tr><td colspan="10" style="padding: 6px; text-align: center; color: #94a3b8; font-size: 8.5px;">Belum ada data nilai</td></tr>'}
              </tbody>
              <tfoot>
                <tr style="background: #f8fafc; border-top: 1.5px solid #cbd5e1; font-weight: 800;">
                  <td colspan="2" style="padding: 2.5px 5px; font-size: 8px; color: #1e293b; text-align: right;">Jumlah Nilai:</td>
                  ${matrixSemTotals.map(t => `<td style="padding: 2.5px 4px; text-align: center; font-weight: 900; color: #4338ca; font-size: 8.5px;">${t}</td>`).join('')}
                  <td style="padding: 2.5px 4px; text-align: center; font-weight: 900; color: #4338ca; font-size: 8.5px; background: #eef2ff;">${studentOverallRank ? studentOverallRank.totalSum : overallTotalSum}</td>
                  <td style="padding: 2.5px 4px; text-align: center; font-size: 7.5px; color: #64748b;">-</td>
                </tr>
                <tr style="background: #f8fafc; border-top: 1px solid #e2e8f0; font-weight: 800;">
                  <td colspan="2" style="padding: 2.5px 5px; font-size: 8px; color: #1e293b; text-align: right;">Rata-Rata:</td>
                  ${matrixSemAvgs.map(a => `<td style="padding: 2.5px 4px; text-align: center; font-weight: 900; color: #059669; font-size: 8.5px;">${a}</td>`).join('')}
                  <td style="padding: 2.5px 4px; text-align: center; font-weight: 900; color: #059669; font-size: 8.5px; background: #ecfdf4;">${overallAverage}</td>
                  <td style="padding: 2.5px 4px; text-align: center; font-size: 7.5px; color: #64748b;">-</td>
                </tr>
                ${hasEverEnteredTop10 ? `
                  <tr style="background: #fffbeb; border-top: 1px solid #fde68a; font-weight: 800;">
                    <td colspan="2" style="padding: 2.5px 5px; font-size: 8px; color: #92400e; text-align: right;">Peringkat Kelas:</td>
                    ${matrixSemRanks.map(r => `<td style="padding: 2.5px 4px; text-align: center; font-weight: 900; color: #b45309; font-size: 8.5px;">${r}</td>`).join('')}
                    <td style="padding: 2.5px 4px; text-align: center; font-weight: 900; color: #b45309; font-size: 8.5px; background: #fef3c7;">${(studentOverallRank && studentOverallRank.rank <= 10) ? `#${studentOverallRank.rank}` : '-'}</td>
                    <td style="padding: 2.5px 4px; text-align: center; font-size: 7px; color: #92400e;">(10 Besar)</td>
                  </tr>
                ` : ''}
              </tfoot>
            </table>
          </div>

          <!-- MAPEL UNGGULAN & EVALUASI KKTP (STACKED) -->
          <div style="display: flex; flex-direction: column; gap: 8px; margin-bottom: 8px;">
            <!-- Top Performers & Career Potential -->
            <div class="section-box" style="display: flex; flex-direction: column; justify-content: space-between;">
              <div>
                <div class="section-header">
                  <span>2. Prestasi Mapel Unggulan</span>
                  <span style="color: #059669; font-weight: 800; font-size: 8px;">Top Performers</span>
                </div>
                <div style="padding: 4px; display: grid; grid-template-columns: 1fr 1fr; gap: 4px;">
                  ${topGradesHtml || '<div style="color: #94a3b8; font-size: 8.5px;">Belum ada nilai</div>'}
                </div>
              </div>
              
              ${studentCareerProfile ? `
                <div style="padding: 0 4px 4px 4px;">
                  <div class="potential-box">
                    <strong>💡 Rekomendasi Potensi Karier & Peminatan:</strong><br/>
                    • <strong>Peminatan:</strong> ${studentCareerProfile.specializationTrack}<br/>
                    • <strong>Prospek Profesi:</strong><br/>
                    ${studentCareerProfile.careerPaths.map(p => `&nbsp;&nbsp;&nbsp;&nbsp;• ${p}`).join('<br/>')}
                  </div>
                </div>
              ` : ''}
            </div>

            <!-- EVALUASI KKTP -->
            ${belowStandardGrades.length > 0 ? `
              <div class="section-box" style="border-color: #fde68a;">
                <div class="section-header" style="background: #fffbeb; color: #92400e;">
                  <span>3. Evaluasi KKTP (&lt; 75) &amp; Solusi Belajar</span>
                  <span style="font-weight: 800; font-size: 8px; color: #b45309;">${belowStandardGrades.length} Remedial</span>
                </div>
                <div style="padding: 4px 6px; font-size: 8px; color: #78350f; line-height: 1.3;">
                  ${Object.entries(subjectMap).filter(([_, grades]) => grades.some(g => g.grade < 75)).map(([subName, grades]) => {
                    const evalData = generateKKTPSubjectEvaluation(subName, grades.map(g => ({ semesterName: g.sem, grade: g.grade })), selectedStudent.name);
                    const semHistory = grades.map(g => `<span style="display:inline-block; margin-right:4px;">${g.sem}: <strong style="color: ${g.grade < 75 ? '#dc2626' : '#059669'};">${g.grade}</strong></span>`).join('');
                    return `
                      <div style="margin-bottom: 3px; padding-bottom: 3px; border-bottom: 1px dashed #fde68a;">
                        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1px;">
                          <strong style="color: #92400e; font-size: 8.5px;">${subName}</strong>
                          <span style="font-size: 7.5px; background: #fef3c7; color: #92400e; font-weight: 800; padding: 1px 4px; border-radius: 3px;">${evalData.trendLabel}</span>
                        </div>
                        <div style="font-size: 7.5px; color: #64748b; margin-bottom: 1px;">Rekam Semester: ${semHistory} (Rata: ${evalData.average})</div>
                        <div style="font-size: 7.5px; color: #475569; line-height: 1.25;">
                          • <strong>Motivasi:</strong> ${evalData.motivation}<br/>
                          • <strong>Saran Strategi:</strong> ${evalData.strategy}<br/>
                          • <strong>Solusi Tindak Lanjut:</strong> ${evalData.solutions[0]}
                        </div>
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>
            ` : `
              <div class="section-box" style="border-color: #bbf7d0;">
                <div class="section-header" style="background: #f0fdf4; color: #166534;">
                  <span>3. Evaluasi KKTP (≥ 75)</span>
                  <span style="font-weight: 800; font-size: 8px; color: #15803d;">✓ 100% Tuntas</span>
                </div>
                <div style="padding: 6px 8px; font-size: 8.5px; color: #14532d; line-height: 1.35;">
                  Sangat membanggakan! Seluruh capaian nilai mata pelajaran ananda pada setiap semester telah tuntas melampaui standar kriteria KKTP (≥ 75).
                </div>
              </div>
            `}
          </div>

          <!-- BIMBINGAN & MOTIVASI WALI KELAS -->
          <div class="narrative-box">
            <div class="narrative-title">
              <span>💬 Bimbingan & Motivasi Wali Kelas</span>
            </div>
            <div class="narrative-text">
              <p>${introStory}</p>
              ${topStory ? `<p>${topStory}</p>` : ''}
              ${progressStory ? `<p>${progressStory}</p>` : ''}
              <p>${evaluationStory}</p>
              <p>${closingStory}</p>
            </div>
          </div>

          <!-- LEMBAR PENGESAHAN (HANYA WALI KELAS) -->
          <div class="sig-section">
            <div class="sig-box">
              <p>${city}, ${currentDate}<br/><strong>Wali Kelas ${homeroomClass}</strong></p>
              <div class="sig-space"></div>
              <div class="sig-name">${homeroomTeacherName}</div>
              <div class="sig-nip">${homeroomTeacherNip}</div>
            </div>
          </div>

          <!-- FOOTER META -->
          <div class="footer-meta">
            <span>Dokumen Analisis Hasil Belajar & Bimbingan Peserta Didik • Kelas ${homeroomClass}</span>
            <span>Dicetak Otomatis pada ${currentDate} • ${schoolTitle}</span>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Generate Modern & Stylish Mass Print Report (Laporan Analisis & Narasi Bimbingan Seluruh Siswa)
  const handlePrintAllStudentsAnalysisReport = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow || classStudents.length === 0) return;

    const schoolTitle = schoolSettings?.schoolName || 'SMP NEGERI 1 KAWALI';
    const govTitle = schoolSettings?.governmentName || 'PEMERINTAH DAERAH PROVINSI JAWA BARAT';
    const deptTitle = schoolSettings?.departmentName || 'DINAS PENDIDIKAN';
    const schoolAddress = schoolSettings?.address || 'Jl. Raya Kawali No. 1, Ciamis - Jawa Barat';
    const additionalKop = schoolSettings?.additionalKopLine || '';
    const academicYear = schoolSettings?.academicYear || '2025/2026';
    const homeroomTeacherName = activeUser?.name || 'Wali Kelas';
    const homeroomTeacherNip = activeUser?.nip ? `NIP. ${activeUser.nip}` : '-';
    const city = schoolSettings?.kabupaten || schoolSettings?.kecamatan || 'Kawali';

    const currentDate = new Date().toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    let htmlContent = `
      <!DOCTYPE html>
      <html lang="id">
        <head>
          <meta charset="utf-8">
          <title>Laporan Analisis & Narasi Bimbingan Masal - Kelas ${homeroomClass}</title>
          <style>
            @page { size: A4 portrait; margin: 10mm 12mm 10mm 12mm; }
            * { box-sizing: border-box; }
            html, body {
              margin: 0;
              padding: 0;
              background: #ffffff;
              color: #0f172a; 
              font-family: 'Plus Jakarta Sans', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              font-size: 11.5px;
              line-height: 1.45;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            .print-page {
              page-break-after: always;
              min-height: 270mm;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
            }
            .print-page:last-child {
              page-break-after: avoid;
            }
            
            .kop-container {
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding-bottom: 8px;
              margin-bottom: 10px;
              border-bottom: 3px double #0f172a;
            }
            .kop-logo {
              width: 58px;
              height: 54px;
              display: flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
            }
            .kop-text {
              flex: 1;
              text-align: center;
              padding: 0 12px;
            }
            .kop-gov { font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; margin: 0; line-height: 1.2; }
            .kop-dept { font-size: 12px; font-weight: 800; color: #1e293b; text-transform: uppercase; margin: 1px 0; line-height: 1.2; }
            .kop-school { font-size: 17.5px; font-weight: 900; color: #4338ca; text-transform: uppercase; letter-spacing: 0.8px; margin: 2px 0; line-height: 1.2; }
            .kop-address { font-size: 9.5px; color: #64748b; margin: 2px 0 0 0; line-height: 1.2; }

            .doc-banner {
              text-align: center;
              margin-bottom: 12px;
            }
            .doc-pill {
              display: inline-block;
              background: linear-gradient(135deg, #4f46e5 0%, #06b6d4 100%);
              color: #ffffff;
              font-size: 12.5px;
              font-weight: 800;
              text-transform: uppercase;
              letter-spacing: 1px;
              padding: 5px 18px;
              border-radius: 9999px;
            }
            .doc-subtitle {
              font-size: 10px;
              color: #64748b;
              font-weight: 600;
              margin-top: 4px;
            }

            .student-card {
              display: flex;
              justify-content: space-between;
              align-items: center;
              background: #f8fafc;
              border: 1px solid #e2e8f0;
              border-radius: 10px;
              padding: 10px 16px;
              margin-bottom: 12px;
            }
            .student-meta {
              display: flex;
              flex-direction: column;
              gap: 3px;
            }
            .student-name {
              font-size: 15px;
              font-weight: 900;
              color: #0f172a;
            }
            .badges-container {
              display: flex;
              align-items: center;
              gap: 8px;
            }
            .stat-badge {
              background: #ffffff;
              border: 1px solid #cbd5e1;
              border-radius: 8px;
              padding: 5px 10px;
              text-align: center;
              min-width: 80px;
            }
            .stat-badge.amber { background: #fffbeb; border-color: #fde68a; }
            .stat-badge.indigo { background: #eef2ff; border-color: #c7d2fe; }
            .stat-badge.emerald { background: #ecfdf5; border-color: #a7f3d0; }
            .stat-badge-title { font-size: 8.5px; font-weight: 800; text-transform: uppercase; color: #64748b; }
            .stat-badge.amber .stat-badge-title { color: #b45309; }
            .stat-badge.indigo .stat-badge-title { color: #4338ca; }
            .stat-badge.emerald .stat-badge-title { color: #047857; }
            .stat-badge-val { font-size: 13.5px; font-weight: 900; color: #0f172a; margin-top: 1px; }
            .stat-badge.amber .stat-badge-val { color: #92400e; }
            .stat-badge.indigo .stat-badge-val { color: #3730a3; }
            .stat-badge.emerald .stat-badge-val { color: #065f46; }

            .kpi-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 8px;
              margin-bottom: 12px;
            }
            .kpi-card {
              border-radius: 8px;
              padding: 6px 10px;
              border: 1px solid transparent;
            }
            .kpi-card.emerald { background: #f0fdf4; border-color: #bbf7d0; }
            .kpi-card.rose { background: #fff1f2; border-color: #fecdd3; }
            .kpi-card.indigo { background: #eef2ff; border-color: #c7d2fe; }
            .kpi-card.amber { background: #fffbeb; border-color: #fde68a; }
            .kpi-label { font-size: 8.5px; font-weight: 800; text-transform: uppercase; margin-bottom: 2px; }
            .kpi-card.emerald .kpi-label { color: #166534; }
            .kpi-card.rose .kpi-label { color: #9f1239; }
            .kpi-card.indigo .kpi-label { color: #3730a3; }
            .kpi-card.amber .kpi-label { color: #92400e; }
            .kpi-val { font-size: 11px; font-weight: 800; color: #0f172a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            .kpi-sub { font-size: 9px; font-weight: 700; margin-top: 2px; }
            .kpi-card.emerald .kpi-sub { color: #15803d; }
            .kpi-card.rose .kpi-sub { color: #be123c; }
            .kpi-card.indigo .kpi-sub { color: #4338ca; }
            .kpi-card.amber .kpi-sub { color: #b45309; }

            .section-box {
              background: #ffffff;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              overflow: hidden;
            }
            .section-header {
              background: #f8fafc;
              padding: 6px 10px;
              border-bottom: 1px solid #e2e8f0;
              font-size: 10.5px;
              font-weight: 800;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              color: #334155;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }

            .narrative-box {
              background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
              border-left: 4.5px solid #4f46e5;
              border-top: 1px solid #e2e8f0;
              border-right: 1px solid #e2e8f0;
              border-bottom: 1px solid #e2e8f0;
              border-radius: 0 8px 8px 0;
              padding: 10px 14px;
              margin-bottom: 12px;
            }
            .narrative-title {
              font-size: 11px;
              font-weight: 800;
              color: #4338ca;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin-bottom: 5px;
            }
            .narrative-text {
              font-size: 11px;
              color: #334155;
              line-height: 1.45;
              text-align: justify;
            }
            .narrative-text p {
              margin: 0 0 5px 0;
            }
            .narrative-text p:last-child {
              margin-bottom: 0;
              font-weight: 700;
              color: #312e81;
            }

            .potential-box {
              background: #f0fdfa;
              border: 1px solid #99f6e4;
              border-radius: 6px;
              padding: 6px 10px;
              margin-top: 5px;
              font-size: 9.5px;
              color: #115e59;
              line-height: 1.35;
            }

            .sig-section {
              margin-top: 12px;
              display: flex;
              justify-content: flex-end;
            }
            .sig-box {
              text-align: center;
              width: 210px;
              font-size: 11px;
            }
            .sig-space {
              height: 48px;
            }
            .sig-name {
              font-weight: 800;
              color: #0f172a;
              text-decoration: underline;
            }
            .sig-nip {
              font-size: 10px;
              color: #64748b;
            }

            .footer-meta {
              padding-top: 5px;
              border-top: 1px dashed #cbd5e1;
              display: flex;
              justify-content: space-between;
              font-size: 9px;
              color: #94a3b8;
            }
          </style>
        </head>
        <body onload="window.print()">
    `;

    classStudents.forEach((student) => {
      const prog = academicProgress.find(p => p.studentId === student.id);
      if (!prog) return;

      const studentSemesterAverages = prog.semesters.map(sem => {
        if (sem.subjects.length === 0) return { name: sem.semesterName, avg: 0, sum: 0, count: 0 };
        const sum = sem.subjects.reduce((acc, curr) => acc + curr.grade, 0);
        return {
          name: sem.semesterName,
          avg: parseFloat((sum / sem.subjects.length).toFixed(1)),
          sum,
          count: sem.subjects.length
        };
      });

      const studentOverallTotalSum = studentSemesterAverages.reduce((acc, curr) => acc + curr.sum, 0);
      const validAverages = studentSemesterAverages.filter(s => s.count > 0);
      const totalSubjectCount = validAverages.reduce((acc, curr) => acc + curr.count, 0);
      const studentOverallAverage = totalSubjectCount > 0 
        ? parseFloat((studentOverallTotalSum / totalSubjectCount).toFixed(1)) 
        : 0;

      const subjectMap: Record<string, { sem: string, grade: number }[]> = {};
      const allGradesList: { semesterName: string; subjectName: string; grade: number }[] = [];

      prog.semesters.forEach(sem => {
        sem.subjects.forEach(sub => {
          allGradesList.push({ semesterName: sem.semesterName, subjectName: sub.subjectName, grade: sub.grade });
          if (!subjectMap[sub.subjectName]) subjectMap[sub.subjectName] = [];
          subjectMap[sub.subjectName].push({ sem: sem.semesterName, grade: sub.grade });
        });
      });

      let maxIncrease = { subject: '-', val: -999 };
      let maxDecrease = { subject: '-', val: 999 };
      let highestAvg = { subject: '-', val: -1 };
      let lowestAvg = { subject: '-', val: 101 };

      Object.entries(subjectMap).forEach(([sub, grades]) => {
        if (grades.length >= 2) {
          const first = grades[0].grade;
          const last = grades[grades.length - 1].grade;
          const diff = last - first;
          if (diff > maxIncrease.val) maxIncrease = { subject: sub, val: diff };
          if (diff < maxDecrease.val) maxDecrease = { subject: sub, val: diff };
        }
        const avg = grades.reduce((sum, g) => sum + g.grade, 0) / grades.length;
        if (avg > highestAvg.val) highestAvg = { subject: sub, val: avg };
        if (avg < lowestAvg.val) lowestAvg = { subject: sub, val: avg };
      });

      const KKTP_STANDARD = 75;
      const belowStandardGrades = allGradesList.filter(g => g.grade < KKTP_STANDARD);
      const topIndividualGrades = [...allGradesList].sort((a, b) => b.grade - a.grade).slice(0, 6);
      const subjectAverages = Object.entries(subjectMap).map(([sub, grades]) => ({
        subject: sub,
        avg: parseFloat((grades.reduce((sum, g) => sum + g.grade, 0) / grades.length).toFixed(1))
      })).sort((a, b) => b.avg - a.avg);

      const topSubjectAverages = subjectAverages.slice(0, 3);
      const studentOverallRank = classRankings.overallRankings[student.id];
      const studentSemesterRanks = classRankings.semesterRankings[student.id] || {};
      const hasEverEnteredTop10 = (studentOverallRank && studentOverallRank.rank <= 10) || prog.semesters.some(sem => studentSemesterRanks[sem.semesterName] && studentSemesterRanks[sem.semesterName].rank <= 10);
      const studentFirstName = student.name.split(' ')[0];

      const studentCareerProfile = getDetailedStudentCareerProfile(
        topSubjectAverages.map(s => s.subject),
        topIndividualGrades,
        student.name,
        student.gender
      );

      let rankStory = '';
      if (studentOverallRank) {
        if (studentOverallRank.rank <= 10) {
          rankStory = `Saat ini, peringkat ananda di kelas ${homeroomClass} berada di urutan ke-${studentOverallRank.rank} dari ${studentOverallRank.total} siswa dengan jumlah nilai seluruh mapel ${studentOverallRank.totalSum} (rata-rata gabungan ${studentOverallAverage}).`;
        } else {
          rankStory = `Saat ini, ananda terus menunjukkan konsistensi dan semangat belajar yang sangat baik di kelas ${homeroomClass} dengan jumlah nilai seluruh mapel ${studentOverallRank.totalSum} (rata-rata gabungan ${studentOverallAverage}).`;
        }
      }

      const introStory = `“Halo ${studentFirstName}, mari kita cermati rekam jejak perjalanan belajar ananda dari semester ke semester. ${rankStory ? rankStory : `Secara keseluruhan, capaian rata-rata gabungan ananda mencapai ${studentOverallAverage > 0 ? studentOverallAverage : '-'}.`}`;
      
      let topStory = '';
      if (topSubjectAverages.length > 0) {
        const topStr = topSubjectAverages.map(s => `${s.subject} (${s.avg})`).join(', ');
        const cleanNarrativeCareers = studentCareerProfile.careerPaths.map(p => p.replace(/^Opsi \d+\s*=\s*/, '')).slice(0, 3).join(', ');
        topStory = `Bapak sangat bangga dan mengapresiasi pencapaian ananda pada mata pelajaran ${topStr}, yang menjadi kekuatan utama prestasi belajarmu. Analisis prestasi mapel unggulan tersebut menunjukkan potensi dan bakat istimewa ananda pada bidang ${studentCareerProfile.specializationTrack}, dengan prospek karier menjanjikan di masa depan sebagai ${cleanNarrativeCareers}. Teruslah asah dan kembangkan minat belajar ini karena menjadi modal penting menggapai cita-citamu!`;
      }

      let progressStory = '';
      if (maxIncrease.subject !== '-' && maxIncrease.val > 0) {
        progressStory = `Selain itu, kerja keras ananda terlihat nyata di mata pelajaran ${maxIncrease.subject} with peningkatan signifikan sebesar +${maxIncrease.val} poin sejak awal semester.`;
      }

      let evaluationStory = '';
      if (belowStandardGrades.length > 0) {
        const belowSubs = Array.from(new Set(belowStandardGrades.map(g => g.subjectName))).join(', ');
        evaluationStory = `Namun, ada beberapa catatan evaluasi belajar bersama: terdapat ${belowStandardGrades.length} nilai yang masih di bawah standar KKTP 75, khususnya pada mata pelajaran ${belowSubs}. Jangan berkecil hati ya, evaluasi ini menjadi peta navigasi bagi kita untuk mulai menyusun strategi belajar mandiri dan bimbingan terarah.`;
      } else {
        evaluationStory = `Istimewanya lagi, seluruh nilai mata pelajaran ananda telah berhasil memenuhi dan melampaui standar kriteria ketercapaian KKTP (≥ 75) di semua semester tanpa kendala!`;
      }
      
      const closingStory = getCustomizedClosingStory(student.id, student.name);

      const uniqueSubjectList: string[] = [];
      prog.semesters.forEach(sem => {
        sem.subjects.forEach(sub => {
          if (!uniqueSubjectList.includes(sub.subjectName)) {
            uniqueSubjectList.push(sub.subjectName);
          }
        });
      });

      const daftarNilaiMatrixRows = uniqueSubjectList.map((subjectName, idx) => {
        const semGrades = prog.semesters.map(sem => {
          const sub = sem.subjects.find(s => s.subjectName.toLowerCase() === subjectName.toLowerCase());
          return sub ? sub.grade : null;
        });

        const validGrades = semGrades.filter((g): g is number => g !== null);
        const subjectTotal = validGrades.reduce((a, b) => a + b, 0);
        const subjectAvg = validGrades.length > 0 ? (subjectTotal / validGrades.length).toFixed(1) : '-';
        const isTuntas = validGrades.length > 0 && validGrades.every(g => g >= KKTP_STANDARD);

        const gradeTds = semGrades.map(g => {
          if (g === null) {
            return `<td style="padding: 4.5px 6px; text-align: center; color: #cbd5e1; font-size: 10.5px;">-</td>`;
          }
          const color = g >= KKTP_STANDARD ? '#059669' : '#dc2626';
          const bg = g < KKTP_STANDARD ? '#fef2f2' : 'transparent';
          return `<td style="padding: 4.5px 6px; text-align: center; font-weight: 800; color: ${color}; background-color: ${bg}; font-size: 10.5px;">${g}</td>`;
        }).join('');

        return `
          <tr style="border-bottom: 1px solid #f1f5f9; ${idx % 2 === 1 ? 'background-color: #fafbfc;' : ''}">
            <td style="padding: 4.5px 6px; text-align: center; color: #64748b; font-size: 9.5px; width: 22px;">${idx + 1}</td>
            <td style="padding: 4.5px 6px; font-weight: 700; color: #1e293b; font-size: 10.5px;">${subjectName}</td>
            ${gradeTds}
            <td style="padding: 4.5px 6px; text-align: center; font-weight: 800; color: #047857; font-size: 10.5px; background-color: #f0fdf4;">${subjectAvg}</td>
            <td style="padding: 4.5px 6px; text-align: center; font-size: 9.5px;">
              ${validGrades.length === 0 
                ? '-' 
                : (isTuntas 
                    ? '<span style="color: #059669; font-weight: 700;">✓ Tuntas</span>' 
                    : '<span style="color: #dc2626; font-weight: 700;">⚠ Remedial</span>')}
            </td>
          </tr>
        `;
      }).join('');

      const matrixSemTotals = prog.semesters.map(sem => sem.subjects.reduce((a, b) => a + b.grade, 0));
      const matrixSemAvgs = prog.semesters.map(sem => sem.subjects.length > 0 ? (sem.subjects.reduce((a, b) => a + b.grade, 0) / sem.subjects.length).toFixed(1) : '-');
      const matrixSemRanks = prog.semesters.map(sem => {
        const sRank = studentSemesterRanks[sem.semesterName];
        return (sRank && sRank.rank <= 10) ? `#${sRank.rank}` : '-';
      });

      const topGradesHtml = topIndividualGrades.map((g) => `
        <div style="display: flex; justify-content: space-between; align-items: center; padding: 4px 8px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 6px;">
          <div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 140px;">
            <span style="font-weight: 700; color: #1e293b; font-size: 10px;">${g.subjectName}</span>
            <span style="font-size: 8.5px; color: #64748b; margin-left: 3px;">(${g.semesterName})</span>
          </div>
          <div style="background: #ecfdf5; color: #065f46; border: 1px solid #a7f3d0; font-weight: 900; font-size: 10.5px; padding: 2px 6px; border-radius: 4px;">
            ${g.grade}
          </div>
        </div>
      `).join('');

      const leftLogoHtml = schoolSettings?.departmentLogoUrl
        ? `<img src="${schoolSettings.departmentLogoUrl}" style="height: 50px; max-width: 58px; object-fit: contain;" alt="Logo Dinas" />`
        : (schoolSettings?.logoUrl 
            ? `<img src="${schoolSettings.logoUrl}" style="height: 50px; max-width: 58px; object-fit: contain;" alt="Logo Sekolah" />` 
            : `<div style="width: 50px; height: 50px; background: linear-gradient(135deg, #4f46e5 0%, #3730a3 100%); border-radius: 8px; display: flex; align-items: center; justify-content: center; color: white; font-weight: 900; font-size: 22px;">${schoolTitle.charAt(0)}</div>`);

      const rightLogoHtml = schoolSettings?.logoUrl
        ? `<img src="${schoolSettings.logoUrl}" style="height: 50px; max-width: 58px; object-fit: contain;" alt="Logo Sekolah" />`
        : `<div style="width: 50px; height: 50px;"></div>`;

      htmlContent += `
        <!-- HALAMAN 1 SISWA: ${student.name} -->
        <div class="print-page">
          <div>
            <!-- KOP SURAT RESMI -->
            <div class="kop-container">
              <div class="kop-logo">
                ${leftLogoHtml}
              </div>
              <div class="kop-text">
                <div class="kop-gov">${govTitle}</div>
                <div class="kop-dept">${deptTitle}</div>
                <div class="kop-school">${schoolTitle}</div>
                <div class="kop-address">${schoolAddress}${additionalKop ? ` • ${additionalKop}` : ''}</div>
              </div>
              <div class="kop-logo">
                ${rightLogoHtml}
              </div>
            </div>

            <!-- DOCUMENT BANNER -->
            <div class="doc-banner">
              <div class="doc-pill">Laporan Analisis Akademik & Narasi Bimbingan</div>
              <div class="doc-subtitle">Analisis Komprehensif Potensi Minat & Perkembangan Prestasi Belajar Siswa</div>
            </div>

            <!-- PROFIL SISWA -->
            <div class="student-card">
              <div class="student-meta">
                <div class="student-name">${student.name}</div>
                <div style="color: #475569; font-size: 10px;">
                  NIS: <strong>${student.nis}</strong> &nbsp;|&nbsp; 
                  NISN: <strong>${student.nisn || '-'}</strong> &nbsp;|&nbsp; 
                  Kelas: <strong>Kelas ${homeroomClass}</strong>
                </div>
                <div style="color: #64748b; font-size: 9.5px;">
                  Tahun Ajaran: <strong>${academicYear}</strong> &nbsp;•&nbsp; 
                  Wali Kelas: <strong>${homeroomTeacherName}</strong>
                </div>
              </div>
              <div class="badges-container">
                ${(studentOverallRank && studentOverallRank.rank <= 10) ? `
                  <div class="stat-badge amber">
                    <div class="stat-badge-title">Peringkat Kelas</div>
                    <div class="stat-badge-val">#${studentOverallRank.rank}</div>
                  </div>
                ` : ''}
                <div class="stat-badge indigo">
                  <div class="stat-badge-title">Jumlah Nilai</div>
                  <div class="stat-badge-val">${studentOverallRank ? studentOverallRank.totalSum : studentOverallTotalSum}</div>
                </div>
                <div class="stat-badge emerald">
                  <div class="stat-badge-title">Rata Gabungan</div>
                  <div class="stat-badge-val">${studentOverallAverage > 0 ? studentOverallAverage : '-'}</div>
                </div>
              </div>
            </div>

            <!-- KPI HIGHLIGHT CARDS -->
            <div class="kpi-grid">
              <div class="kpi-card emerald">
                <div class="kpi-label">🚀 Peningkatan Tertinggi</div>
                <div class="kpi-val">${maxIncrease.subject}</div>
                <div class="kpi-sub">+${maxIncrease.val === -999 ? 0 : maxIncrease.val} Poin</div>
              </div>
              <div class="kpi-card rose">
                <div class="kpi-label">📉 Evaluasi Penurunan</div>
                <div class="kpi-val">${maxDecrease.subject}</div>
                <div class="kpi-sub">${maxDecrease.val === 999 ? 'Stabil' : `${maxDecrease.val} Poin`}</div>
              </div>
              <div class="kpi-card indigo">
                <div class="kpi-label">🌟 Rata Tertinggi</div>
                <div class="kpi-val">${highestAvg.subject}</div>
                <div class="kpi-sub">Skor: ${highestAvg.val === -1 ? '-' : highestAvg.val.toFixed(1)}</div>
              </div>
              <div class="kpi-card amber">
                <div class="kpi-label">🎯 Perlu Perhatian</div>
                <div class="kpi-val">${lowestAvg.subject}</div>
                <div class="kpi-sub">Skor: ${lowestAvg.val === 101 ? '-' : lowestAvg.val.toFixed(1)}</div>
              </div>
            </div>

            <!-- 1. DAFTAR NILAI AKADEMIK SISWA -->
            <div class="section-box" style="margin-bottom: 12px;">
              <div class="section-header">
                <span>1. Daftar Nilai Akademik Siswa (Seluruh Semester)</span>
                <span style="font-size: 9px; color: #64748b; font-weight: 700;">Standar KKTP: 75</span>
              </div>
              <table style="width: 100%; border-collapse: collapse; text-align: left;">
                <thead>
                  <tr style="background: #f8fafc; border-bottom: 1.5px solid #cbd5e1;">
                    <th style="padding: 4.5px 6px; font-size: 9.5px; font-weight: 800; color: #475569; width: 22px; text-align: center;">No</th>
                    <th style="padding: 4.5px 6px; font-size: 9.5px; font-weight: 800; color: #475569;">Mata Pelajaran</th>
                    ${prog.semesters.map(sem => `
                      <th style="padding: 4.5px 6px; font-size: 9.5px; font-weight: 800; color: #475569; text-align: center;">${sem.semesterName}</th>
                    `).join('')}
                    <th style="padding: 4.5px 6px; font-size: 9.5px; font-weight: 800; color: #047857; text-align: center; width: 60px;">Rata-Rata</th>
                    <th style="padding: 4.5px 6px; font-size: 9.5px; font-weight: 800; color: #475569; text-align: center; width: 70px;">KKTP</th>
                  </tr>
                </thead>
                <tbody>
                  ${daftarNilaiMatrixRows || '<tr><td colspan="10" style="padding: 8px; text-align: center; color: #94a3b8; font-size: 10px;">Belum ada data nilai</td></tr>'}
                </tbody>
                <tfoot>
                  <tr style="background: #f8fafc; border-top: 1.5px solid #cbd5e1; font-weight: 800;">
                    <td colspan="2" style="padding: 4px 6px; font-size: 9.5px; color: #1e293b; text-align: right;">Jumlah Nilai:</td>
                    ${matrixSemTotals.map(t => `<td style="padding: 4px 6px; text-align: center; font-weight: 900; color: #4338ca; font-size: 10px;">${t}</td>`).join('')}
                    <td style="padding: 4px 6px; text-align: center; font-weight: 900; color: #4338ca; font-size: 10px; background: #eef2ff;">${studentOverallRank ? studentOverallRank.totalSum : studentOverallTotalSum}</td>
                    <td style="padding: 4px 6px; text-align: center; font-size: 9px; color: #64748b;">-</td>
                  </tr>
                  <tr style="background: #f8fafc; border-top: 1px solid #e2e8f0; font-weight: 800;">
                    <td colspan="2" style="padding: 4px 6px; font-size: 9.5px; color: #1e293b; text-align: right;">Rata-Rata:</td>
                    ${matrixSemAvgs.map(a => `<td style="padding: 4px 6px; text-align: center; font-weight: 900; color: #059669; font-size: 10px;">${a}</td>`).join('')}
                    <td style="padding: 4px 6px; text-align: center; font-weight: 900; color: #059669; font-size: 10px; background: #ecfdf4;">${studentOverallAverage}</td>
                    <td style="padding: 4px 6px; text-align: center; font-size: 9px; color: #64748b;">-</td>
                  </tr>
                  ${hasEverEnteredTop10 ? `
                    <tr style="background: #fffbeb; border-top: 1px solid #fde68a; font-weight: 800;">
                      <td colspan="2" style="padding: 4px 6px; font-size: 9.5px; color: #92400e; text-align: right;">Peringkat Kelas:</td>
                      ${matrixSemRanks.map(r => `<td style="padding: 4px 6px; text-align: center; font-weight: 900; color: #b45309; font-size: 10px;">${r}</td>`).join('')}
                      <td style="padding: 4px 6px; text-align: center; font-weight: 900; color: #b45309; font-size: 10px; background: #fef3c7;">${(studentOverallRank && studentOverallRank.rank <= 10) ? `#${studentOverallRank.rank}` : '-'}</td>
                      <td style="padding: 4px 6px; text-align: center; font-size: 8px; color: #92400e;">(10 Besar)</td>
                    </tr>
                  ` : ''}
                </tfoot>
              </table>
            </div>

            <!-- MAPEL UNGGULAN & EVALUASI KKTP (STACKED) -->
            <div style="display: flex; flex-direction: column; gap: 12px; margin-bottom: 12px;">
              <!-- Top Performers & Career Potential -->
              <div class="section-box" style="display: flex; flex-direction: column; justify-content: space-between;">
                <div>
                  <div class="section-header">
                    <span>2. Prestasi Mapel Unggulan</span>
                    <span style="color: #059669; font-weight: 800; font-size: 8.5px;">Top Performers</span>
                  </div>
                  <div style="padding: 6px; display: grid; grid-template-columns: 1fr 1fr; gap: 6px;">
                    ${topGradesHtml || '<div style="color: #94a3b8; font-size: 10px;">Belum ada nilai</div>'}
                  </div>
                </div>
                
                ${studentCareerProfile ? `
                  <div style="padding: 0 6px 6px 6px;">
                    <div class="potential-box">
                      <strong>💡 Rekomendasi Potensi Karier & Peminatan:</strong><br/>
                      • <strong>Peminatan:</strong> ${studentCareerProfile.specializationTrack}<br/>
                      • <strong>Prospek Profesi:</strong><br/>
                      ${studentCareerProfile.careerPaths.map(p => `&nbsp;&nbsp;&nbsp;&nbsp;• ${p}`).join('<br/>')}
                    </div>
                  </div>
                ` : ''}
              </div>

              <!-- EVALUASI KKTP -->
              ${belowStandardGrades.length > 0 ? `
                <div class="section-box" style="border-color: #fde68a;">
                  <div class="section-header" style="background: #fffbeb; color: #92400e;">
                    <span>3. Evaluasi KKTP (&lt; 75) &amp; Solusi Belajar</span>
                    <span style="font-weight: 800; font-size: 8px; color: #b45309;">${belowStandardGrades.length} Remedial</span>
                  </div>
                  <div style="padding: 6px 8px; font-size: 9px; color: #78350f; line-height: 1.4;">
                    ${Object.entries(subjectMap).filter(([_, grades]) => grades.some(g => g.grade < 75)).map(([subName, grades]) => {
                      const evalData = generateKKTPSubjectEvaluation(subName, grades.map(g => ({ semesterName: g.sem, grade: g.grade })), student.name);
                      const semHistory = grades.map(g => `<span style="display:inline-block; margin-right:4px;">${g.sem}: <strong style="color: ${g.grade < 75 ? '#dc2626' : '#059669'};">${g.grade}</strong></span>`).join('');
                      return `
                        <div style="margin-bottom: 5px; padding-bottom: 5px; border-bottom: 1px dashed #fde68a;">
                          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5px;">
                            <strong style="color: #92400e; font-size: 9.5px;">${subName}</strong>
                            <span style="font-size: 8px; background: #fef3c7; color: #92400e; font-weight: 800; padding: 1px 5px; border-radius: 3px;">${evalData.trendLabel}</span>
                          </div>
                          <div style="font-size: 8.5px; color: #64748b; margin-bottom: 1.5px;">Rekam Semester: ${semHistory} (Rata: ${evalData.average})</div>
                          <div style="font-size: 8.5px; color: #475569; line-height: 1.3;">
                            • <strong>Motivasi:</strong> ${evalData.motivation}<br/>
                            • <strong>Saran:</strong> ${evalData.strategy}<br/>
                            • <strong>Tindak Lanjut:</strong> ${evalData.solutions[0]}
                          </div>
                        </div>
                      `;
                    }).join('')}
                  </div>
                </div>
              ` : `
                <div class="section-box" style="border-color: #bbf7d0;">
                  <div class="section-header" style="background: #f0fdf4; color: #166534;">
                    <span>3. Evaluasi KKTP (≥ 75)</span>
                    <span style="font-weight: 800; font-size: 8.5px; color: #15803d;">✓ 100% Tuntas</span>
                  </div>
                  <div style="padding: 10px 12px; font-size: 10px; color: #14532d; line-height: 1.45;">
                    Sangat membanggakan! Seluruh capaian nilai mata pelajaran ananda pada setiap semester telah tuntas melampaui standar kriteria KKTP (≥ 75) di semua semester tanpa ada kendala pembelajaran apapun. Pertahankan konsistensi ini!
                  </div>
                </div>
              `}
            </div>

            <!-- BIMBINGAN & MOTIVASI WALI KELAS -->
            <div class="narrative-box">
              <div class="narrative-title">
                <span>💬 Bimbingan & Motivasi Wali Kelas</span>
              </div>
              <div class="narrative-text">
                <p>${introStory}</p>
                ${topStory ? `<p>${topStory}</p>` : ''}
                ${progressStory ? `<p>${progressStory}</p>` : ''}
                <p>${evaluationStory}</p>
                <p>${closingStory}</p>
              </div>
            </div>

            <!-- LEMBAR PENGESAHAN -->
            <div class="sig-section">
              <div class="sig-box">
                <p>${city}, ${currentDate}<br/><strong>Wali Kelas ${homeroomClass}</strong></p>
                <div class="sig-space"></div>
                <div class="sig-name">${homeroomTeacherName}</div>
                <div class="sig-nip">${homeroomTeacherNip}</div>
              </div>
            </div>
          </div>

          <!-- FOOTER META -->
          <div class="footer-meta">
            <span>Dokumen Analisis Hasil Belajar & Bimbingan Peserta Didik • Kelas ${homeroomClass}</span>
            <span>Dicetak Otomatis pada ${currentDate} • ${schoolTitle}</span>
          </div>
        </div>
      `;
    });

    htmlContent += `
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800/80 shadow-xs overflow-hidden mt-6">
      {/* Top Header Card */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-indigo-600 dark:from-emerald-950 dark:via-teal-950 dark:to-slate-900 p-6 sm:p-7 text-white flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 relative overflow-hidden">
        <div className="absolute -right-12 -bottom-12 w-64 h-64 bg-white/5 rounded-full blur-2xl pointer-events-none" />
        <div className="relative z-10">
          <div className="flex items-center gap-2">
            <span className="p-1.5 bg-white/15 backdrop-blur-xs rounded-lg">
              <Award size={20} className="text-emerald-100" />
            </span>
            <span className="text-[11px] font-black tracking-widest uppercase text-emerald-200">Wali Kelas {homeroomClass}</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-black mt-1.5 tracking-tight">Perkembangan Akademik Siswa</h2>
          <p className="text-xs text-emerald-100/90 mt-0.5 font-medium max-w-xl">
            Pantau, kelola, dan unggah masal (Excel) pencapaian nilai siswa lintas semester 1-6 secara fleksibel.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto relative z-10">
          <button
            onClick={handleDownloadTemplate}
            className="flex-1 lg:flex-none flex items-center justify-center gap-2 px-3.5 py-2 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-xl font-bold text-xs transition-all cursor-pointer backdrop-blur-xs"
            title="Unduh Format Excel Template Upload"
          >
            <Download size={14} />
            <span>Format Excel</span>
          </button>

          <button
            onClick={() => setIsBulkUploadModalOpen(true)}
            className="flex-1 lg:flex-none flex items-center justify-center gap-2 px-3.5 py-2 bg-white text-emerald-900 hover:bg-emerald-50 rounded-xl font-bold text-xs shadow-xs transition-all cursor-pointer"
          >
            <FileSpreadsheet size={14} className="text-emerald-600" />
            <span>Upload Nilai Masal</span>
          </button>

          <button
            onClick={handlePrintAllStudentsAnalysisReport}
            className="flex-1 lg:flex-none flex items-center justify-center gap-2 px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-xs shadow-xs transition-all cursor-pointer"
          >
            <Printer size={14} />
            <span>Cetak Semua Siswa (Analisis & Narasi)</span>
          </button>

          {selectedStudent && currentProgress && (
            <button
              onClick={handlePrintReport}
              className="flex-1 lg:flex-none flex items-center justify-center gap-2 px-3.5 py-2 bg-emerald-950/40 hover:bg-emerald-950/60 text-white rounded-xl font-bold text-xs shadow-2xs transition-all cursor-pointer border border-emerald-400/30"
            >
              <Printer size={14} />
              <span>Cetak Rekap</span>
            </button>
          )}
        </div>
      </div>

      <div className="p-6">
        <div className="mb-6 flex flex-col sm:flex-row gap-4 sm:items-end justify-between">
          <div className="flex-1 max-w-md">
            <label className="block text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">
              Pilih Siswa
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handlePrevStudent}
                disabled={classStudents.length <= 1}
                className="px-3.5 py-2.5 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed border border-slate-200 dark:border-slate-700 rounded-xl text-slate-700 dark:text-slate-200 font-bold text-xs flex items-center gap-1 transition-all cursor-pointer shrink-0"
                title="Siswa Sebelumnya (Back)"
              >
                <ChevronLeft size={16} />
                <span className="hidden sm:inline">Back</span>
              </button>

              <select
                value={selectedStudentId}
                onChange={(e) => {
                  setSelectedStudentId(e.target.value);
                  setInlineEditCell(null);
                }}
                className="flex-1 px-4 py-2.5 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-semibold text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500 min-w-0"
              >
                <option value="">-- Tabel Rekapitulasi Seluruh Siswa ({classStudents.length}) --</option>
                {classStudents.map(student => (
                  <option key={student.id} value={student.id}>
                    {student.name} ({student.gender === 'Laki-laki' ? 'L' : 'P'} • NIS: {student.nis})
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={handleNextStudent}
                disabled={classStudents.length <= 1}
                className="px-3.5 py-2.5 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed border border-slate-200 dark:border-slate-700 rounded-xl text-slate-700 dark:text-slate-200 font-bold text-xs flex items-center gap-1 transition-all cursor-pointer shrink-0"
                title="Siswa Selanjutnya (Next)"
              >
                <span className="hidden sm:inline">Next</span>
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {selectedStudent && (
              <button
                type="button"
                onClick={() => {
                  setSelectedStudentId('');
                  setInlineEditCell(null);
                }}
                className="flex items-center gap-1.5 px-3.5 py-2.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl font-bold text-xs shadow-2xs transition-all cursor-pointer border border-slate-200 dark:border-slate-700"
                title="Kembali ke Tabel Rekapitulasi Kelas"
              >
                <Users size={15} />
                <span>Tabel Rekap Kelas</span>
              </button>
            )}

            {selectedStudent && currentProgress && (
              <button
                onClick={() => setShowAnalysisModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 rounded-xl font-black text-sm shadow-sm transition-all cursor-pointer"
              >
                <BarChart size={18} />
                <span>Analisis Siswa</span>
              </button>
            )}
          </div>
        </div>

        <div className="bg-slate-50/50 dark:bg-slate-900/30 rounded-3xl border border-slate-100 dark:border-slate-800/80 overflow-hidden">
          {selectedStudent ? (
            <div className="p-6">
              {/* Student Identity Banner */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-slate-800/60 p-5 rounded-3xl border border-slate-100 dark:border-slate-800/50 shadow-xs mb-6">
                <div>
                  <div className="flex items-center gap-2.5">
                    <h3 className="text-lg font-black text-slate-800 dark:text-white">{selectedStudent.name}</h3>
                    {currentStudentIndex >= 0 && (
                      <span className="text-[10px] font-extrabold px-2.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-full">
                        Siswa {currentStudentIndex + 1} dari {classStudents.length}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 dark:text-slate-500 font-bold mt-0.5 flex items-center gap-1.5 flex-wrap">
                    <span>NIS: {selectedStudent.nis}</span>
                    <span>•</span>
                    <span>NISN: {selectedStudent.nisn || '-'}</span>
                    <span>•</span>
                    <span className={`px-2 py-0.5 rounded text-[11px] font-black ${
                      selectedStudent.gender === 'Laki-laki'
                        ? 'bg-blue-50 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400'
                        : 'bg-pink-50 text-pink-600 dark:bg-pink-950/60 dark:text-pink-400'
                    }`}>
                      {selectedStudent.gender || 'L/P'}
                    </span>
                    <span>•</span>
                    <span>Kelas {homeroomClass}</span>
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-2xl border border-slate-200/60 dark:border-slate-700/60">
                    <button
                      onClick={handlePrevStudent}
                      disabled={classStudents.length <= 1}
                      className="px-3 py-1.5 hover:bg-white dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl font-bold text-xs flex items-center gap-1 transition-all cursor-pointer shadow-2xs disabled:opacity-40"
                      title="Siswa Sebelumnya (Back)"
                    >
                      <ChevronLeft size={14} />
                      <span>Back</span>
                    </button>
                    <button
                      onClick={handleNextStudent}
                      disabled={classStudents.length <= 1}
                      className="px-3 py-1.5 hover:bg-white dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl font-bold text-xs flex items-center gap-1 transition-all cursor-pointer shadow-2xs disabled:opacity-40"
                      title="Siswa Selanjutnya (Next)"
                    >
                      <span>Next</span>
                      <ChevronRight size={14} />
                    </button>
                  </div>

                  {overallAverage > 0 && (
                    <div className="flex items-center gap-3 bg-emerald-50 dark:bg-emerald-950/20 px-4 py-2.5 rounded-2xl border border-emerald-100 dark:border-emerald-900/30">
                      <TrendingUp className="text-emerald-500" size={20} />
                      <div>
                        <div className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Rata-rata Gabungan</div>
                        <div className="text-xl font-black text-emerald-700 dark:text-emerald-300">{overallAverage}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {!currentProgress ? (
                /* No progress document exists yet */
                <div className="flex flex-col items-center justify-center text-center py-12 p-6 bg-white dark:bg-slate-800/40 rounded-3xl border border-slate-100 dark:border-slate-800/40 shadow-xs">
                  <BookOpen size={48} className="text-slate-300 dark:text-slate-700 mb-4" />
                  <h4 className="font-bold text-slate-800 dark:text-white text-base">Belum Ada Data Perkembangan Akademik</h4>
                  <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm mt-1 mb-6">
                    Mulai catat perkembangan akademik siswa ini dengan menginisialisasi Semester 1 hingga 6 secara otomatis.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleInitializeDefaultSemesters}
                      className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl font-black text-sm flex items-center gap-2 shadow-md transition-all cursor-pointer"
                    >
                      <Sparkles size={16} />
                      <span>Inisialisasi Semester 1-6</span>
                    </button>
                    <button
                      onClick={() => setShowAddSemester(true)}
                      className="px-6 py-3 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-2xl font-black text-sm flex items-center gap-2 transition-all cursor-pointer"
                    >
                      <Plus size={16} />
                      <span>Buat Semester Custom</span>
                    </button>
                  </div>
                </div>
              ) : (
                /* Main Academic Progression Dashboard */
                <div className="space-y-6">
                  {/* Visual Trend Chart - Custom Dual Bar Chart with Average & Total Sum */}
                  {semesterAverages.some(s => s.count > 0) && (
                    <div className="bg-white dark:bg-slate-800/60 p-6 rounded-3xl border border-slate-100 dark:border-slate-800/50 shadow-xs space-y-5">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <TrendingUp size={18} className="text-indigo-500" />
                          <h4 className="text-xs font-black text-slate-700 dark:text-slate-300 uppercase tracking-widest">
                            Grafik Perkembangan Nilai Rata-Rata & Jumlah Nilai (Sum)
                          </h4>
                        </div>
                        <div className="flex items-center gap-4 text-xs font-bold bg-slate-50 dark:bg-slate-900 px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700">
                          <span className="flex items-center gap-1.5 text-indigo-600 dark:text-indigo-400">
                            <span className="w-3 h-3 rounded bg-indigo-500 inline-block"></span> Rata-Rata (Skala 0-100)
                          </span>
                          <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                            <span className="w-3 h-3 rounded bg-emerald-500 inline-block"></span> Jumlah Nilai / Sum
                          </span>
                        </div>
                      </div>
                      
                      {/* Dual Bar Chart Stage */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 pt-2">
                        {(() => {
                          const validAvgs = semesterAverages.map(s => s.avg).filter(a => a > 0);
                          const minAvg = validAvgs.length ? Math.min(...validAvgs) : 0;
                          const maxAvg = validAvgs.length ? Math.max(...validAvgs) : 100;
                          const avgRange = maxAvg - minAvg;

                          const validSums = semesterAverages.map(s => s.sum).filter(s => s > 0);
                          const minSum = validSums.length ? Math.min(...validSums) : 0;
                          const maxSum = validSums.length ? Math.max(...validSums) : 100;
                          const sumRange = maxSum - minSum;

                          return semesterAverages.map((sem) => {
                            const avgPct = sem.avg > 0 
                              ? (avgRange > 0 ? 25 + ((sem.avg - minAvg) / avgRange) * 75 : Math.min(100, Math.max(20, sem.avg))) 
                              : 0;

                            const sumPct = sem.sum > 0 
                              ? (sumRange > 0 ? 25 + ((sem.sum - minSum) / sumRange) * 75 : Math.min(100, Math.max(20, (sem.sum / (sem.count * 100)) * 100))) 
                              : 0;
                          
                            return (
                              <div 
                                key={sem.name} 
                                className="flex flex-col bg-slate-50/80 dark:bg-slate-900/80 p-3.5 rounded-2xl border border-slate-200/80 dark:border-slate-700/60 shadow-xs space-y-3"
                              >
                                {/* Semester Header */}
                                <div className="text-center pb-2 border-b border-slate-200 dark:border-slate-800">
                                  <span className="text-xs font-black text-slate-900 dark:text-white block truncate">
                                    {sem.name}
                                  </span>
                                  <span className="text-[10px] text-slate-500 dark:text-slate-400 font-semibold block mt-0.5">
                                    {sem.count > 0 ? `${sem.count} Mata Pelajaran` : 'Belum Ada Nilai'}
                                  </span>
                                </div>

                                {/* Bars Area (Fixed Height h-40) */}
                                <div className="h-40 w-full flex items-end justify-center gap-2.5 px-2 py-2 bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 dark:border-slate-700 relative">
                                  {/* Grid reference line */}
                                  <div className="absolute inset-x-0 top-1/2 border-t border-dashed border-slate-200 dark:border-slate-700/50 pointer-events-none" />

                                  {/* Bar 1: Rata-Rata (Avg) */}
                                  <div className="flex-1 h-full flex flex-col justify-end items-center group relative z-10">
                                    <span className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 mb-1 bg-indigo-50 dark:bg-indigo-950/60 px-1 rounded">
                                      {sem.avg > 0 ? sem.avg : '-'}
                                    </span>
                                    <div 
                                      className="w-full bg-gradient-to-t from-indigo-600 to-indigo-400 rounded-t-lg transition-all duration-500 shadow-sm min-h-[6px]"
                                      style={{ height: sem.avg > 0 ? `${avgPct}%` : '6px' }}
                                    />
                                    <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 mt-1 uppercase">Avg</span>
                                  </div>

                                  {/* Bar 2: Jumlah Nilai (Sum) */}
                                  <div className="flex-1 h-full flex flex-col justify-end items-center group relative z-10">
                                    <span className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 mb-1 bg-emerald-50 dark:bg-emerald-950/60 px-1 rounded truncate max-w-full">
                                      {sem.sum > 0 ? sem.sum : '-'}
                                    </span>
                                    <div 
                                      className="w-full bg-gradient-to-t from-emerald-600 to-emerald-400 rounded-t-lg transition-all duration-500 shadow-sm min-h-[6px]"
                                      style={{ height: sem.sum > 0 ? `${sumPct}%` : '6px' }}
                                    />
                                    <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 mt-1 uppercase">Sum</span>
                                  </div>
                                </div>

                                {/* Footer Summary pill */}
                                <div className="text-center pt-1">
                                  <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-lg inline-block ${
                                    sem.avg >= 75 
                                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300' 
                                      : sem.avg > 0 
                                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300' 
                                        : 'bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                                  }`}>
                                    {sem.avg >= 75 ? 'Tuntas KKTP' : sem.avg > 0 ? 'Perlu Perhatian' : 'Kosong'}
                                  </span>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}

                  {/* View Switcher & Add Semester Controls */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <h4 className="text-sm font-black text-slate-700 dark:text-white uppercase tracking-wider">
                        Daftar Nilai Akademik
                      </h4>

                      {/* View Switcher (Matrix vs Semester Cards) */}
                      <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200/80 dark:border-slate-700/80">
                        <button
                          type="button"
                          onClick={() => setStudentScoreViewMode('matrix')}
                          className={`px-2.5 py-1 text-[11px] font-black rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                            studentScoreViewMode === 'matrix'
                              ? 'bg-white dark:bg-slate-700 text-emerald-700 dark:text-emerald-400 shadow-xs'
                              : 'text-slate-500 hover:text-slate-700 dark:text-slate-400'
                          }`}
                        >
                          <Table size={13} />
                          <span>Matriks Tabel</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setStudentScoreViewMode('cards')}
                          className={`px-2.5 py-1 text-[11px] font-black rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                            studentScoreViewMode === 'cards'
                              ? 'bg-white dark:bg-slate-700 text-emerald-700 dark:text-emerald-400 shadow-xs'
                              : 'text-slate-500 hover:text-slate-700 dark:text-slate-400'
                          }`}
                        >
                          <LayoutGrid size={13} />
                          <span>Kartu Semester (Mobile)</span>
                        </button>
                      </div>
                    </div>

                    {!showAddSemester ? (
                      <button
                        onClick={() => setShowAddSemester(true)}
                        className="px-3.5 py-1.5 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400 rounded-xl font-black text-xs flex items-center gap-1.5 transition-all cursor-pointer self-start sm:self-auto"
                      >
                        <Plus size={14} />
                        <span>Tambah Semester</span>
                      </button>
                    ) : (
                      <form onSubmit={handleAddSemester} className="flex items-center gap-2 bg-white dark:bg-slate-800 p-1.5 rounded-2xl border border-slate-200 dark:border-slate-700">
                        <input
                          type="text"
                          required
                          placeholder="Nama Semester"
                          value={newSemesterName}
                          onChange={(e) => setNewSemesterName(e.target.value)}
                          className="px-3 py-1.5 text-xs font-semibold bg-transparent border-0 focus:ring-0 text-slate-800 dark:text-white"
                        />
                        <button type="submit" className="p-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
                          <Check size={12} />
                        </button>
                        <button type="button" onClick={() => setShowAddSemester(false)} className="p-1.5 bg-slate-100 dark:bg-slate-700 text-slate-500 rounded-lg">
                          <X size={12} />
                        </button>
                      </form>
                    )}
                  </div>

                  {/* Mode 1: Unified Matrix Table View */}
                  {studentScoreViewMode === 'matrix' ? (
                    <div>
                      <div className="flex sm:hidden items-center justify-between text-[11px] text-slate-400 font-medium px-1 mb-2">
                        <span>Geser tabel ke samping untuk melihat seluruh semester →</span>
                      </div>
                      <div className="overflow-x-auto w-full border border-slate-200 dark:border-slate-800 rounded-2xl custom-scrollbar shadow-xs pb-2">
                        <table className="w-full text-left border-collapse whitespace-nowrap min-w-max">
                          <thead>
                            <tr className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700 text-[10px] text-slate-500 uppercase tracking-widest font-black">
                              <th className="p-4 border-r border-slate-200 dark:border-slate-700 sticky left-0 bg-slate-50 dark:bg-slate-800/60 z-20">Mata Pelajaran</th>
                              {currentProgress.semesters.map(sem => (
                                <th key={sem.semesterName} className="p-4 border-r border-slate-200 dark:border-slate-700 text-center relative group">
                                  {sem.semesterName}
                                  <button 
                                    onClick={() => handleDeleteSemester(sem.semesterName)} 
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer bg-white dark:bg-slate-800 rounded-md shadow-sm"
                                    title="Hapus Semester"
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                            {Array.from(new Set(currentProgress.semesters.flatMap(sem => sem.subjects.map(s => s.subjectName)))).sort().map(subject => (
                              <tr key={subject} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                                <td className="p-4 text-xs font-bold text-slate-800 dark:text-slate-200 border-r border-slate-200 dark:border-slate-700 sticky left-0 bg-white dark:bg-slate-900 z-10">
                                  {subject}
                                </td>
                                {currentProgress.semesters.map(sem => {
                                  const subjectGrade = sem.subjects.find(s => s.subjectName === subject);
                                  const isEditing = inlineEditCell?.semesterName === sem.semesterName && inlineEditCell?.subjectName === subject;
                                  
                                  return (
                                    <td key={`${sem.semesterName}-${subject}`} className="p-2 border-r border-slate-200 dark:border-slate-700 text-center align-middle w-24">
                                      {isEditing ? (
                                        <input
                                          type="number"
                                          min="0"
                                          max="100"
                                          autoFocus
                                          defaultValue={subjectGrade?.grade ?? ''}
                                          onBlur={(e) => {
                                            handleUpdateGrade(sem.semesterName, subject, e.target.value);
                                            setInlineEditCell(null);
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              handleUpdateGrade(sem.semesterName, subject, e.currentTarget.value);
                                              setInlineEditCell(null);
                                            } else if (e.key === 'Escape') {
                                              setInlineEditCell(null);
                                            }
                                          }}
                                          className="w-full p-2 text-center text-xs font-bold bg-emerald-50 dark:bg-emerald-900/30 border-2 border-emerald-500 rounded-lg focus:outline-none dark:text-emerald-100"
                                        />
                                      ) : (
                                        <div 
                                          onClick={() => setInlineEditCell({ semesterName: sem.semesterName, subjectName: subject })}
                                          className="p-2 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg flex items-center justify-center group/cell h-full min-h-[32px]"
                                        >
                                          {subjectGrade ? (
                                            <span className={`font-black ${subjectGrade.grade < 75 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-700 dark:text-emerald-400'}`}>
                                              {subjectGrade.grade}
                                            </span>
                                          ) : (
                                            <span className="text-slate-300 dark:text-slate-600 text-xs font-bold">KOSONG</span>
                                          )}
                                          <Edit3 size={10} className="absolute right-3 text-slate-400 opacity-0 group-hover/cell:opacity-100 transition-opacity" />
                                        </div>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                            
                            {/* Row for Averages */}
                            <tr className="bg-slate-50/50 dark:bg-slate-800/30 border-t-2 border-slate-200 dark:border-slate-700">
                              <td className="p-4 text-xs font-black text-slate-500 dark:text-slate-400 border-r border-slate-200 dark:border-slate-700 sticky left-0 bg-slate-50 dark:bg-slate-800/60 z-10 text-right">
                                RATA-RATA SEMESTER
                              </td>
                              {currentProgress.semesters.map(sem => {
                                const sum = sem.subjects.reduce((acc, curr) => acc + curr.grade, 0);
                                const avg = sem.subjects.length > 0 ? parseFloat((sum / sem.subjects.length).toFixed(1)) : 0;
                                return (
                                  <td key={`avg-${sem.semesterName}`} className="p-4 text-center font-black text-sm text-indigo-600 dark:text-indigo-400 border-r border-slate-200 dark:border-slate-700">
                                    {avg > 0 ? avg : '-'}
                                  </td>
                                );
                              })}
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    /* Mode 2: Semester Card Grid (Ultra Mobile-Friendly) */
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {currentProgress.semesters.map((sem) => {
                        const semSum = sem.subjects.reduce((a, b) => a + b.grade, 0);
                        const semAvg = sem.subjects.length > 0
                          ? parseFloat((semSum / sem.subjects.length).toFixed(1))
                          : 0;

                        return (
                          <div 
                            key={`card-sem-${sem.semesterName}`}
                            className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 p-4 shadow-xs flex flex-col justify-between"
                          >
                            <div>
                              <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3 mb-3">
                                <div>
                                  <h5 className="text-sm font-black text-slate-800 dark:text-white">
                                    {sem.semesterName}
                                  </h5>
                                  <span className="text-[11px] text-slate-400 font-medium">
                                    {sem.subjects.length} Mata Pelajaran Terdata
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteSemester(sem.semesterName)}
                                  className="p-1.5 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-xl transition-colors cursor-pointer"
                                  title="Hapus Semester"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>

                              <div className="space-y-2 divide-y divide-slate-100 dark:divide-slate-800">
                                {sem.subjects.map((sub) => {
                                  const isEditing = inlineEditCell?.semesterName === sem.semesterName && inlineEditCell?.subjectName === sub.subjectName;

                                  return (
                                    <div 
                                      key={`${sem.semesterName}-${sub.subjectName}`} 
                                      className="pt-2 flex items-center justify-between gap-2 text-xs"
                                    >
                                      <span className="font-bold text-slate-700 dark:text-slate-300 truncate">
                                        {sub.subjectName}
                                      </span>

                                      {isEditing ? (
                                        <input
                                          type="number"
                                          min="0"
                                          max="100"
                                          autoFocus
                                          defaultValue={sub.grade}
                                          onBlur={(e) => {
                                            handleUpdateGrade(sem.semesterName, sub.subjectName, e.target.value);
                                            setInlineEditCell(null);
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              handleUpdateGrade(sem.semesterName, sub.subjectName, e.currentTarget.value);
                                              setInlineEditCell(null);
                                            } else if (e.key === 'Escape') {
                                              setInlineEditCell(null);
                                            }
                                          }}
                                          className="w-16 p-1.5 text-center text-xs font-black bg-emerald-50 dark:bg-emerald-950/40 border-2 border-emerald-500 rounded-xl focus:outline-none dark:text-emerald-200"
                                        />
                                      ) : (
                                        <button
                                          type="button"
                                          onClick={() => setInlineEditCell({ semesterName: sem.semesterName, subjectName: sub.subjectName })}
                                          className="px-3 py-1 bg-slate-50 dark:bg-slate-800 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 border border-slate-200 dark:border-slate-700 rounded-xl flex items-center gap-1.5 cursor-pointer group"
                                        >
                                          <span className={`font-black ${sub.grade < 75 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-700 dark:text-emerald-400'}`}>
                                            {sub.grade}
                                          </span>
                                          <Edit3 size={11} className="text-slate-400 group-hover:text-emerald-600" />
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}

                                {sem.subjects.length === 0 && (
                                  <div className="py-6 text-center text-xs text-slate-400 italic">
                                    Belum ada mata pelajaran untuk semester ini.
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs font-bold">
                              <span className="text-slate-400">Rata-Rata</span>
                              <span className="px-2.5 py-0.5 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-400 font-black rounded-lg">
                                {semAvg > 0 ? semAvg : '-'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Add New Subject Across the Board */}
                  <div>
                    {!isAddingNewSubject ? (
                      <button
                        onClick={() => setIsAddingNewSubject(true)}
                        className="px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-2xl font-black text-xs flex items-center gap-1.5 transition-all cursor-pointer"
                      >
                        <Plus size={14} />
                        <span>Tambah Mata Pelajaran Baru</span>
                      </button>
                    ) : (
                      <form onSubmit={handleAddGlobalSubject} className="flex items-center gap-2 max-w-sm">
                        <input
                          type="text"
                          required
                          autoFocus
                          placeholder="Nama Mata Pelajaran (misal: Fisika)"
                          value={globalNewSubjectName}
                          onChange={(e) => setGlobalNewSubjectName(e.target.value)}
                          className="flex-1 px-3 py-2 text-xs font-semibold bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-white"
                        />
                        <button type="submit" className="p-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700">
                          <Check size={14} />
                        </button>
                        <button type="button" onClick={() => setIsAddingNewSubject(false)} className="p-2 bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-xl">
                          <X size={14} />
                        </button>
                      </form>
                    )}
                    <p className="text-[10px] text-slate-400 mt-2 ml-1 font-medium">Klik pada sel KOSONG di dalam tabel untuk mulai memasukkan atau mengubah nilai secara spesifik.</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-4 sm:p-6 space-y-6">
              {/* Overview Header & KPI Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-slate-800/80 p-4 sm:p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-xs flex items-center gap-3.5">
                  <div className="p-3 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-2xl shrink-0">
                    <Users size={22} />
                  </div>
                  <div>
                    <div className="text-[11px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-500">
                      Total Siswa Kelas {homeroomClass}
                    </div>
                    <div className="text-xl font-black text-slate-800 dark:text-white mt-0.5">
                      {overviewStats.total} <span className="text-xs font-bold text-slate-500 font-normal">Siswa</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1 text-[10px] font-extrabold">
                      <span className="text-blue-600 dark:text-blue-400">{overviewStats.maleCount} L</span>
                      <span className="text-slate-300 dark:text-slate-600">•</span>
                      <span className="text-pink-600 dark:text-pink-400">{overviewStats.femaleCount} P</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-slate-800/80 p-4 sm:p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-xs flex items-center gap-3.5">
                  <div className="p-3 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 rounded-2xl shrink-0">
                    <CheckCircle2 size={22} />
                  </div>
                  <div>
                    <div className="text-[11px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-500">
                      Rekap Nilai Terisi
                    </div>
                    <div className="text-xl font-black text-slate-800 dark:text-white mt-0.5">
                      {overviewStats.withDataCount} <span className="text-xs font-bold text-slate-500 font-normal">/ {overviewStats.total} Siswa</span>
                    </div>
                    <div className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                      {overviewStats.total > 0 ? Math.round((overviewStats.withDataCount / overviewStats.total) * 100) : 0}% terdata
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-slate-800/80 p-4 sm:p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-xs flex items-center gap-3.5">
                  <div className="p-3 bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 rounded-2xl shrink-0">
                    <Trophy size={22} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-500">
                      Peringkat 1 Kelas
                    </div>
                    <div className="text-sm font-black text-slate-800 dark:text-white mt-0.5 truncate" title={overviewStats.rank1Student?.student.name || '-'}>
                      {overviewStats.rank1Student ? overviewStats.rank1Student.student.name : '-'}
                    </div>
                    <div className="text-[10px] font-bold text-amber-600 dark:text-amber-400 mt-1">
                      {overviewStats.rank1Student ? `Total: ${overviewStats.rank1Student.totalSum} Poin (Avg: ${overviewStats.rank1Student.overallAvg})` : 'Belum dihitung'}
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-slate-800/80 p-4 sm:p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-xs flex items-center gap-3.5">
                  <div className="p-3 bg-teal-50 dark:bg-teal-950/60 text-teal-600 dark:text-teal-400 rounded-2xl shrink-0">
                    <TrendingUp size={22} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-500">
                      Rata-Rata Tertinggi
                    </div>
                    <div className="text-xl font-black text-teal-600 dark:text-teal-400 mt-0.5">
                      {overviewStats.topAvgStudent ? overviewStats.topAvgStudent.overallAvg : '-'}
                    </div>
                    <div className="text-[10px] font-bold text-slate-500 dark:text-slate-400 mt-1 truncate">
                      {overviewStats.topAvgStudent ? overviewStats.topAvgStudent.student.name : 'Belum ada nilai'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Controls Bar: Search, Gender Filter, Sorting, and View Switcher */}
              <div className="bg-white dark:bg-slate-800/80 p-4 sm:p-5 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-xs space-y-4">
                <div className="flex flex-col lg:flex-row gap-3 justify-between items-stretch lg:items-center">
                  {/* Search Input */}
                  <div className="relative flex-1">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input
                      type="text"
                      value={overviewSearchQuery}
                      onChange={(e) => setOverviewSearchQuery(e.target.value)}
                      placeholder="Cari nama siswa, NIS, atau NISN..."
                      className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-xs font-semibold text-slate-800 dark:text-white placeholder-slate-400 outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                    />
                    {overviewSearchQuery && (
                      <button
                        onClick={() => setOverviewSearchQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>

                  {/* Filter, Sort & View Mode Controls */}
                  <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
                    {/* Gender Tabs */}
                    <div className="flex items-center p-1 bg-slate-100 dark:bg-slate-900 rounded-2xl border border-slate-200/70 dark:border-slate-800 shrink-0">
                      <button
                        type="button"
                        onClick={() => setOverviewGenderFilter('all')}
                        className={`px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-black transition-all cursor-pointer ${
                          overviewGenderFilter === 'all'
                            ? 'bg-white dark:bg-slate-800 text-slate-800 dark:text-white shadow-2xs'
                            : 'text-slate-500 hover:text-slate-800 dark:hover:text-white'
                        }`}
                      >
                        Semua ({overviewStats.total})
                      </button>
                      <button
                        type="button"
                        onClick={() => setOverviewGenderFilter('Laki-laki')}
                        className={`flex items-center gap-1 px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-black transition-all cursor-pointer ${
                          overviewGenderFilter === 'Laki-laki'
                            ? 'bg-blue-500 text-white shadow-2xs shadow-blue-500/20'
                            : 'text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30'
                        }`}
                      >
                        <span>Laki-laki</span>
                        <span className="text-[10px] opacity-90 font-bold">({overviewStats.maleCount})</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setOverviewGenderFilter('Perempuan')}
                        className={`flex items-center gap-1 px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-black transition-all cursor-pointer ${
                          overviewGenderFilter === 'Perempuan'
                            ? 'bg-pink-500 text-white shadow-2xs shadow-pink-500/20'
                            : 'text-pink-600 dark:text-pink-400 hover:bg-pink-50 dark:hover:bg-pink-950/30'
                        }`}
                      >
                        <span>Perempuan</span>
                        <span className="text-[10px] opacity-90 font-bold">({overviewStats.femaleCount})</span>
                      </button>
                    </div>

                    {/* Sort Dropdown */}
                    <div className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-900 px-3 py-2 rounded-2xl border border-slate-200 dark:border-slate-700 shrink-0">
                      <ArrowUpDown size={14} className="text-slate-400" />
                      <span className="text-[11px] font-bold text-slate-400 uppercase hidden sm:inline">Urutkan:</span>
                      <select
                        value={overviewSort}
                        onChange={(e) => setOverviewSort(e.target.value as any)}
                        className="bg-transparent text-xs font-black text-slate-800 dark:text-white outline-none cursor-pointer"
                      >
                        <option value="rank" className="dark:bg-slate-900">Peringkat Kelas (Tertinggi)</option>
                        <option value="totalSum" className="dark:bg-slate-900">Jumlah Nilai Terbanyak</option>
                        <option value="avg" className="dark:bg-slate-900">Rata-Rata Nilai (Tertinggi)</option>
                        <option value="name" className="dark:bg-slate-900">Nama Siswa (A-Z)</option>
                        <option value="nis" className="dark:bg-slate-900">Nomor Induk Siswa (NIS)</option>
                      </select>
                    </div>

                    {/* View Switcher: Table vs Cards */}
                    <div className="flex items-center p-1 bg-slate-100 dark:bg-slate-900 rounded-2xl border border-slate-200/70 dark:border-slate-800 shrink-0">
                      <button
                        type="button"
                        onClick={() => setOverviewViewMode('cards')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-black transition-all cursor-pointer ${
                          overviewViewMode === 'cards'
                            ? 'bg-emerald-600 text-white shadow-2xs'
                            : 'text-slate-500 hover:text-slate-800 dark:hover:text-white'
                        }`}
                        title="Tampilan Kartu Siswa (Optimal Mobile)"
                      >
                        <LayoutGrid size={14} />
                        <span className="hidden sm:inline">Kartu Siswa</span>
                        <span className="sm:hidden">Kartu</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setOverviewViewMode('table')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-black transition-all cursor-pointer ${
                          overviewViewMode === 'table'
                            ? 'bg-emerald-600 text-white shadow-2xs'
                            : 'text-slate-500 hover:text-slate-800 dark:hover:text-white'
                        }`}
                        title="Tampilan Tabel Rekapitulasi (Optimal Desktop/Tablet)"
                      >
                        <List size={14} />
                        <span className="hidden sm:inline">Tabel Lengkap</span>
                        <span className="sm:hidden">Tabel</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Card View Mode (Optimal for Mobile & Quick Browsing) */}
                {overviewViewMode === 'cards' && (
                  <div className="space-y-4">
                    {filteredOverviewStudents.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-4">
                        {filteredOverviewStudents.map((item, idx) => {
                          const isMale = item.student.gender === 'Laki-laki';
                          const hasScore = item.totalSum > 0;

                          return (
                            <div
                              key={item.student.id}
                              onClick={() => {
                                setSelectedStudentId(item.student.id);
                                setInlineEditCell(null);
                              }}
                              className="bg-white dark:bg-slate-900 p-4 sm:p-5 rounded-2xl sm:rounded-3xl border border-slate-200/80 dark:border-slate-800 hover:border-emerald-500 dark:hover:border-emerald-500/60 shadow-xs hover:shadow-md transition-all cursor-pointer flex flex-col justify-between group relative overflow-hidden"
                            >
                              {/* Top Section: Avatar, Name, Gender, NISN & Rank Badge */}
                              <div>
                                <div className="flex items-start justify-between gap-2.5 mb-3">
                                  <div className="flex items-center gap-2.5 min-w-0">
                                    <div className={`w-9 h-9 rounded-2xl flex items-center justify-center font-black text-sm shrink-0 shadow-xs ${
                                      isMale
                                        ? 'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
                                        : 'bg-pink-100 dark:bg-pink-950 text-pink-700 dark:text-pink-300 border border-pink-200 dark:border-pink-800'
                                    }`}>
                                      {isMale ? 'L' : 'P'}
                                    </div>
                                    <div className="min-w-0">
                                      <h4 className={`font-black text-sm leading-tight truncate transition-colors ${
                                        isMale 
                                          ? 'text-blue-700 dark:text-blue-400 group-hover:text-blue-800 dark:group-hover:text-blue-300' 
                                          : 'text-pink-700 dark:text-pink-400 group-hover:text-pink-800 dark:group-hover:text-pink-300'
                                      }`}>
                                        {item.student.name}
                                      </h4>
                                      <div className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400 font-semibold mt-0.5 flex-wrap">
                                        <span className="font-mono font-bold text-slate-700 dark:text-slate-300">NIS {item.student.nis}</span>
                                        {item.student.nisn && (
                                          <>
                                            <span>•</span>
                                            <span className="font-mono text-[10px] bg-slate-100 dark:bg-slate-800 px-1.5 py-0.2 rounded text-slate-600 dark:text-slate-300">
                                              NISN: {item.student.nisn}
                                            </span>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Rank Badge */}
                                  <div className="shrink-0">
                                    {hasScore && item.rank !== null ? (
                                      item.rank === 1 ? (
                                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-950 font-black text-xs rounded-xl shadow-xs border border-amber-300">
                                          <Trophy size={13} className="text-amber-950" />
                                          #1
                                        </span>
                                      ) : item.rank === 2 ? (
                                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-slate-200 to-slate-300 text-slate-900 font-black text-xs rounded-xl shadow-xs border border-slate-300">
                                          <Medal size={13} className="text-slate-700" />
                                          #2
                                        </span>
                                      ) : item.rank === 3 ? (
                                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-amber-600 to-amber-700 text-white font-black text-xs rounded-xl shadow-xs border border-amber-500">
                                          <Medal size={13} className="text-amber-200" />
                                          #3
                                        </span>
                                      ) : item.rank <= 10 ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800/60 font-black text-[11px] rounded-xl">
                                          <Award size={12} className="text-amber-600" />
                                          #{item.rank}
                                        </span>
                                      ) : (
                                        <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-black text-[11px] rounded-xl">
                                          #{item.rank}
                                        </span>
                                      )
                                    ) : (
                                      <span className="text-slate-300 dark:text-slate-600 text-xs font-bold">-</span>
                                    )}
                                  </div>
                                </div>

                                {/* 3 Metrics Mini Grid */}
                                <div className="grid grid-cols-3 gap-2 bg-slate-50 dark:bg-slate-800/60 p-2.5 rounded-2xl border border-slate-100 dark:border-slate-800 text-center mb-3.5">
                                  <div>
                                    <span className="text-[9px] font-black uppercase text-slate-400 dark:text-slate-500 block">
                                      Semester
                                    </span>
                                    <span className="text-xs font-black text-slate-700 dark:text-slate-200 mt-0.5 inline-block">
                                      {item.semCount > 0 ? `${item.semCount} Sem` : '0 Sem'}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-[9px] font-black uppercase text-slate-400 dark:text-slate-500 block">
                                      Total Poin
                                    </span>
                                    <span className="text-xs font-black text-indigo-600 dark:text-indigo-400 mt-0.5 inline-block">
                                      {item.totalSum > 0 ? item.totalSum : '-'}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-[9px] font-black uppercase text-slate-400 dark:text-slate-500 block">
                                      Rata-Rata
                                    </span>
                                    <span className={`text-xs font-black mt-0.5 inline-block ${
                                      item.overallAvg >= 75 
                                        ? 'text-emerald-600 dark:text-emerald-400' 
                                        : item.overallAvg > 0 
                                          ? 'text-amber-600 dark:text-amber-400' 
                                          : 'text-slate-400'
                                    }`}>
                                      {item.overallAvg > 0 ? item.overallAvg : '-'}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Action Footer */}
                              <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2">
                                <span className="text-[10px] font-extrabold text-slate-400">
                                  {item.student.gender} • No. {idx + 1}
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedStudentId(item.student.id);
                                    setInlineEditCell(null);
                                  }}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs rounded-xl shadow-xs transition-all cursor-pointer group-hover:scale-102"
                                >
                                  <span>Kelola Nilai</span>
                                  <ChevronRight size={14} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="py-12 text-center text-slate-400 bg-white dark:bg-slate-900 rounded-3xl border border-slate-200/80 dark:border-slate-800 p-6">
                        <Search size={32} className="text-slate-300 dark:text-slate-600 mx-auto mb-2" />
                        <p className="text-xs font-bold text-slate-600 dark:text-slate-400">
                          Tidak ada siswa yang sesuai dengan filter pencarian.
                        </p>
                        {(overviewSearchQuery || overviewGenderFilter !== 'all') && (
                          <button
                            type="button"
                            onClick={() => {
                              setOverviewSearchQuery('');
                              setOverviewGenderFilter('all');
                            }}
                            className="mt-2 text-xs font-black text-emerald-600 hover:underline"
                          >
                            Reset Filter Pencarian
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Table View Mode (Optimal for Desktop/Tablets) */}
                {overviewViewMode === 'table' && (
                  <div className="space-y-2">
                    {/* Mobile swipe indicator banner */}
                    <div className="md:hidden flex items-center justify-between px-3 py-1.5 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/50 rounded-xl text-[11px] font-bold text-amber-800 dark:text-amber-300">
                      <span>💡 Geser tabel ke samping untuk melihat seluruh kolom</span>
                      <ChevronRight size={14} className="animate-pulse shrink-0" />
                    </div>

                    <div className="overflow-x-auto rounded-2xl border border-slate-200/80 dark:border-slate-700/80 shadow-2xs custom-scrollbar">
                      <table className="w-full text-left border-collapse min-w-[780px]">
                        <thead>
                          <tr className="bg-slate-100/90 dark:bg-slate-800/90 text-slate-600 dark:text-slate-300 text-[11px] font-black uppercase tracking-wider border-b border-slate-200 dark:border-slate-700">
                            <th className="py-3.5 px-3 text-center w-12">No</th>
                            <th className="py-3.5 px-4">Nama Siswa</th>
                            <th className="py-3.5 px-3">NIS</th>
                            <th className="py-3.5 px-3">NISN</th>
                            <th className="py-3.5 px-3 text-center">L/P</th>
                            <th className="py-3.5 px-3 text-center">Semester Terisi</th>
                            <th className="py-3.5 px-3 text-center">Jumlah Nilai (Semua Sem)</th>
                            <th className="py-3.5 px-3 text-center">Rata-Rata</th>
                            <th className="py-3.5 px-3 text-center">Peringkat Kelas</th>
                            <th className="py-3.5 px-3 text-center w-28">Aksi</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800 bg-white dark:bg-slate-900 text-xs">
                          {filteredOverviewStudents.length > 0 ? (
                            filteredOverviewStudents.map((item, idx) => {
                              const isMale = item.student.gender === 'Laki-laki';
                              const hasScore = item.totalSum > 0;

                              return (
                                <tr
                                  key={item.student.id}
                                  onClick={() => {
                                    setSelectedStudentId(item.student.id);
                                    setInlineEditCell(null);
                                  }}
                                  className="hover:bg-slate-50/90 dark:hover:bg-slate-800/50 transition-colors cursor-pointer group"
                                >
                                  {/* No */}
                                  <td className="py-3.5 px-3 text-center font-bold text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300">
                                    {idx + 1}
                                  </td>

                                  {/* Nama Siswa (Warna disesuaikan L/P) */}
                                  <td className="py-3.5 px-4">
                                    <div className="flex items-center gap-2.5">
                                      {/* Avatar Gender Indicator */}
                                      <div className={`w-7 h-7 rounded-xl flex items-center justify-center font-black text-xs shrink-0 shadow-2xs ${
                                        isMale
                                          ? 'bg-blue-100 dark:bg-blue-950/70 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
                                          : 'bg-pink-100 dark:bg-pink-950/70 text-pink-700 dark:text-pink-300 border border-pink-200 dark:border-pink-800'
                                      }`}>
                                        {isMale ? 'L' : 'P'}
                                      </div>
                                      
                                      <div>
                                        <span className={`font-black text-xs transition-colors ${
                                          isMale
                                            ? 'text-blue-700 dark:text-blue-400 group-hover:text-blue-800 dark:group-hover:text-blue-300'
                                            : 'text-pink-700 dark:text-pink-400 group-hover:text-pink-800 dark:group-hover:text-pink-300'
                                        }`}>
                                          {item.student.name}
                                        </span>
                                        {item.student.nickname && (
                                          <span className="text-[10px] text-slate-400 ml-1.5 font-normal">
                                            ({item.student.nickname})
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </td>

                                  {/* NIS */}
                                  <td className="py-3.5 px-3 font-mono font-bold text-slate-700 dark:text-slate-300 text-xs">
                                    {item.student.nis}
                                  </td>

                                  {/* NISN */}
                                  <td className="py-3.5 px-3">
                                    {item.student.nisn ? (
                                      <span className="font-mono text-[11px] font-bold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md border border-slate-200/60 dark:border-slate-700/60">
                                        {item.student.nisn}
                                      </span>
                                    ) : (
                                      <span className="text-slate-300 dark:text-slate-600 font-mono text-xs">-</span>
                                    )}
                                  </td>

                                  {/* Jenis Kelamin Badge */}
                                  <td className="py-3.5 px-3 text-center">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-extrabold border ${
                                      isMale
                                        ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/50 dark:text-blue-300 dark:border-blue-800/80'
                                        : 'bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-950/50 dark:text-pink-300 dark:border-pink-800/80'
                                    }`}>
                                      {isMale ? 'Laki-laki' : 'Perempuan'}
                                    </span>
                                  </td>

                                  {/* Semester Terisi */}
                                  <td className="py-3.5 px-3 text-center">
                                    {item.semCount > 0 ? (
                                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-[10px] font-extrabold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                                        <BookOpen size={11} className="text-slate-400" />
                                        {item.semCount} Semester
                                      </span>
                                    ) : (
                                      <span className="text-slate-300 dark:text-slate-600 text-[11px] italic">0 Semester</span>
                                    )}
                                  </td>

                                  {/* Jumlah Nilai Semua Semester */}
                                  <td className="py-3.5 px-3 text-center">
                                    {hasScore ? (
                                      <div className="inline-flex flex-col items-center">
                                        <span className="px-3 py-1 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200/70 dark:border-indigo-800/70 font-black text-xs rounded-xl shadow-2xs">
                                          {item.totalSum}
                                        </span>
                                        <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 mt-0.5">
                                          {item.totalSubjects} nilai mapel
                                        </span>
                                      </div>
                                    ) : (
                                      <span className="text-slate-400 dark:text-slate-500 text-[11px] font-medium italic">
                                        Belum diinput
                                      </span>
                                    )}
                                  </td>

                                  {/* Rata-Rata Nilai */}
                                  <td className="py-3.5 px-3 text-center">
                                    {item.overallAvg > 0 ? (
                                      <span className="px-2.5 py-1 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200/70 dark:border-emerald-800/70 font-black text-xs rounded-xl shadow-2xs">
                                        {item.overallAvg}
                                      </span>
                                    ) : (
                                      <span className="text-slate-400 text-xs">-</span>
                                    )}
                                  </td>

                                  {/* Peringkat / Rangking Kelas */}
                                  <td className="py-3.5 px-3 text-center">
                                    {hasScore && item.rank !== null ? (
                                      item.rank === 1 ? (
                                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-950 font-black text-xs rounded-xl shadow-xs border border-amber-300">
                                          <Trophy size={13} className="text-amber-950" />
                                          #1 Juara 1
                                        </span>
                                      ) : item.rank === 2 ? (
                                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-slate-200 to-slate-300 text-slate-900 font-black text-xs rounded-xl shadow-xs border border-slate-300">
                                          <Medal size={13} className="text-slate-700" />
                                          #2 Juara 2
                                        </span>
                                      ) : item.rank === 3 ? (
                                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-amber-600 to-amber-700 text-white font-black text-xs rounded-xl shadow-xs border border-amber-500">
                                          <Medal size={13} className="text-amber-200" />
                                          #3 Juara 3
                                        </span>
                                      ) : item.rank <= 10 ? (
                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-amber-50 dark:bg-amber-950/50 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800/60 font-black text-xs rounded-xl">
                                          <Award size={12} className="text-amber-600" />
                                          #{item.rank} <span className="text-[9px] font-bold opacity-80">(10 Besar)</span>
                                        </span>
                                      ) : (
                                        <span className="px-2.5 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-extrabold text-xs rounded-xl">
                                          #{item.rank}
                                        </span>
                                      )
                                    ) : (
                                      <span className="text-slate-400 text-xs font-semibold">-</span>
                                    )}
                                  </td>

                                  {/* Aksi Button */}
                                  <td className="py-3.5 px-3 text-center">
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedStudentId(item.student.id);
                                        setInlineEditCell(null);
                                      }}
                                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-[11px] rounded-xl shadow-2xs transition-all cursor-pointer group-hover:scale-105"
                                      title="Kelola Riwayat Semester & Nilai Siswa"
                                    >
                                      <span>Kelola</span>
                                      <ChevronRight size={13} />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={10} className="py-12 text-center text-slate-400">
                                <div className="flex flex-col items-center justify-center">
                                  <Search size={32} className="text-slate-300 dark:text-slate-600 mb-2" />
                                  <p className="text-xs font-bold text-slate-600 dark:text-slate-400">
                                    Tidak ada siswa yang sesuai dengan filter pencarian.
                                  </p>
                                  {(overviewSearchQuery || overviewGenderFilter !== 'all') && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOverviewSearchQuery('');
                                        setOverviewGenderFilter('all');
                                      }}
                                      className="mt-2 text-xs font-black text-emerald-600 hover:underline"
                                    >
                                      Reset Filter Pencarian
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                        {filteredOverviewStudents.length > 0 && (
                          <tfoot>
                            <tr className="bg-slate-50 dark:bg-slate-800/80 font-black text-slate-600 dark:text-slate-300 text-xs border-t border-slate-200 dark:border-slate-700">
                              <td colSpan={6} className="py-3.5 px-4 text-right">
                                Total Data Ditampilkan: {filteredOverviewStudents.length} Siswa
                              </td>
                              <td className="py-3.5 px-3 text-center font-black text-indigo-700 dark:text-indigo-400">
                                {filteredOverviewStudents.reduce((acc, curr) => acc + curr.totalSum, 0)} Total Poin
                              </td>
                              <td className="py-3.5 px-3 text-center font-black text-emerald-700 dark:text-emerald-400">
                                {(
                                  filteredOverviewStudents.filter(s => s.overallAvg > 0).length > 0
                                    ? (filteredOverviewStudents.reduce((acc, curr) => acc + curr.overallAvg, 0) / filteredOverviewStudents.filter(s => s.overallAvg > 0).length).toFixed(1)
                                    : '-'
                                )} (Rata-rata)
                              </td>
                              <td colSpan={2} className="py-3.5 px-3 text-center text-[11px] font-bold text-slate-400">
                                Kelas {homeroomClass}
                              </td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal Upload Nilai Masal */}
      {isBulkUploadModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-2xl w-full p-6 sm:p-8 shadow-2xl border border-slate-100 dark:border-slate-800 max-h-[90vh] overflow-y-auto custom-scrollbar flex flex-col">
            <div className="flex justify-between items-center mb-6 border-b border-slate-100 dark:border-slate-800 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400 rounded-2xl">
                  <FileSpreadsheet size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-800 dark:text-white">Upload Nilai Masal Excel / CSV</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Unggah rekap nilai semester siswa Kelas {homeroomClass} sekaligus.</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setIsBulkUploadModalOpen(false);
                  setParsedBulkData([]);
                  setUnmatchedRows([]);
                  setUploadFileName('');
                }}
                className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
              >
                <X size={20} />
              </button>
            </div>

            {/* Step 1: File Upload & Template Download */}
            <div className="space-y-6">
              <div className="bg-emerald-50/60 dark:bg-emerald-950/20 p-4 rounded-2xl border border-emerald-100 dark:border-emerald-900/40 flex flex-col gap-3">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                  <div className="flex items-start gap-2.5">
                    <Info size={18} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                    <div className="text-xs text-emerald-900 dark:text-emerald-200 font-medium leading-relaxed">
                      Belum punya format file yang sesuai? Unduh template Excel yang sudah terisi nama siswa dan format kolom yang benar.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowTemplateSettings(!showTemplateSettings)}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-xs flex items-center gap-1.5 shrink-0 cursor-pointer"
                  >
                    <Download size={14} />
                    <span>Konfigurasi & Unduh Template</span>
                  </button>
                </div>
                
                {showTemplateSettings && (
                  <div className="mt-2 p-4 bg-white dark:bg-slate-900 rounded-xl border border-emerald-200 dark:border-emerald-800/60">
                    <label className="block text-xs font-black text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                      Nama-Nama Mata Pelajaran (Kolom)
                    </label>
                    <p className="text-[10px] text-slate-500 mb-2">
                      Pisahkan setiap mata pelajaran dengan koma (,). Kolom ini akan digenerate secara horizontal.
                    </p>
                    <textarea
                      value={templateSubjectsInput}
                      onChange={(e) => setTemplateSubjectsInput(e.target.value)}
                      rows={3}
                      className="w-full text-xs p-3 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-emerald-500 dark:bg-slate-800 dark:text-white"
                    />
                    <div className="flex justify-end mt-3">
                      <button
                        type="button"
                        onClick={handleDownloadTemplate}
                        className="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl font-bold text-xs flex items-center gap-1.5 cursor-pointer shadow-md"
                      >
                        <FileSpreadsheet size={14} />
                        <span>Unduh File .xlsx Sekarang</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Upload Input Area */}
              <div>
                <label className="block text-xs font-black text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                  Pilih File Excel / CSV (.xlsx, .xls, .csv)
                </label>
                <div 
                  onClick={() => bulkFileInputRef.current?.click()}
                  className="border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-emerald-500 dark:hover:border-emerald-500 rounded-2xl p-8 text-center bg-slate-50/50 dark:bg-slate-800/30 hover:bg-emerald-50/30 dark:hover:bg-emerald-950/10 transition-all cursor-pointer group"
                >
                  <input
                    ref={bulkFileInputRef}
                    type="file"
                    accept=".xlsx, .xls, .csv"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <FileUp size={36} className="mx-auto text-slate-400 group-hover:text-emerald-500 transition-colors mb-3" />
                  {uploadFileName ? (
                    <div>
                      <span className="text-xs font-extrabold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-1.5 rounded-xl">
                        {uploadFileName}
                      </span>
                      <p className="text-[11px] text-slate-400 font-medium mt-2">Klik untuk mengganti file</p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-xs font-bold text-slate-700 dark:text-slate-300">Klik di sini untuk memilih file dari perangkat Anda</p>
                      <p className="text-[11px] text-slate-400 mt-1">Mendukung format .xlsx, .xls, .csv (Maksimal 10MB)</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Mode Selection */}
              <div>
                <label className="block text-xs font-black text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                  Metode Penggabungan Data
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className={`p-3.5 rounded-2xl border flex items-start gap-3 cursor-pointer transition-all ${
                    bulkMode === 'merge'
                      ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/20 text-emerald-800 dark:text-emerald-300 font-bold'
                      : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                  }`}>
                    <input
                      type="radio"
                      name="bulkMode"
                      checked={bulkMode === 'merge'}
                      onChange={() => setBulkMode('merge')}
                      className="mt-1 text-emerald-600 focus:ring-emerald-500"
                    />
                    <div>
                      <div className="text-xs font-black">Gabungkan (Merge)</div>
                      <div className="text-[10px] opacity-80 font-normal mt-0.5">
                        Menambahkan & memperbarui nilai baru tanpa menghapus data semester/mapel yang sudah tersimpan.
                      </div>
                    </div>
                  </label>

                  <label className={`p-3.5 rounded-2xl border flex items-start gap-3 cursor-pointer transition-all ${
                    bulkMode === 'replace'
                      ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/20 text-emerald-800 dark:text-emerald-300 font-bold'
                      : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                  }`}>
                    <input
                      type="radio"
                      name="bulkMode"
                      checked={bulkMode === 'replace'}
                      onChange={() => setBulkMode('replace')}
                      className="mt-1 text-emerald-600 focus:ring-emerald-500"
                    />
                    <div>
                      <div className="text-xs font-black">Timpa Total (Replace)</div>
                      <div className="text-[10px] opacity-80 font-normal mt-0.5">
                        Mengganti seluruh riwayat semester & nilai siswa dengan data baru yang diunggah.
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Parsed Preview Section */}
              {parsedBulkData.length > 0 && (
                <div className="space-y-3 pt-2 border-t border-slate-100 dark:border-slate-800">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-black text-slate-800 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
                      <CheckCircle2 size={16} className="text-emerald-500" />
                      Data Terbaca ({parsedBulkData.length} Siswa Teridentifikasi)
                    </span>
                    {unmatchedRows.length > 0 && (
                      <span className="text-[11px] font-bold text-amber-600 dark:text-amber-400 flex items-center gap-1">
                        <AlertCircle size={14} />
                        {unmatchedRows.length} baris tidak cocok
                      </span>
                    )}
                  </div>

                  <div className="max-h-48 overflow-y-auto border border-slate-200 dark:border-slate-800 rounded-2xl p-2 bg-slate-50/50 dark:bg-slate-800/40 divide-y divide-slate-100 dark:divide-slate-800 custom-scrollbar">
                    {parsedBulkData.map((item) => {
                      const totalSubjects = item.semesters.reduce((acc, s) => acc + s.subjects.length, 0);
                      return (
                        <div key={item.matchedStudent.id} className="py-2 px-3 flex justify-between items-center text-xs">
                          <div>
                            <span className="font-bold text-slate-800 dark:text-white">{item.matchedStudent.name}</span>
                            <span className="text-slate-400 text-[10px] ml-2">NIS: {item.matchedStudent.nis}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 rounded-lg text-[10px] font-extrabold">
                              {item.semesters.length} Semester
                            </span>
                            <span className="px-2 py-0.5 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-400 rounded-lg text-[10px] font-extrabold">
                              {totalSubjects} Nilai Mapel
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Footer Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => {
                    setIsBulkUploadModalOpen(false);
                    setParsedBulkData([]);
                    setUnmatchedRows([]);
                    setUploadFileName('');
                  }}
                  className="px-5 py-2.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-2xl font-bold text-xs transition-all cursor-pointer"
                >
                  Batal
                </button>
                <button
                  type="button"
                  disabled={parsedBulkData.length === 0 || isProcessingBulk}
                  onClick={handleConfirmBulkSave}
                  className={`px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl font-black text-xs flex items-center gap-2 shadow-md transition-all cursor-pointer ${
                    parsedBulkData.length === 0 || isProcessingBulk ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isProcessingBulk ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" />
                      <span>Memproses Data...</span>
                    </>
                  ) : (
                    <>
                      <Save size={14} />
                      <span>Simpan ({parsedBulkData.length} Siswa)</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analysis Modal */}
      {showAnalysisModal && selectedStudent && currentProgress && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-4xl w-full p-6 sm:p-8 shadow-2xl border border-slate-100 dark:border-slate-800 max-h-[92vh] overflow-y-auto custom-scrollbar flex flex-col">
            {/* Header Modal */}
            <div className="flex justify-between items-center mb-6 border-b border-slate-100 dark:border-slate-800 pb-5">
              <div className="flex items-center gap-3.5">
                <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-2xl shadow-md shadow-indigo-500/20">
                  <BarChart size={24} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-black text-slate-800 dark:text-white">Analisis Perkembangan & Narasi Bimbingan</h3>
                    <span className="px-2.5 py-0.5 bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 font-extrabold text-[10px] rounded-full border border-indigo-200/60 dark:border-indigo-800/60">
                      Kelas {homeroomClass}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    <strong className="text-slate-700 dark:text-slate-200">{selectedStudent.name}</strong> • NIS: {selectedStudent.nis} • NISN: {selectedStudent.nisn || '-'}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => {
                  setShowAnalysisModal(false);
                  setCopiedNarrative(false);
                }}
                className="p-2.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-2xl transition-colors cursor-pointer text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <X size={20} />
              </button>
            </div>

            {(() => {
              const subjectMap: Record<string, { sem: string, grade: number }[]> = {};
              const allGradesList: { semesterName: string; subjectName: string; grade: number }[] = [];

              currentProgress.semesters.forEach(sem => {
                sem.subjects.forEach(sub => {
                  allGradesList.push({
                    semesterName: sem.semesterName,
                    subjectName: sub.subjectName,
                    grade: sub.grade
                  });
                  if (!subjectMap[sub.subjectName]) subjectMap[sub.subjectName] = [];
                  subjectMap[sub.subjectName].push({ sem: sem.semesterName, grade: sub.grade });
                });
              });

              let maxIncrease = { subject: '-', val: -999 };
              let maxDecrease = { subject: '-', val: 999 };
              let highestAvg = { subject: '-', val: -1 };
              let lowestAvg = { subject: '-', val: 101 };

              Object.entries(subjectMap).forEach(([sub, grades]) => {
                if (grades.length >= 2) {
                  const first = grades[0].grade;
                  const last = grades[grades.length - 1].grade;
                  const diff = last - first;
                  if (diff > maxIncrease.val) maxIncrease = { subject: sub, val: diff };
                  if (diff < maxDecrease.val) maxDecrease = { subject: sub, val: diff };
                }
                
                const avg = grades.reduce((sum, g) => sum + g.grade, 0) / grades.length;
                if (avg > highestAvg.val) highestAvg = { subject: sub, val: avg };
                if (avg < lowestAvg.val) lowestAvg = { subject: sub, val: avg };
              });

              // Standar KKTP = 75
              const KKTP_STANDARD = 75;
              const belowStandardGrades = allGradesList.filter(g => g.grade < KKTP_STANDARD);
              
              // Top Individual Grades
              const topIndividualGrades = [...allGradesList]
                .sort((a, b) => b.grade - a.grade)
                .slice(0, 6);

              // Subject Averages List
              const subjectAverages = Object.entries(subjectMap).map(([sub, grades]) => ({
                subject: sub,
                avg: parseFloat((grades.reduce((sum, g) => sum + g.grade, 0) / grades.length).toFixed(1))
              })).sort((a, b) => b.avg - a.avg);

              const topSubjectAverages = subjectAverages.slice(0, 3);
              const topSubjectNames = topSubjectAverages.map(s => s.subject);

              // Ranking data for selected student
              const studentOverallRank = classRankings.overallRankings[selectedStudent.id];
              const studentSemesterRanks = classRankings.semesterRankings[selectedStudent.id] || {};

              // Compute Full Detailed Career & Specialization Profile for this student
              const studentCareerProfile = getDetailedStudentCareerProfile(
                topSubjectNames,
                topIndividualGrades,
                selectedStudent.name,
                selectedStudent.gender
              );

              // Construct Narrative Story for Student Counseling
              const studentFirstName = selectedStudent.name.split(' ')[0];
              let rankStory = '';
              if (studentOverallRank) {
                if (studentOverallRank.rank <= 10) {
                  rankStory = `Saat ini, peringkat ananda di kelas ${homeroomClass} berada di urutan ke-${studentOverallRank.rank} dari ${studentOverallRank.total} siswa dengan jumlah nilai seluruh mapel ${studentOverallRank.totalSum} (rata-rata gabungan ${overallAverage}).\n`;
                } else {
                  rankStory = `Saat ini, ananda terus menunjukkan konsistensi dan semangat belajar yang sangat baik di kelas ${homeroomClass} dengan jumlah nilai seluruh mapel ${studentOverallRank.totalSum} (rata-rata gabungan ${overallAverage}).\n`;
                }
              }

              const introStory = `“Halo ${studentFirstName}, mari kita cermati rekam jejak perjalanan belajar ananda dari semester ke semester. ${rankStory ? rankStory : `Secara keseluruhan, capaian rata-rata gabungan ananda mencapai ${overallAverage > 0 ? overallAverage : '-'}.\n`}`;
              
              let topStory = '';
              if (topSubjectAverages.length > 0) {
                const topStr = topSubjectAverages.map(s => `${s.subject} (${s.avg})`).join(', ');
                const cleanNarrativeCareers = studentCareerProfile.careerPaths.map(p => p.replace(/^Opsi \d+\s*=\s*/, '')).slice(0, 3).join(', ');
                topStory = `Bapak sangat bangga dan mengapresiasi pencapaian ananda pada mata pelajaran ${topStr}, yang menjadi kekuatan dan keunggulan utamamu. Berdasarkan analisis pencapaian mapel unggulan tersebut, ananda memiliki bakat dan potensi istimewa pada ${studentCareerProfile.specializationTrack} dengan prospek karier menjanjikan sebagai ${cleanNarrativeCareers}. Teruslah asah dan kembangkan minat belajar ini karena menjadi modal penting menggapai cita-citamu!\n`;
              }

              let progressStory = '';
              if (maxIncrease.subject !== '-' && maxIncrease.val > 0) {
                progressStory = `Selain itu, kerja keras dan dedikasi ananda terlihat nyata di mata pelajaran ${maxIncrease.subject} dengan peningkatan signifikan sebesar +${maxIncrease.val} poin sejak awal semester.\n`;
              }

              let evaluationStory = '';
              if (belowStandardGrades.length > 0) {
                const belowSubs = Array.from(new Set(belowStandardGrades.map(g => g.subjectName))).join(', ');
                evaluationStory = `Namun, ada beberapa aspek yang perlu kita evaluasi dan perbaiki bersama. Terdapat ${belowStandardGrades.length} catatan nilai yang masih di bawah kriteria ketercapaian KKTP 75, khususnya pada mata pelajaran ${belowSubs}. Jangan berkecil hati ya, evaluasi ini menjadi peta navigasi bagi kita untuk mulai menyusun strategi belajar mandiri serta bimbingan ekstra yang lebih terarah.\n`;
              } else {
                evaluationStory = `Istimewanya lagi, seluruh nilai mata pelajaran ananda telah berhasil memenuhi dan melampaui standar kriteria ketercapaian KKTP (≥ 75) di semua semester tanpa kendala!\n`;
              }
              
              const closingStory = getCustomizedClosingStory(selectedStudent.id, selectedStudent.name);

              const fullNarrativeText = `${introStory}\n${topStory}\n${progressStory}\n${evaluationStory}\n${closingStory}`;

              const handleCopyNarrative = () => {
                navigator.clipboard.writeText(fullNarrativeText);
                setCopiedNarrative(true);
                setTimeout(() => setCopiedNarrative(false), 2500);
              };

              return (
                <div className="space-y-6">
                  {/* Top Stats 4 Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
                    <div className="bg-emerald-50/80 dark:bg-emerald-950/20 border border-emerald-200/80 dark:border-emerald-800/50 p-4 rounded-2xl shadow-xs flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-400">
                            Peningkatan Tertinggi
                          </span>
                          <TrendingUp size={14} className="text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <div className="text-sm font-black text-slate-800 dark:text-white truncate">
                          {maxIncrease.subject}
                        </div>
                      </div>
                      <div className="text-xs text-emerald-700 dark:text-emerald-300 font-bold mt-2 flex items-center gap-1">
                        <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/60 rounded-md">
                          +{maxIncrease.val === -999 ? 0 : maxIncrease.val} Poin
                        </span>
                        <span className="text-[10px] font-medium text-emerald-600/80">sejak awal</span>
                      </div>
                    </div>

                    <div className="bg-rose-50/80 dark:bg-rose-950/20 border border-rose-200/80 dark:border-rose-800/50 p-4 rounded-2xl shadow-xs flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[10px] font-black uppercase tracking-widest text-rose-700 dark:text-rose-400">
                            Evaluasi Penurunan
                          </span>
                          <AlertTriangle size={14} className="text-rose-600 dark:text-rose-400" />
                        </div>
                        <div className="text-sm font-black text-slate-800 dark:text-white truncate">
                          {maxDecrease.subject}
                        </div>
                      </div>
                      <div className="text-xs text-rose-700 dark:text-rose-300 font-bold mt-2 flex items-center gap-1">
                        <span className="px-2 py-0.5 bg-rose-100 dark:bg-rose-900/60 rounded-md">
                          {maxDecrease.val === 999 ? 'Stabil' : `${maxDecrease.val} Poin`}
                        </span>
                        <span className="text-[10px] font-medium text-rose-600/80">perlu perhatian</span>
                      </div>
                    </div>

                    <div className="bg-indigo-50/80 dark:bg-indigo-950/20 border border-indigo-200/80 dark:border-indigo-800/50 p-4 rounded-2xl shadow-xs flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[10px] font-black uppercase tracking-widest text-indigo-700 dark:text-indigo-400">
                            Rata-Rata Tertinggi
                          </span>
                          <Award size={14} className="text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <div className="text-sm font-black text-slate-800 dark:text-white truncate">
                          {highestAvg.subject}
                        </div>
                      </div>
                      <div className="text-xs text-indigo-700 dark:text-indigo-300 font-bold mt-2">
                        <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/60 rounded-md">
                          Skor: {highestAvg.val === -1 ? '-' : highestAvg.val.toFixed(1)}
                        </span>
                      </div>
                    </div>

                    <div className="bg-amber-50/80 dark:bg-amber-950/20 border border-amber-200/80 dark:border-amber-800/50 p-4 rounded-2xl shadow-xs flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[10px] font-black uppercase tracking-widest text-amber-700 dark:text-amber-400">
                            Perlu Pembinaan
                          </span>
                          <Target size={14} className="text-amber-600 dark:text-amber-400" />
                        </div>
                        <div className="text-sm font-black text-slate-800 dark:text-white truncate">
                          {lowestAvg.subject}
                        </div>
                      </div>
                      <div className="text-xs text-amber-700 dark:text-amber-300 font-bold mt-2">
                        <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/60 rounded-md">
                          Skor: {lowestAvg.val === 101 ? '-' : lowestAvg.val.toFixed(1)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Section: Ranking Siswa & Peringkat Kelas */}
                  <div className="bg-gradient-to-br from-amber-500/10 via-indigo-500/10 to-purple-500/10 dark:from-amber-950/30 dark:via-indigo-950/30 dark:to-purple-950/30 rounded-3xl border border-amber-200/70 dark:border-amber-800/40 p-5 shadow-xs">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4 border-b border-amber-200/50 dark:border-amber-800/40 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="p-2 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-xl shadow-xs">
                          <Trophy size={18} />
                        </div>
                        <div>
                          <h4 className="text-xs font-black uppercase tracking-wider text-slate-900 dark:text-amber-200">
                            Peringkat Prestasi Belajar Siswa
                          </h4>
                          <p className="text-[11px] text-slate-500 dark:text-amber-300/80 font-medium">
                            Peringkat dihitung berdasarkan jumlah seluruh nilai mata pelajaran relatif terhadap {classStudents.length} siswa di Kelas {homeroomClass}
                          </p>
                        </div>
                      </div>

                      {studentOverallRank && (
                        <span className="px-3.5 py-1 bg-amber-500 text-slate-950 font-black rounded-full text-xs shadow-xs flex items-center gap-1.5 self-start sm:self-auto">
                          <Sparkles size={13} />
                          <span>Peringkat Gabungan: #{studentOverallRank.rank} / {studentOverallRank.total}</span>
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
                      {/* Overall Card */}
                      <div className="p-4 bg-white dark:bg-slate-800/90 rounded-2xl border border-amber-200/80 dark:border-amber-900/50 flex flex-col justify-between shadow-xs">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-extrabold uppercase tracking-widest text-amber-600 dark:text-amber-400">
                            Rangking Gabungan Semua Semester
                          </span>
                          <Trophy size={16} className="text-amber-500" />
                        </div>
                        <div className="my-3 space-y-1">
                          <div className="text-3xl font-black text-slate-900 dark:text-white">
                            {studentOverallRank ? `#${studentOverallRank.rank}` : '-'}
                            <span className="text-xs font-bold text-slate-400 dark:text-slate-500 ml-1.5">
                              / {studentOverallRank ? studentOverallRank.total : classStudents.length} Siswa
                            </span>
                          </div>
                          <div className="text-xs font-bold text-indigo-600 dark:text-indigo-400">
                            Jumlah Seluruh Nilai: <span className="text-sm font-black">{studentOverallRank ? studentOverallRank.totalSum : overallTotalSum}</span>
                          </div>
                          <div className="text-xs font-bold text-emerald-600 dark:text-emerald-400">
                            Rata-Rata Kumulatif: <span className="text-sm font-black">{overallAverage}</span>
                          </div>
                        </div>
                        <span className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">
                          Peringkat berbasis akumulasi jumlah seluruh nilai mapel
                        </span>
                      </div>

                      {/* Per Semester Ranks */}
                      <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {currentProgress.semesters.map((sem) => {
                          const semRank = studentSemesterRanks[sem.semesterName];
                          const semSum = sem.subjects.reduce((a, b) => a + b.grade, 0);
                          const semAvg = sem.subjects.length > 0
                            ? parseFloat((semSum / sem.subjects.length).toFixed(1))
                            : 0;

                          return (
                            <div 
                              key={`rank-sem-${sem.semesterName}`}
                              className="p-3.5 bg-white dark:bg-slate-800/90 rounded-2xl border border-slate-200/80 dark:border-slate-700/80 flex items-center justify-between text-xs shadow-xs"
                            >
                              <div>
                                <span className="font-extrabold text-slate-800 dark:text-slate-200 block text-xs">
                                  {sem.semesterName}
                                </span>
                                <div className="text-[10px] text-slate-500 dark:text-slate-400 font-medium mt-0.5 space-y-0.5">
                                  <div>Jumlah Nilai: <strong className="text-indigo-600 dark:text-indigo-400 font-black">{semSum}</strong> ({sem.subjects.length} Mapel)</div>
                                  <div>Rata-rata: <strong className="text-emerald-600 dark:text-emerald-400">{semAvg || '-'}</strong></div>
                                </div>
                              </div>

                              {semRank ? (
                                <div className="text-right">
                                  <span className="px-2.5 py-1 bg-indigo-100 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300 font-black rounded-lg text-xs inline-block">
                                    Rangking #{semRank.rank}
                                  </span>
                                  <span className="text-[9.5px] text-slate-400 dark:text-slate-500 block mt-0.5">
                                    dari {semRank.total} siswa
                                  </span>
                                </div>
                              ) : (
                                <span className="text-[10px] text-slate-400 italic">Belum teranking</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Section: Prestasi Mapel Unggulan & Analisis Potensi Masa Depan */}
                  <div className="bg-white dark:bg-slate-800/80 rounded-3xl border border-slate-200 dark:border-slate-700 p-5 shadow-xs space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-3">
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 rounded-xl">
                          <Award size={18} />
                        </div>
                        <div>
                          <h4 className="text-xs font-black uppercase tracking-wider text-slate-800 dark:text-white">
                            Pencapaian Mapel Unggulan & Prospek Minat Bakat
                          </h4>
                          <p className="text-[10.5px] text-slate-400 font-medium">
                            Mata pelajaran dengan torehan nilai tertinggi beserta analisis arah pengembangan
                          </p>
                        </div>
                      </div>
                      <span className="px-2.5 py-0.5 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 font-extrabold text-[10px] rounded-full border border-emerald-200 dark:border-emerald-800">
                        Top Performers
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                      {topIndividualGrades.length > 0 ? (
                        topIndividualGrades.map((g, idx) => (
                          <div 
                            key={`${g.semesterName}-${g.subjectName}-${idx}`}
                            className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/60 rounded-2xl border border-slate-100 dark:border-slate-700/60 text-xs"
                          >
                            <div className="min-w-0 pr-2">
                              <span className="font-bold text-slate-800 dark:text-slate-200 block truncate">
                                {g.subjectName}
                              </span>
                              <span className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">
                                {g.semesterName}
                              </span>
                            </div>
                            <span className="px-2.5 py-1 bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 font-black rounded-xl text-xs shrink-0 shadow-2xs">
                              {g.grade}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-slate-400 italic">Belum ada data nilai.</p>
                      )}
                    </div>

                    {/* Career & Specialization Recommendation Card */}
                    {studentCareerProfile && (
                      <div className="mt-4 p-5 bg-gradient-to-br from-indigo-50/90 via-teal-50/70 to-emerald-50/90 dark:from-indigo-950/40 dark:via-teal-950/30 dark:to-emerald-950/40 border border-teal-200/80 dark:border-teal-800/60 rounded-3xl space-y-4 shadow-xs">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-teal-200/60 dark:border-teal-800/50 pb-3">
                          <div className="flex items-center gap-2">
                            <div className="p-2 bg-gradient-to-br from-teal-500 to-emerald-600 text-white rounded-xl shadow-xs">
                              <Compass size={18} />
                            </div>
                            <div>
                              <h5 className="text-xs font-black uppercase tracking-wider text-teal-950 dark:text-teal-200">
                                Rekomendasi Potensi Karier & Peminatan Lanjutan
                              </h5>
                              <p className="text-[10.5px] text-teal-700/80 dark:text-teal-300/80 font-medium">
                                Analisis kecocokan profil akademik siswa dengan peta karier masa depan
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-1.5 self-start sm:self-auto">
                            <span className="text-[10px] font-extrabold px-3 py-1 bg-teal-600 text-white rounded-full flex items-center gap-1 shadow-xs">
                              <GraduationCap size={13} />
                              <span>{studentCareerProfile.specializationTrack}</span>
                            </span>
                          </div>
                        </div>

                        {/* Career Prospects List */}
                        <div>
                          <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400 block mb-2 flex items-center gap-1.5">
                            <Briefcase size={13} className="text-teal-600 dark:text-teal-400" />
                            Pilihan Prospek Profesi & Karier Utama:
                          </span>
                          <div className="space-y-2">
                            {studentCareerProfile.careerPaths.map((career, cIdx) => (
                              <div 
                                key={`career-${cIdx}`}
                                className="p-3 bg-white dark:bg-slate-800/90 text-slate-800 dark:text-slate-100 font-bold text-xs rounded-2xl border border-teal-200/90 dark:border-teal-800/80 shadow-xs flex items-start gap-2.5 leading-relaxed"
                              >
                                <span className="w-2 h-2 rounded-full bg-teal-500 mt-1.5 shrink-0"></span>
                                <span className="break-words font-black text-teal-950 dark:text-teal-200">{career}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Cognitive Strengths & Learning Style */}
                        <div className="p-3.5 bg-white/80 dark:bg-slate-800/80 rounded-2xl border border-teal-100 dark:border-teal-900/60 space-y-1.5">
                          <div className="flex items-center gap-1.5 text-xs font-black text-indigo-900 dark:text-indigo-200">
                            <Brain size={15} className="text-indigo-600 dark:text-indigo-400" />
                            <span>Karakteristik & Kekuatan Belajar Siswa:</span>
                          </div>
                          <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed font-normal">
                            {studentCareerProfile.cognitiveStrength}
                          </p>
                        </div>

                        {/* Recommended Next Steps */}
                        <div className="p-3.5 bg-white/80 dark:bg-slate-800/80 rounded-2xl border border-emerald-100 dark:border-emerald-900/60 space-y-2">
                          <div className="flex items-center gap-1.5 text-xs font-black text-emerald-900 dark:text-emerald-200">
                            <Lightbulb size={15} className="text-emerald-600 dark:text-emerald-400" />
                            <span>Langkah Pembinaan yang Dianjurkan (Wali Kelas & Orang Tua):</span>
                          </div>
                          <div className="space-y-1.5">
                            {studentCareerProfile.recommendedSteps.map((step, sIdx) => (
                              <div key={`step-${sIdx}`} className="flex items-start gap-2 text-xs text-slate-700 dark:text-slate-300">
                                <CheckCircle2 size={13} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                                <span>{step}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Section: Evaluasi Nilai Di Bawah Standar KKTP (< 75) */}
                  <div className="bg-white dark:bg-slate-800/80 rounded-3xl border border-slate-200 dark:border-slate-700 p-5 shadow-xs">
                    {(() => {
                      const subjectsWithBelowKKM = Object.entries(subjectMap).filter(([sub, grades]) => {
                        return grades.some(g => g.grade < 75);
                      });

                      return (
                        <>
                          <div className="flex items-center justify-between mb-3.5 border-b border-slate-100 dark:border-slate-700 pb-3">
                            <div className="flex items-center gap-2">
                              <div className={`p-2 rounded-xl ${
                                subjectsWithBelowKKM.length > 0 
                                  ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400' 
                                  : 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400'
                              }`}>
                                <ShieldAlert size={18} />
                              </div>
                              <div>
                                <h4 className="text-xs font-black uppercase tracking-wider text-slate-800 dark:text-white">
                                  Evaluasi Ketuntasan KKTP (&lt; 75) & Rekam Nilai Semester
                                </h4>
                                <p className="text-[10.5px] text-slate-400 font-medium">
                                  Monitoring ketercapaian kriteria ketuntasan tujuan pembelajaran
                                </p>
                              </div>
                            </div>
                            <span className={`text-[10px] font-extrabold px-3 py-1 rounded-full ${
                              subjectsWithBelowKKM.length > 0
                                ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                                : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                            }`}>
                              {subjectsWithBelowKKM.length > 0 ? `⚠ ${subjectsWithBelowKKM.length} Mapel Remedial` : '✓ 100% Tuntas'}
                            </span>
                          </div>

                          {subjectsWithBelowKKM.length === 0 ? (
                            <div className="p-4 bg-emerald-50/60 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-800/40 rounded-2xl flex items-center gap-3">
                              <CheckCircle2 size={22} className="text-emerald-500 shrink-0" />
                              <p className="text-xs font-bold text-emerald-800 dark:text-emerald-300">
                                Sangat Membanggakan! Seluruh capaian nilai mata pelajaran pada semua semester berada di atas standar KKTP (≥ 75).
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {subjectsWithBelowKKM.map(([subjectName, grades]) => {
                                const subAvg = grades.reduce((sum, g) => sum + g.grade, 0) / grades.length;
                                const evalData = generateKKTPSubjectEvaluation(
                                  subjectName, 
                                  grades.map(g => ({ semesterName: g.sem, grade: g.grade })), 
                                  selectedStudent.name
                                );

                                return (
                                  <div 
                                    key={`subject-eval-${subjectName}`}
                                    className="p-4 bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200/80 dark:border-amber-900/50 rounded-2xl space-y-3.5 shadow-xs"
                                  >
                                    {/* Header */}
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-black text-slate-900 dark:text-white text-sm">
                                          {subjectName}
                                        </span>
                                        <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium bg-white dark:bg-slate-900 px-2.5 py-0.5 rounded-md border border-slate-200 dark:border-slate-700">
                                          Rata-rata: <strong className={subAvg < 75 ? 'text-amber-600 dark:text-amber-400 font-extrabold' : 'text-emerald-600 dark:text-emerald-400 font-extrabold'}>{subAvg.toFixed(1)}</strong>
                                        </span>
                                        <span className={`text-[10px] px-2.5 py-0.5 border font-extrabold rounded-full ${evalData.trendBadgeColor}`}>
                                          {evalData.trendLabel}
                                        </span>
                                      </div>
                                      <span className="text-[10px] px-2.5 py-0.5 bg-rose-100 dark:bg-rose-950/60 text-rose-800 dark:text-rose-300 font-black rounded-md border border-rose-200 dark:border-rose-800">
                                        Nilai &lt; 75 Perlu Penguatan
                                      </span>
                                    </div>

                                    {/* Display all grades across semesters for this subject */}
                                    <div>
                                      <div className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                                        <BarChart size={12} />
                                        Rekam Nilai Per Semester:
                                      </div>
                                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
                                        {currentProgress.semesters.map(sem => {
                                          const found = sem.subjects.find(s => s.subjectName === subjectName);
                                          const gradeVal = found ? found.grade : null;
                                          
                                          return (
                                            <div 
                                              key={`sub-sem-${sem.semesterName}-${subjectName}`}
                                              className={`p-2.5 rounded-xl text-center border text-xs transition-all ${
                                                gradeVal !== null && gradeVal < 75
                                                  ? 'bg-rose-50 dark:bg-rose-950/60 border-rose-300 dark:border-rose-800 text-rose-900 dark:text-rose-200 font-black shadow-2xs'
                                                  : gradeVal !== null
                                                    ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300 font-bold'
                                                    : 'bg-slate-100 dark:bg-slate-900/40 border-dashed border-slate-200 dark:border-slate-800 text-slate-400 font-medium'
                                              }`}
                                            >
                                              <div className="text-[9px] text-slate-400 uppercase tracking-wider font-extrabold">{sem.semesterName}</div>
                                              <div className="mt-0.5 font-black text-sm flex items-center justify-center gap-1">
                                                {gradeVal !== null ? (
                                                  <>
                                                    <span>{gradeVal}</span>
                                                    {gradeVal < 75 ? (
                                                      <AlertCircle size={12} className="text-rose-500" />
                                                    ) : (
                                                      <Check size={12} className="text-emerald-500" />
                                                    )}
                                                  </>
                                                ) : '-'}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>

                                    {/* Dynamic Feedback: Motivasi, Saran, Solusi */}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 pt-1">
                                      {/* Motivasi */}
                                      <div className="bg-amber-100/70 dark:bg-amber-950/40 border border-amber-200/80 dark:border-amber-800/60 rounded-xl p-3 flex flex-col justify-between">
                                        <div>
                                          <div className="flex items-center gap-1.5 text-[11px] font-extrabold text-amber-900 dark:text-amber-200 mb-1.5">
                                            <Sparkles size={13} className="text-amber-600 dark:text-amber-400" />
                                            Motivasi Belajar
                                          </div>
                                          <p className="text-[11px] text-amber-900/90 dark:text-amber-300 leading-relaxed font-medium">
                                            {evalData.motivation}
                                          </p>
                                        </div>
                                      </div>

                                      {/* Saran Strategis */}
                                      <div className="bg-sky-50 dark:bg-sky-950/40 border border-sky-200 dark:border-sky-800/60 rounded-xl p-3 flex flex-col justify-between">
                                        <div>
                                          <div className="flex items-center gap-1.5 text-[11px] font-extrabold text-sky-900 dark:text-sky-200 mb-1.5">
                                            <Target size={13} className="text-sky-600 dark:text-sky-400" />
                                            Saran &amp; Pendekatan Mapel
                                          </div>
                                          <p className="text-[11px] text-sky-900/90 dark:text-sky-300 leading-relaxed font-medium">
                                            {evalData.strategy}
                                          </p>
                                        </div>
                                      </div>

                                      {/* Solusi & Tindak Lanjut */}
                                      <div className="bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 rounded-xl p-3">
                                        <div className="flex items-center gap-1.5 text-[11px] font-extrabold text-emerald-900 dark:text-emerald-200 mb-1.5">
                                          <Wrench size={13} className="text-emerald-600 dark:text-emerald-400" />
                                          Solusi &amp; Rencana Aksi
                                        </div>
                                        <div className="space-y-1">
                                          {evalData.solutions.slice(0, 3).map((sol, solIdx) => (
                                            <div key={`sol-${solIdx}`} className="flex items-start gap-1.5 text-[10.5px] text-emerald-900/90 dark:text-emerald-300 leading-snug">
                                              <span className="font-extrabold text-emerald-600 dark:text-emerald-400">•</span>
                                              <span>{sol}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {/* Section: Narasi Bimbingan Wali Kelas */}
                  <div className="bg-gradient-to-br from-indigo-500/10 via-purple-500/10 to-emerald-500/10 dark:from-indigo-950/40 dark:via-purple-950/30 dark:to-emerald-950/30 rounded-3xl border border-indigo-200/80 dark:border-indigo-800/60 p-6 shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 border-b border-indigo-200/60 dark:border-indigo-900/60 pb-3.5">
                      <div className="flex items-center gap-2.5">
                        <div className="p-2 bg-gradient-to-br from-indigo-600 to-indigo-700 text-white rounded-xl shadow-xs">
                          <MessageSquareQuote size={18} />
                        </div>
                        <div>
                          <h4 className="text-xs font-black uppercase tracking-wider text-indigo-950 dark:text-indigo-200">
                            Bimbingan & Motivasi Wali Kelas
                          </h4>
                          <p className="text-[11px] text-indigo-700 dark:text-indigo-400 font-medium">
                            Rekomendasi naskah motivasi & evaluasi belajar siap disampaikan ke siswa / orang tua
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={handleCopyNarrative}
                        className="px-3.5 py-1.5 bg-white dark:bg-slate-800 hover:bg-indigo-50 dark:hover:bg-slate-700 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700 rounded-xl font-bold text-xs flex items-center gap-1.5 transition-all cursor-pointer shadow-xs shrink-0 self-start sm:self-auto"
                      >
                        {copiedNarrative ? (
                          <>
                            <Check size={14} className="text-emerald-500" />
                            <span className="text-emerald-600 dark:text-emerald-400 font-extrabold">Tercopy!</span>
                          </>
                        ) : (
                          <>
                            <Copy size={14} />
                            <span>Salin Narasi</span>
                          </>
                        )}
                      </button>
                    </div>

                    <div className="space-y-3 text-xs text-slate-700 dark:text-slate-300 leading-relaxed font-medium bg-white/80 dark:bg-slate-900/70 p-5 rounded-2xl border border-indigo-100/80 dark:border-indigo-900/50 shadow-2xs">
                      <p>{introStory}</p>
                      {topStory && <p>{topStory}</p>}
                      {progressStory && <p>{progressStory}</p>}
                      <p>{evaluationStory}</p>
                      <p className="font-extrabold text-indigo-900 dark:text-indigo-200 pt-1">{closingStory}</p>
                    </div>
                  </div>
                </div>
              );
            })()}
            
            {/* Modal Bottom Actions */}
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 border-t border-slate-100 dark:border-slate-800 pt-5">
              <button
                onClick={handlePrintAnalysisReport}
                className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-2xl font-black text-xs flex items-center justify-center gap-2 shadow-lg shadow-indigo-500/20 transition-all cursor-pointer"
              >
                <Printer size={16} />
                <span>Cetak Laporan Analisis & Narasi (Modern Layout)</span>
              </button>
              <button
                onClick={() => {
                  setShowAnalysisModal(false);
                  setCopiedNarrative(false);
                }}
                className="px-6 py-3 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-2xl font-bold text-xs transition-all cursor-pointer text-center"
              >
                Tutup Modal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentAcademicProgressManager;
